import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronLeft, faChevronDown, faThumbtack, faPlusCircle, faTrash, faCircle, faArrowDown, faArrowUp, faFilter, faLayerGroup, faSort, faTimes, faCog, faFileExport, faFileImport, faWrench, faUndo, faRedo, faGripLinesVertical, faGripVertical, faHandsHelping, faQuestionCircle, faUpload, faImage, faEdit, faPencilAlt, faSave, faSpinner, faExclamationTriangle, faFolderOpen, faFile, faFilePdf, faFileExcel, faFileWord, faSync, faClipboardList, faSearch, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ClientSessionService, PageContainerService, TimerService } from '../../services';
import { ProjectAccessArea, PROJECT_IDLE_TIMEOUT_MS } from '../../utilities/project-access-area';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import { AdminCommandResponse, DeleteReadinessTaskCommandRequest, DeleteReadinessTaskCommandResponse, GetReadinessFormDataCommandResponse, GetReadinessSummaryCommandRequest, GetReadinessSummaryCommandResponse, GetWorkpacksByProjectCommandResponse, ReadinessColumnDataDto, ReadinessData as OriginalReadinessData, ReadinessSummaryDto, SaveArchiveTaskCommandRequest, SaveArchiveTaskCommandResponse, SaveReadinessCommandRequest, SaveReadinessCommandResponse, UserRoleDto, ProjectSetupDto, GetProjectSetupCommandRequest, GetProjectSetupCommandResponse, ClientDto, WorkPackDto, ReadinessData as ReadinessDataObject } from '../../data/CommandServiceDtos.generated';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SignalRService } from '../../services/signalr.service';
import { CanComponentDeactivate } from '../../services/can-deactivate-guard.service';
import { Subscription } from 'rxjs';

type ReadinessData = OriginalReadinessData & { _dropdownOptions?: { [key: string]: any[] }; _attachmentCount?: number; _thumbnailUrl?: string };

/** Saved: id + blobUrl; Pending user file: file (File); ProPak: source 'ProPak' + blobUrl, no file */
export interface AttachmentListItem {
  id?: number;
  fileName: string;
  blobUrl?: string;
  fileType: string;
  source?: string;
  file?: File;
  loading?: boolean;
  loadError?: boolean;
  isTaskImage?: boolean;
}

/** A cell another user has edited, with the colour slot their edits are painted in. */
export interface RemoteCellChange {
  user: string;
  colorIndex: number;
  tooltip: string;
}

/** How many colour slots the SCSS defines for other users' edits. */
const COLLAB_COLOR_COUNT = 8;

@Component({
  standalone: true,
  selector: 'project-readniess-form',
  templateUrl: './project-readniess-form.component.html',
  styleUrls: ['./project-readniess-form.component.scss'],
  imports: [CommonModule, FormsModule, RouterModule, FontAwesomeModule],
})
export class ProjectReadniessFormComponent extends ControllerForViewBase implements OnInit, CanComponentDeactivate, OnDestroy {
  // Show undo button if there are changes in the undo stack
  canUndo(): boolean {
    return this.undoStack && this.undoStack.length > 0;
  }

  // Show redo button if there are changes in the redo stack
  canRedo(): boolean {
    return this.redoStack && this.redoStack.length > 0;
  }
  faChevronLeft = faChevronLeft;
  faPlusCircle = faPlusCircle;
  faTrash = faTrash;
  faCircle = faCircle;
  faArrowDown = faArrowDown;
  faArrowUp = faArrowUp;
  faLayerGroup = faLayerGroup;
  faSort = faSort;
  faTimes = faTimes;
  faWorkPackIcon = faWrench;
  faCog = faCog;
  faFilter = faFilter;
  faFileExport = faFileExport;
  faFileImport = faFileImport;
  faPin = faThumbtack;
  faUndo = faUndo;
  faRedo = faRedo;
  faGripLinesVertical = faGripVertical;
  faHandsHelping = faQuestionCircle;
  faUpload = faUpload;
  faImage = faImage;
  faChevronDown = faChevronDown;
  faEdit = faEdit;
  faPencil = faPencilAlt;
  faPencilAlt = faPencilAlt;
  faSave = faSave;
  faSpinner = faSpinner;
  faWarning = faExclamationTriangle;
  faFolderOpen = faFolderOpen;
  faFile = faFile;
  faFilePdf = faFilePdf;
  faFileExcel = faFileExcel;
  faFileWord = faFileWord;
  faSync = faSync;
  faClipboardList = faClipboardList;
  faSearch = faSearch;
  faMinus = faMinus;
  faPlus = faPlus;
  tempImageBase64: string | null = null;
  isUploadingImage: boolean = false;
  showAttachmentModal: boolean = false;
  selectedAttachmentRow: any = null;
  /** Unified list: saved items have id+blobUrl; pending have file (File); ProPak have source+'ProPak'+blobUrl */
  attachmentList: AttachmentListItem[] = [];
  /** When user removes an existing attachment we track its id to send on Save */
  attachmentIdsToDelete: number[] = [];
  workPackTaskReference: { workPackNumber?: string; taskNumber?: string; revision?: string; taskImageUrl?: string } | null = null;
  isUploadingAttachment: boolean = false;
  attachmentLoadWorkPackRef: boolean = false;
  isLoadingWorkPackReference: boolean = false;
  workPackTaskImageLoaded: boolean = false;
  workPackTaskImageError: boolean = false;
  /** Toggle: when true fetch and show ProPak task image; user can add it (saved as link only). Default ON when popup opens. */
  fetchProPakImage: boolean = true;
  /** Bit flag: hide/show non-task-image attachments UI without changing logic. */
  showMultipleAttachments: boolean = true;
  isLoadingAttachments: boolean = false;
  isDeletingImage: boolean = false;
  deletingImageRowNumber: number | null = null;
  imageLoadFailed: boolean = false;
  isDragOverAttachmentZone: boolean = false;
  isDragOverTaskImageZone: boolean = false;

  /** When true, saves periodically without blocking UI. On by default for each page load; user toggle lasts until refresh. */
  autoSaveEnabled = true;
  autoSaveCountdownSec = 120;
  autoSaveInProgress = false;

  /** Auto-save UI: total seconds shown as mm:ss */
  get autoSaveCountdownDisplay(): string {
    const total = this.autoSaveCountdownSec < 0 ? 0 : this.autoSaveCountdownSec;
    const m = Math.floor(total / 60);
    const s = total % 60;
    const pad = (n: number) => (n < 10 ? '0' : '') + n;
    return pad(m) + ':' + pad(s);
  }

  /** Zoom applies only to the readiness table/form area (not header/stats). */
  readonly formZoomMin = 20;
  readonly formZoomMax = 200;
  readonly formZoomStep = 5;
  formZoomPercent = 100;
  formZoomDraft: number | string = 100;

  get formZoomScale(): number {
    return this.formZoomPercent / 100;
  }

  adjustFormZoom(delta: number): void {
    this.setFormZoom(this.formZoomPercent + delta);
  }

  setFormZoom(value: number | string): void {
    const n = Math.round(Number(value));
    if (isNaN(n)) {
      return;
    }
    this.formZoomPercent = Math.min(this.formZoomMax, Math.max(this.formZoomMin, n));
    this.formZoomDraft = this.formZoomPercent;
  }

  onFormZoomDraftChange(value: number | string): void {
    const digits = String(value ?? '').replace(/\D/g, '');
    this.formZoomDraft = digits;
    if (digits === '') {
      return;
    }
    const n = Number(digits);
    if (n >= this.formZoomMin && n <= this.formZoomMax) {
      this.formZoomPercent = Math.round(n);
    }
  }

  commitFormZoomDraft(): void {
    const n = Number(this.formZoomDraft);
    if (this.formZoomDraft === '' || this.formZoomDraft === null || isNaN(n)) {
      this.formZoomDraft = this.formZoomPercent;
      return;
    }
    this.setFormZoom(n);
  }

  // `ReturnType` is not available in this repo's TS setup, and setInterval returns a number in browsers.
  // In this repo's TS setup, `setInterval` may be typed as a Node `Timer`.
  private autoSaveIntervalId: any = null;
  private autoSaveFailureCount = 0;
  private readonly autoSaveIntervalSec = 120;
  private lastAutoSavedChangeKey = '';
  /** Shared lock so manual Save and autosave cannot run at the same time. */
  private saveInProgress = false;

  readinessSummary: ReadinessSummaryDto;
  titleValue: string = "Project Readiness";
  formsList: any[] = [];
  // originalFormsList: any[] = [];
  // selectedClient: any = null;
  getCommandResponse: AdminCommandResponse = null;
  options = [
    { Description: 'Yes', Id: 'true' },
    { Description: 'No', Id: 'false' }
  ];
  projectPhaseId: number = 0;
  projectId: number = 0;
  readinessData: GetReadinessFormDataCommandResponse = null;

  /** Set when the readiness form could not be loaded; shown in place of the grid. */
  readinessLoadError: string = null;
  filteredData: ReadinessData[] = [];
  allWorkPacks: GetWorkpacksByProjectCommandResponse = null;
  isGroupingEnabled: boolean = false;
  groupedRows: any = {};
  groupedByColumnValues: any = [];
  groupedByColumns: string[] = [];
  showEditIconForUser: boolean = false;
  isRowSelected: boolean = false;
  selectedTaskType: string = 'current';
  isCurrentTaskSelected: boolean = true;
  isArchiveTaskSelected: boolean = false;
  project: ProjectSetupDto = new ProjectSetupDto;
  userRoles: UserRoleDto[];
  selectedRows: any[] = [];
  showUnarchiveButton: boolean = false;
  showArchiveButton: boolean = false;
  originalTaskNumbers: number[] = [];
  columnSettings: any = {};
  readinessColumnData: ReadinessColumnDataDto[] = [];
  categoryToggle: { [key: string]: boolean } = {};
  groupedData: { sectionName: string, sectionOrder: number, data: ReadinessColumnDataDto[] }[] = [];
  collapsedSections: { [key: string]: boolean } = {};
  // collapsedSections1: boolean[] = [];
  groupedColumns: any[] = [];
  sectionVisibility: { [key: string]: boolean } = {};
  selectedDropdownValues: { [sectionName: string]: string } = {};
  selectAll: boolean = false;
  dropdownOptions: any;
  // wpStatusValue: number = 0;
  showValidationErrors = false;
  /** Rows the last save attempt reported as duplicates, by getDuplicateRowKey. */
  duplicateTaskRows = new Set<string>();
  Array = Array; // To use Array.from() in template
  userName: string = "";
  isSticky: boolean = true;
  trafficLightRules: any;
  rowDragged: boolean = false;
  currentUrl = window.location.href;
  originalReadinessFormData: any;
  isLoading: boolean = true;
  archieveTasks: any[] = [];
  originalCurrentReadinessFormData: ReadinessData[] = [];
  originalArchiveReadinessFormData: ReadinessData[] = [];
  modifiedArchiveReadinessFormData: ReadinessData[] = [];
  isAutoRouted: boolean = false;
  private bypassNextCanDeactivatePrompt: boolean = false;
  modifiedCurrentReadinessFormData: ReadinessData[] = [];
  originalCombinedReadinessFormData: ReadinessData[] = [];
  modifiedCombinedReadinessFormData: ReadinessData[] = [];
  currentTaskChangedFields: { [rowNumber: number]: { [columnName: string]: boolean } } = {};
  archiveTaskChangedFields: { [rowNumber: number]: { [columnName: string]: boolean } } = {};
  combinedTaskChangedFields: { [rowNumber: number]: { [columnName: string]: boolean } } = {};
  undoStack: any[] = [];
  redoStack: any[] = [];
  private boundHandleKeyDown: any;
  isProPakWorkPackAvailable: boolean = false;
  isCtrlPressed = false;
  isGripActive = false;
  isProPakWPEnable = false;
  isDisplayProPakWPToggle: boolean = false;
  showExportDropdown: boolean = false;
  showProPakDropdown: boolean = false;
  exportInProgress: 'excel' | 'pdf' | null = null;
  includeTaskImages: boolean = false;
  exportIncludeOtherTaskType: boolean = false;
  filterDropdownOpen: { [columnName: string]: boolean } = {};
  openFilterColumn: { name: string; displayName: string } | null = null;
  filterDropdownStyle: { [key: string]: string } = { top: '0px', left: '0px' };
  selectedFilters: { [columnName: string]: any[] } = {};
  filteredColumns: { [key: string]: boolean } = {};
  filteredColumnsDisplayNames: string[] = [];
  isDisplayFilterButton: boolean = false;
  readinessSearchText: string = '';
  dropdownOptionsCache: { [key: string]: any[] } = {};
  filterSearchText: { [columnName: string]: string } = {};
  isNewRowAdded: boolean = false;
  isRowSorted: boolean = false;
  @ViewChild('filterIcon') filterIcon: ElementRef;
  @ViewChild('filterDropdown') filterDropdown: ElementRef;

  // Image upload properties
  selectedImageRow: any = null;
  showImagePreview: boolean = false;
  imagePreviewUrl: string = '';
  imagePreviewPosition = { x: 100, y: 100 };
  isDraggingImage: boolean = false;
  dragOffset = { x: 0, y: 0 };
  tasksWithUnsavedImages: Set<string> = new Set(); // Track task numbers with unsaved images

  canDeactivate(): Promise<boolean> {
    if (this.bypassNextCanDeactivatePrompt) {
      this.bypassNextCanDeactivatePrompt = false;
      return Promise.resolve(true);
    }

    const hasChanges = Object.keys(this.changedFields).some(rowNumber => {
      return Object.keys(this.changedFields[+rowNumber])
        .some(columnName => columnName !== 'Status' && this.changedFields[+rowNumber][columnName]);
    });

    if (hasChanges || this.rowDragged || this.isNewRowAdded) {

      return this.pcs.showUnsavedChangesDialogWithSaveButton(true)
        .then(result => {

          if (result === 'discard') {
            return true;
          }

          if (result === 'stay') {
            return false;
          }

          if (result === 'save') {
            return this.saveAllTasks().then(success => success);
          }

          return false;
        });
    }

    return Promise.resolve(true);
  }


  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    const hasChanges = Object.keys(this.changedFields).some(rowNumber => {
      return Object.keys(this.changedFields[+rowNumber])
        .some(columnName => columnName !== 'Status' && this.changedFields[+rowNumber][columnName]);
    });
    if (hasChanges || this.rowDragged || this.isNewRowAdded) {
      $event.returnValue = true;
      // return this.pcs.showUnsavedChangesDialog();
    }
  }
  get archivedCount(): number {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) return 0;
    return this.readinessData.ReadinessFormData.filter(x => x.IsArchived).length;
  }
  get currentCount(): number {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) return 0;
    return this.readinessData.ReadinessFormData.filter(x => !x.IsArchived).length;
  }
  private get _statsRows(): any[] {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) return [];
    if (this.selectedTaskType === 'archived') return this.readinessData.ReadinessFormData.filter(x => x.IsArchived);
    if (this.selectedTaskType === 'current') return this.readinessData.ReadinessFormData.filter(x => !x.IsArchived);
    return this.readinessData.ReadinessFormData;
  }
  private _pct(row: any): number {
    const s = row.Status;
    if (s == null) return 0;
    const n = typeof s === 'number' ? s : parseInt(String(s).replace('%', ''), 10);
    return isNaN(n) ? 0 : n;
  }
  get completeCount(): number {
    return this._statsRows.filter(r => this._pct(r) >= 80).length;
  }
  get attentionCount(): number {
    return this._statsRows.filter(r => { const p = this._pct(r); return p > 0 && p < 80; }).length;
  }
  get blockedCount(): number {
    return this._statsRows.filter(r => this._pct(r) <= 0).length;
  }
  get overallProgress(): number {
    const rows = this._statsRows;
    if (rows.length === 0) return 0;
    const sum = rows.reduce((acc, r) => acc + this._pct(r), 0);
    return Math.round(sum / rows.length);
  }
  get completePercent(): number {
    const total = this.totalTasksForCurrentView;
    return total > 0 ? Math.round((this.completeCount / total) * 100) : 0;
  }
  get attentionPercent(): number {
    const total = this.totalTasksForCurrentView;
    return total > 0 ? Math.round((this.attentionCount / total) * 100) : 0;
  }
  get blockedPercent(): number {
    const total = this.totalTasksForCurrentView;
    return total > 0 ? Math.round((this.blockedCount / total) * 100) : 0;
  }
  get hasActiveFilters(): boolean {
    return Object.keys(this.selectedFilters || {}).some(
      col => this.selectedFilters[col] && this.selectedFilters[col].length > 0
    );
  }
  get totalTasksForCurrentView(): number {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) return 0;
    if (this.selectedTaskType === 'archived') {
      return this.readinessData.ReadinessFormData.filter(x => x.IsArchived).length;
    }
    if (this.selectedTaskType === 'current') {
      return this.readinessData.ReadinessFormData.filter(x => !x.IsArchived).length;
    }
    return this.readinessData.ReadinessFormData.length;
  }
  get displayedTasksForCurrentView(): number {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) return 0;
    return this.readinessData.ReadinessFormData.filter(x => !!x["EnableDisplay"]).length;
  }
  get currentViewFilterStatusText(): string {
    const total = this.totalTasksForCurrentView;
    const shown = this.displayedTasksForCurrentView;
    const hasSearchText = (this.readinessSearchText || '').trim().length > 0;
    if ((!this.hasActiveFilters && !hasSearchText) || total === 0 || shown === total) {
      return '';
    }
    return `Showing ${shown} task(s) of ${total} task(s)`;
  }
  formatClassName(sectionName: string): string {
    return 'custom-collapse-' + sectionName.toLowerCase().replace(/[\s/]+/g, '-');
  }

  formatRowClassName(sectionName: string): string {
    return 'custom-row-' + sectionName.toLowerCase().replace(/[\s/]+/g, '-');
  }

  togglePin() {
    this.isSticky = !this.isSticky;
  }

  toggleOnOff() {
    this.isProPakWPEnable = !this.isProPakWPEnable;
    this.getAllWorkPacksByProjectId();
  }

  async refreshReadinessDataOnly() {
    if (this.hasUnsavedChanges()) {
      const proceed = await this.pcs.confirm(
        'You have unsaved changes. Refreshing will discard local edits. Continue?',
        'Unsaved changes'
      );
      if (!proceed) {
        return;
      }
    }

    this.isLoading = true;
    try {
      this.resetAllData();
      await this.getAllWorkPacksByProjectId();

      // Rebuild per-row dropdown options so selects render descriptions instead of raw IDs.
      // getAllWorkPacksByProjectId() only prepares these when ProPak WP is enabled, so we
      // do it explicitly here using the already-loaded admin/dropdown data.
      if (this.getCommandResponse && this.readinessData && this.readinessData.ReadinessFormData) {
        await this.prepareDropdownsForRows('refreshReadinessDataOnly');
        this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
        this.initializeRowColors();
      }

      // Re-apply the previously active filters, grouping and search to the freshly loaded data
      // (refresh should NOT clear the user's filters or grouping).
      this.reapplyColumnFilters();
      if (this.isGroupingEnabled && this.groupedByColumns && this.groupedByColumns.length > 0) {
        this.groupRowsByColumns(this.groupedByColumns);
      }
      this.applySearchAndFilterVisibility();
      this.toggleFilterButton();
      this.fillFormList();

      this.tasksWithUnsavedImages.clear();
      this.pcs.notificationService.success('Refresh', 'Readiness data refreshed.');
    } catch (error) {
      console.error('Error refreshing readiness data:', error);
      this.pcs.notificationService.danger('Refresh Failed', 'Unable to refresh readiness data.');
    } finally {
      this.isLoading = false;
    }
  }

  private reapplyColumnFilters(): void {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) {
      return;
    }

    const activeFilters = Object.keys(this.selectedFilters || {}).filter(
      col => this.selectedFilters[col] && this.selectedFilters[col].length > 0
    );

    this.readinessData.ReadinessFormData.forEach(row => {
      let matchesAll = true;
      for (const col of activeFilters) {
        const selectedValues = this.selectedFilters[col];
        if (!selectedValues.some(s => this.areFilterValuesEqual(s, row[col]))) {
          matchesAll = false;
          break;
        }
      }
      row["isFilteredOut"] = !matchesAll;
    });
  }

  get activeFilteredColumnsText(): string {
    return this.filteredColumnsDisplayNames.join(', ');
  }

  // @HostListener('document:click', ['$event'])
  // onClickOutside(event: MouseEvent) {
  //   const target = event.target as HTMLElement;

  //   if (
  //     this.filterIcon && this.filterDropdown &&
  //     !this.filterIcon.nativeElement.contains(target) &&
  //     !this.filterDropdown.nativeElement.contains(target)
  //   ) {
  //     Object.keys(this.filterDropdownOpen).forEach(key => {
  //       this.filterDropdownOpen[key] = false;
  //     });
  //   }
  // }

  toggleFilterDropdown(columnName: string, displayName: string, event: MouseEvent) {
    event.stopPropagation();
    const wasOpen = !!this.filterDropdownOpen[columnName];
    this.closeAllFilterDropdowns();
    if (wasOpen) {
      return;
    }

    this.filterDropdownOpen[columnName] = true;
    this.openFilterColumn = { name: columnName, displayName };
    this.dropdownOptionsCache[columnName] = this.computeDropdownOptions(columnName);
    this.placeFilterDropdown((event.currentTarget as HTMLElement).getBoundingClientRect());
    setTimeout(() => this.placeFilterDropdown((event.currentTarget as HTMLElement).getBoundingClientRect()), 0);
  }

  closeAllFilterDropdowns() {
    Object.keys(this.filterDropdownOpen).forEach(key => {
      this.filterDropdownOpen[key] = false;
    });
    this.openFilterColumn = null;
  }

  private placeFilterDropdown(trigger: DOMRect) {
    const width = 220;
    const maxHeight = Math.min(280, window.innerHeight - 16);
    let left = trigger.right - width;
    left = Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8));
    let top = trigger.bottom + 4;
    if (top + Math.min(180, maxHeight) > window.innerHeight - 8) {
      top = Math.max(8, trigger.top - maxHeight - 4);
    }
    this.filterDropdownStyle = { top: `${top}px`, left: `${left}px` };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClickForFilter(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.propak-dropdown')) {
      this.showProPakDropdown = false;
    }
    if (!target.closest('.export-dropdown')) {
      this.showExportDropdown = false;
    }
    if (!this.openFilterColumn) {
      return;
    }
    if (target.closest('.filter-dropdown') || target.closest('.filter-icon')) {
      return;
    }
    this.closeAllFilterDropdowns();
  }

  @HostListener('window:resize')
  onWindowResizeForFilter() {
    this.closeAllFilterDropdowns();
  }

  /** Matches the archived styling the grid gives a row. */
  private isArchivedRow(row: any): boolean {
    return !!row && (row.ArchiveTaskId > 0 || row['IsArchived'] === true);
  }

  /**
   * Groups a value the way the filter comparison does, so an archived marker
   * lands on the option it actually describes.
   */
  private getFilterOptionKey(value: any): string {
    return String(this.normalizeFilterValue(value));
  }

  computeDropdownOptions(columnName: string) {
    let options = this.dropdownOptions[columnName] || [];

    if (!options || options.length === 0) {
      const uniqueValues = Array.from(
        new Set(
          this.readinessData.ReadinessFormData
            .map(row => row[columnName])
            .filter(val => val !== null && val !== undefined && val !== '')
        )
      );

      // The options are built from every row, archived ones included, so a value
      // that exists nowhere but the archive is marked as archived. A value held by
      // an archived and a live row both is an ordinary option.
      const liveValueKeys = new Set<string>();
      const archivedValueKeys = new Set<string>();
      this.readinessData.ReadinessFormData.forEach(row => {
        const val = row[columnName];
        if (val === null || val === undefined || val === '') {
          return;
        }
        const key = this.getFilterOptionKey(val);
        if (this.isArchivedRow(row)) {
          archivedValueKeys.add(key);
        } else {
          liveValueKeys.add(key);
        }
      });

      options = uniqueValues.map(val => {
        const key = this.getFilterOptionKey(val);
        return {
          Id: val,
          Description: val ? val.toString().trim().slice(0, 20) : '',
          IsArchivedOnly: archivedValueKeys.has(key) && !liveValueKeys.has(key)
        };
      });

      if (options.length === 0) {
        options = [{ Id: '', Description: 'Blank' }];
      }
    }

    return options;
  }

  getFilteredDropdownOptions(columnName: string): any[] {
    const allOptions = this.dropdownOptionsCache[columnName] || [];
    const searchText = (this.filterSearchText[columnName] || '').trim().toLowerCase();
    
    if (!searchText) {
      return allOptions;
    }
    
    return allOptions.filter(option =>
      option.Description.toLowerCase().includes(searchText)
    );
  }

  isOptionSelected(columnName: string, value: any): boolean {
    return this.selectedFilters[columnName]
      ? this.selectedFilters[columnName].some(v => this.areFilterValuesEqual(v, value))
      : false;
  }

  isAllFilterOptionsSelected(columnName: string): boolean {
    const visibleOptions = this.getFilteredDropdownOptions(columnName);
    if (visibleOptions.length === 0) {
      return false;
    }
    const selected = this.selectedFilters[columnName] || [];
    return visibleOptions.every(option =>
      selected.some(v => this.areFilterValuesEqual(v, option.Id))
    );
  }

  onSelectAllFilterChange(columnName: string, checked: boolean): void {
    const visibleOptions = this.getFilteredDropdownOptions(columnName);
    const visibleIds = visibleOptions.map(option => option.Id);

    if (!this.selectedFilters[columnName]) {
      this.selectedFilters[columnName] = [];
    }

    if (checked) {
      visibleIds.forEach(id => {
        if (!this.selectedFilters[columnName].some(v => this.areFilterValuesEqual(v, id))) {
          this.selectedFilters[columnName].push(id);
        }
      });
    } else {
      this.selectedFilters[columnName] = this.selectedFilters[columnName].filter(
        v => !visibleIds.some(id => this.areFilterValuesEqual(v, id))
      );
    }
  }

  onFilterChange(columnName: string, value: any, checked: boolean) {
    if (!this.selectedFilters[columnName]) {
      this.selectedFilters[columnName] = [];
    }
    if (checked) {
      this.selectedFilters[columnName].push(value);
    } else {
      this.selectedFilters[columnName] = this.selectedFilters[columnName].filter(v => !this.areFilterValuesEqual(v, value));
    }
  }

  onReadinessSearchChange() {
    this.applySearchAndFilterVisibility();
  }

  resetAllFilters() {
    // Keep the global search intact; only clear the per-column filter search boxes.
    this.filterSearchText = {};

    // Step 1: Clear all selected filters
    Object.keys(this.selectedFilters).forEach(col => {
      this.selectedFilters[col] = [];
    });

    // Step 2: Reset all filtered column states
    Object.keys(this.filteredColumns).forEach(col => {
      this.filteredColumns[col] = false;
    });

    // Step 3: Reset all rows to visible and clear filtering flags
    this.readinessData.ReadinessFormData.forEach(row => {
      row["isFilteredOut"] = false;

      if (this.isArchiveTaskSelected && row.IsArchived) {
        row["EnableDisplay"] = true;
      } else if (this.isCurrentTaskSelected && !row.IsArchived) {
        row["EnableDisplay"] = true;
      } else if (!this.isCurrentTaskSelected && !this.isArchiveTaskSelected) {
        row["EnableDisplay"] = true;
      }
    });

    // Step 4: Close all dropdowns
    this.closeAllFilterDropdowns();

    // Step 5: Update filter button visibility
    this.toggleFilterButton();
    this.filteredColumnsDisplayNames = [];
    this.applySearchAndFilterVisibility();
    this.fillFormList();
  }

  toggleFilterButton() {
    if (this.filteredColumns && Object.values(this.filteredColumns).some(v => v === true)) {
      this.isDisplayFilterButton = true;
    } else {
      this.isDisplayFilterButton = false;
    }
  }
  applyFilter(columnName: string, displayName: string) {
    // Close the dropdown for the current column
    this.closeAllFilterDropdowns();
    this.filteredColumns[columnName] = true;

    if (this.filteredColumnsDisplayNames.indexOf(displayName) === -1) {
      this.filteredColumnsDisplayNames.push(displayName);
    }
    // Step 1: Get all columns with active filters
    const activeFilters = Object.keys(this.selectedFilters).filter(
      col => this.selectedFilters[col] && this.selectedFilters[col].length > 0
    );

    if (activeFilters.findIndex(col => col == columnName) == -1) {
      this.filteredColumns[columnName] = false;
      if (this.filteredColumnsDisplayNames.indexOf(displayName) > -1) {
        this.filteredColumnsDisplayNames = this.filteredColumnsDisplayNames.filter(name => name !== displayName);
      }
    }
    this.toggleFilterButton();

    // Step 2: Iterate over all rows and determine if they should be displayed
    this.readinessData.ReadinessFormData.forEach(row => {
      // Default: row should be visible
      let matchesAll = true;

      // Check each active filter
      for (const col of activeFilters) {
        const selectedValues = this.selectedFilters[col];

        // If the row’s value for this column isn’t in the selected filter list → hide it
        if (!selectedValues.some(s => this.areFilterValuesEqual(s, row[col]))) {
          matchesAll = false;
          break;
        }
      }

      // Step 3: Set EnableDisplay based on match and archived/current toggle
      if (matchesAll) {
        // Row passed all filters
        row["isFilteredOut"] = false;

        if (this.isArchiveTaskSelected && row.IsArchived) {
          row["EnableDisplay"] = true;
        } else if (this.isCurrentTaskSelected && !row.IsArchived) {
          row["EnableDisplay"] = true;
        } else if (!this.isCurrentTaskSelected && !this.isArchiveTaskSelected) {
          row["EnableDisplay"] = true;
        } else {
          row["EnableDisplay"] = false;
        }
      } else {
        // Row failed at least one filter
        row["isFilteredOut"] = true;

        if (this.isArchiveTaskSelected && row.IsArchived) {
          row["EnableDisplay"] = false;
        } else if (this.isCurrentTaskSelected && !row.IsArchived) {
          row["EnableDisplay"] = false;
        } else if (!this.isCurrentTaskSelected && !this.isArchiveTaskSelected) {
          row["EnableDisplay"] = false;
        }
      }
    });
    this.applySearchAndFilterVisibility();
    if (this.isGroupingEnabled && this.groupedByColumns && this.groupedByColumns.length > 0) {
      this.groupRowsByColumns(this.groupedByColumns);
    }
    this.fillFormList();
  }


  clearFilter(columnName: string, displayName: string) {
    // Step 1: Clear the filter for the given column
    this.selectedFilters[columnName] = [];
    this.filteredColumns[columnName] = false;

    if (this.filteredColumnsDisplayNames.indexOf(displayName) > -1) {
      this.filteredColumnsDisplayNames = this.filteredColumnsDisplayNames.filter(name => name !== displayName);
    }
    // Step 2: Start by showing all rows (reset)
    this.readinessData.ReadinessFormData.forEach(row => {
      row["isFilteredOut"] = false;

      if (this.isArchiveTaskSelected && row.IsArchived) {
        row["EnableDisplay"] = true;
      } else if (this.isCurrentTaskSelected && !row.IsArchived) {
        row["EnableDisplay"] = true;
      } else if (!this.isCurrentTaskSelected && !this.isArchiveTaskSelected) {
        row["EnableDisplay"] = true;
      }
    });

    // Step 3: Reapply filters for other active columns
    Object.keys(this.selectedFilters).forEach(col => {
      const selected = this.selectedFilters[col];
      if (selected && selected.length > 0) {
        this.readinessData.ReadinessFormData.forEach(row => {
          const match = selected.some(s => this.areFilterValuesEqual(s, row[col]));
          if (!match) {
            row["isFilteredOut"] = true;
            row["EnableDisplay"] = false;
          }
        });
      }
    });

    // Step 4: Close dropdown
    this.closeAllFilterDropdowns();
    this.toggleFilterButton();
    this.applySearchAndFilterVisibility();
    this.fillFormList();
  }

  private applySearchAndFilterVisibility(): void {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) {
      return;
    }

    const search = (this.readinessSearchText || '').trim().toLowerCase();
    const useGroupKeySearch = this.isGroupingEnabled && this.groupedByColumns && this.groupedByColumns.length > 0 && search.length > 0;

    for (const row of this.readinessData.ReadinessFormData) {
      const inSelectedView = this.isArchiveTaskSelected
        ? !!row.IsArchived
        : this.isCurrentTaskSelected
          ? !row.IsArchived
          : true;
      const passesColumnFilters = !row["isFilteredOut"];
      // In grouped view a row passes the search when either the group name it belongs to
      // matches (so the whole group shows) OR the row's own content matches. This keeps
      // grouping + column filters + search working together instead of hiding everything.
      const passesSearch = useGroupKeySearch
        ? (this.rowGroupKeyMatchesSearch(row, search) || this.rowMatchesSearch(row))
        : this.rowMatchesSearch(row);
      row["EnableDisplay"] = inSelectedView && passesColumnFilters && passesSearch;
    }
  }

  shouldDisplayGroup(group: any): boolean {
    return !!group &&
          !!group.rows &&
          group.rows.some(function (row) {
            return !!row["EnableDisplay"];
          });
  }

  private rowGroupKeyMatchesSearch(row: any, search: string): boolean {
    if (!search) {
      return true;
    }
    const groupKey = this.buildGroupKeyForRow(row);
    return String(groupKey || '').toLowerCase().includes(search);
  }

  private buildGroupKeyForRow(row: any): string {
    if (!this.groupedByColumns || this.groupedByColumns.length === 0) {
      return '';
    }

    return this.groupedByColumns
      .map((col) => {
        const options = this.getDropdownOptions(col);
        const selectedValue = row[col];

        if (selectedValue !== null && selectedValue !== undefined && selectedValue !== '' && selectedValue != -1) {
          const matchedOption = options.find(opt => String(opt.Id) === String(selectedValue));
          if (matchedOption) {
            return matchedOption.Description;
          }
        }
        if (selectedValue == -1) {
          return '-';
        }

        return row[col] || '-';
      })
      .join(' | ');
  }

  private rowMatchesSearch(row: any): boolean {
    const search = (this.readinessSearchText || '').trim().toLowerCase();
    if (!search) {
      return true;
    }

    const skipKeys: { [key: string]: boolean } = {
      _dropdownOptions: true,
      IsSelected: true,
      EnableDisplay: true,
      isFilteredOut: true
    };

    const values = Object.keys(row || {})
      .filter(key => !skipKeys[key])
      .map(key => row[key])
      .filter(v => v !== null && v !== undefined && typeof v !== 'object')
      .map(v => String(v).toLowerCase());

    const dropdownSelectedValues: string[] = [];
    const dropdownOptions = row && row._dropdownOptions ? row._dropdownOptions : null;
    if (dropdownOptions) {
      const dropdownColumns = Object.keys(dropdownOptions);
      for (const columnName of dropdownColumns) {
        const selectedValue = row[columnName];
        const options = dropdownOptions[columnName] || [];
        const selectedOption = options.find((opt: any) => String(opt.Id) === String(selectedValue));
        if (selectedOption && selectedOption.Description !== null && selectedOption.Description !== undefined) {
          dropdownSelectedValues.push(String(selectedOption.Description).toLowerCase());
        }
      }
    }

    const searchableValues = values.concat(dropdownSelectedValues);
    return searchableValues.some(v => v.includes(search));
  }

  stripPercent(value: string): string {
    if (!value) {
      return '0%';
    }
    const numericValue = value.replace('%', '').trim();
    return numericValue + '%';
  }

  getStatusPercentValue(value: any): number {
    if (value == null || value === '') return 0;
    const n = typeof value === 'number' ? value : parseInt(String(value).replace('%', ''), 10);
    if (isNaN(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }

  private normalizeFilterValue(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const lower = trimmed.toLowerCase();
      if (lower === 'true') {
        return true;
      }
      if (lower === 'false') {
        return false;
      }
      return trimmed;
    }
    return value;
  }

  private areFilterValuesEqual(a: any, b: any): boolean {
    const left = this.normalizeFilterValue(a);
    const right = this.normalizeFilterValue(b);
    if (left === right) {
      return true;
    }
    if (left == null || right == null) {
      return false;
    }
    return String(left) === String(right);
  }

  private refreshRowVisibilityAfterEdit(): void {
    if (this.hasActiveFilters) {
      this.reapplyColumnFilters();
    }
    this.applySearchAndFilterVisibility();
    if (this.isGroupingEnabled && this.groupedByColumns && this.groupedByColumns.length > 0) {
      this.groupRowsByColumns(this.groupedByColumns);
    }
  }

  async loadProjectData(projectId) {
    const request: GetProjectSetupCommandRequest = { ProjectId: projectId, logTimeIn: true, AccessArea: ProjectAccessArea.Readiness }
    const response: GetProjectSetupCommandResponse = await this.pcs.apiService.GetProjectSetup(request, true, false);
    this.timerService.startTimer(projectId);
    this.startReadinessIdleMonitor();
    this.project = response.ProjectInfo;
    this.userRoles = response.ProjectInfo.UserRoles || [];
    this.showEditIconForUser = this.userRoles.some(role =>
      role.RoleName === 'Global Administrator' ||
      role.RoleName === 'Company Administrator' ||
      role.RoleName === 'Project Manager'
    );
  }

  private startReadinessIdleMonitor(): void {
    this.timerService.startIdleMonitor(PROJECT_IDLE_TIMEOUT_MS, () => {
      void this.handleIdleTimeoutNavigation();
    });
  }

  private async handleIdleTimeoutNavigation(): Promise<void> {
    const hasChanges = Object.keys(this.changedFields).some(rowNumber => {
      return Object.keys(this.changedFields[+rowNumber])
        .some(columnName => columnName !== 'Status' && this.changedFields[+rowNumber][columnName]);
    });
    const hasUnsaved = hasChanges || this.rowDragged || this.isNewRowAdded;

    if (!hasUnsaved) {
      this.isAutoRouted = true;
      this.bypassNextCanDeactivatePrompt = true;
      this.router.navigate(['/dashboard']);
      return;
    }

    const result = await this.pcs.showUnsavedChangesDialogWithSaveButton(true);
    if (result === 'stay') {
      this.startReadinessIdleMonitor();
      return;
    }

    if (result === 'save') {
      const saved = await this.saveAllTasks();
      if (!saved) {
        this.pcs.notificationService.warning('Session timeout', 'Could not save changes. Please try again.');
        this.startReadinessIdleMonitor();
        return;
      }
    }

    this.isAutoRouted = true;
    this.bypassNextCanDeactivatePrompt = true;
    this.router.navigate(['/dashboard']);
  }

  initializeRowColors() {
    this.readinessData.ReadinessFormData.forEach(row => {
      this.onDropdownChange("TARApprovalId", row.TARApprovalId, row, "APPROVED For Project/TAR");
      this.onDropdownChange("RevId", row.RevId, row, "WP STATUS");
      this.onDropdownChange("IsolationPlanId", row.IsolationPlanId, row, "TR STATUS");
      this.onDropdownChange("MaterialStatusId", row.MaterialStatusId, row, "MATERIALS");
      this.onDropdownChange("MOCStatusId", row.MOCStatusId, row, "MTL MOC");
      this.calculateOverallStatus(row);
    });

    this.cdr.detectChanges();
  }

  calculateOverallStatus(row: ReadinessData) {
    let ApprovedForTARSectionValue = 0
    let TRStatusSectionValue = 0;
    let WPStatusSectionValue = 0;
    let MaterialsSectionValue = 0;
    let MTLMOCSectionValue = 0;

    // Approved For Project/TAR Section Calculation:
    if (row.TaskDescription)
      ApprovedForTARSectionValue += this.readinessColumnData.find(x => x.ColumnName == "TaskDescription").Value;
    if (row.Scope)
      ApprovedForTARSectionValue += this.readinessColumnData.find(x => x.ColumnName == "Scope").Value;
    if (row.PriorityId != null && row.PriorityId != -1 && row.PriorityId > 0)
      ApprovedForTARSectionValue += this.readinessColumnData.find(x => x.ColumnName == "PriorityId").Value;
    if (row.Owner)
      ApprovedForTARSectionValue += this.readinessColumnData.find(x => x.ColumnName == "Owner").Value;
    if (row.Discipline)
      ApprovedForTARSectionValue += this.readinessColumnData.find(x => x.ColumnName == "Discipline").Value;
    if (row.Contractor)
      ApprovedForTARSectionValue += this.readinessColumnData.find(x => x.ColumnName == "Contractor").Value;
    if (row.TARApprovalId != null && row.TARApprovalId != -1 && row.TARApprovalId > 0) {
      const dropDownOptions = this.getDropdownOptions("TARApprovalId");
      let selectedRows = dropDownOptions.filter(x => x.Id == row.TARApprovalId)
      if (selectedRows.length > 0 && selectedRows[0].Id > 0) {
        ApprovedForTARSectionValue += dropDownOptions.find(x => x.Description == selectedRows[0].Description).Value;
      }
    }

    // TR Status Section Calculation:
    if (row.HeightAccessId != null && row.HeightAccessId > 0) {
      TRStatusSectionValue += this.readinessColumnData.find(x => x.ColumnName == "HeightAccessId").Value;
    }
    if (row.Crane != null && row.Crane != undefined && String(row.Crane) != String(-1)) {
      TRStatusSectionValue += this.readinessColumnData.find(x => x.ColumnName == "Crane").Value;
    }
    if (row.HotWork != null && row.HotWork != undefined && String(row.HotWork) != String(-1)) {
      TRStatusSectionValue += this.readinessColumnData.find(x => x.ColumnName == "HotWork").Value;
    }
    if (row.IsolationPlanId != null && row.IsolationPlanId > 0) {
      // TRStatusSectionValue += this.readinessColumnData.find(x => x.ColumnName == "IsolationPlanId").Value;
      const isolationPlandropDownOptions = this.getDropdownOptions("IsolationPlanId");
      let isolationPlanselectedRows = isolationPlandropDownOptions.filter(x => x.Id == row.IsolationPlanId)
      if (isolationPlanselectedRows.length > 0) {
        TRStatusSectionValue += isolationPlanselectedRows.find(x => x.Description == isolationPlanselectedRows[0].Description).Value;
      }
    }

    // WP Status Section Calculation:
    let firstPart = 0;
    if (row.WorkPackRequiredId != null && row.WorkPackRequiredId > 0) {
      const workPackRequireddropDownOptions = this.getDropdownOptions("WorkPackRequiredId");
      let workPackRequiredselectedRows = workPackRequireddropDownOptions.filter(x => x.Id == row.WorkPackRequiredId)
      if (workPackRequiredselectedRows.length > 0) {
        firstPart = workPackRequiredselectedRows.find(x => x.Description == workPackRequiredselectedRows[0].Description).Value;
      }
    }
    let secondPart: number = 0;
    // Calculate the second part based on RevId (Q7)
    if (row.RevId != null && row.RevId > 0) {
      const revIddropDownOptions = this.getDropdownOptions("RevId");
      let revIdselectedRows = revIddropDownOptions.filter(x => x.Id == row.RevId)

      secondPart = revIdselectedRows.find(x => x.Description == revIdselectedRows[0].Description).Value;
    }
    WPStatusSectionValue = firstPart + secondPart;
    // Materials Section Calculation:
    if (row.MaterialStatusId > 0)
      MaterialsSectionValue += this.readinessColumnData.find(x => x.ColumnName == "MaterialStatusId").Value;
    if (row.LongLead != null && row.LongLead != undefined && String(row.LongLead) != String(-1))
      MaterialsSectionValue += this.readinessColumnData.find(x => x.ColumnName == "LongLead").Value;

    if (row.MaterialStatusId > 0) {
      const materialStatusdropDownOptions = this.getDropdownOptions("MaterialStatusId");
      let materialStatusselectedRows = materialStatusdropDownOptions.filter(x => x.Id == row.MaterialStatusId)
      if (materialStatusselectedRows.length > 0) {
        MaterialsSectionValue += materialStatusselectedRows[0].Value;

      }
    }

    if (row.FabricationId > 0) {
      MaterialsSectionValue += this.readinessColumnData.find(x => x.ColumnName == "FabricationId").Value;
      const fabricationdropDownOptions = this.getDropdownOptions("FabricationId");
      let fabricationselectedRows = fabricationdropDownOptions.filter(x => x.Id == row.FabricationId)
      if (fabricationselectedRows.length > 0) {
        MaterialsSectionValue += fabricationselectedRows[0].Value;
      }
    }

    const stagedOnSitedropDownOptions = this.getDropdownOptions("StagedOnSite");
    // let selectedOption = this.options.find(x => x.Id == row.StagedOnSite);
    let stagedOnSiteselectedRows = stagedOnSitedropDownOptions.filter(x => x.Id == row.StagedOnSite);
    if (stagedOnSiteselectedRows.length > 0 && stagedOnSiteselectedRows[0].Id > 0) {
      MaterialsSectionValue += this.readinessColumnData.find(x => x.ColumnName == "StagedOnSite").Value;
      MaterialsSectionValue += stagedOnSiteselectedRows[0].Value;
    }

    // MTL MOC Section Calculation:
    if (row.MOCRequired != null && row.MOCRequired != undefined)
      MTLMOCSectionValue += this.readinessColumnData.find(x => x.ColumnName == "MOCRequired").Value;
    if (row.MOCNumber != null && row.MOCNumber != "" && row.MOCNumber != undefined)
      MTLMOCSectionValue += this.readinessColumnData.find(x => x.ColumnName == "MOCNumber").Value;
    if (row.MOCStatusId != null && row.MOCStatusId > 0) {
      MTLMOCSectionValue += this.readinessColumnData.find(x => x.ColumnName == "MOCStatusId").Value;;
      const mocStatusdropDownOptions = this.getDropdownOptions("MOCStatusId");
      let mocStatusselectedRows = mocStatusdropDownOptions.filter(x => x.Id == row.MOCStatusId)
      if (mocStatusselectedRows.length > 0 && mocStatusselectedRows[0].Id > 0) {
        MTLMOCSectionValue += mocStatusselectedRows[0].Value;
      }
    }
    let overallStatus = ApprovedForTARSectionValue + TRStatusSectionValue + WPStatusSectionValue + MaterialsSectionValue + MTLMOCSectionValue;
    if (overallStatus > 100)
      overallStatus = 100;
    row.Status = overallStatus + '%';

    this.onFieldChange(row.ActualRowNumber, 'Status', row.Status, false)
  }

  onDropdownChange(columnName: string, value: any, row: any, sectionName?: string | null, onLoad: boolean = true) {
    if (!onLoad) {
      this.prepareDropdownsForSingleRow(row);
      if (!this.changedFields[row.ActualRowNumber]) {
        this.changedFields[row.ActualRowNumber] = {};
      }

      if (columnName === "MOCRequired") {
        const options = this.getDropdownOptions(columnName);
        const selectedValue = row[columnName];

        if (selectedValue !== null && selectedValue !== undefined && selectedValue !== '') {
          const matchedOption = options.find(opt => String(opt.Id) === String(selectedValue));
          if (matchedOption.Description == 'No') {
            this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousMOCNumber = row.MOCNumber;
            row.MOCNumber = 'N/A';
            let MocOptions = this.getCommandResponse.MOCStatuses.filter(r => r.Description == 'N/A');
            row._dropdownOptions['MOCStatusId'] = MocOptions || [];
            this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousMOCRequired = row.MOCStatusId;
            row.MOCStatusId = MocOptions[0].Id;
            this.changedFields[row.ActualRowNumber]['MOCStatusId'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousMOCRequired !== MocOptions[0].Id;
            this.changedFields[row.ActualRowNumber]['MOCNumber'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousMOCNumber !== row.MOCNumber;
          }
          else if (matchedOption.Description == 'Yes') {
            row.MOCStatusId = -1;
            row.MOCNumber = '';
            let MocOptions = this.getCommandResponse.MOCStatuses.filter(r => r.Description !== 'N/A');
            row._dropdownOptions['MOCStatusId'] = MocOptions || [];
            this.changedFields[row.ActualRowNumber]['MOCStatusId'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).MOCStatusId !== row.MOCStatusId;
            this.changedFields[row.ActualRowNumber]['MOCNumber'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).MOCNumber !== row.MOCNumber;
          }
        }
      }
      else if (columnName === "MaterialStatusId") {
        const options = this.getDropdownOptions(columnName);
        let previousSelectedValue = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousMaterialStatusId;
        this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousMaterialStatusId = value;
        const previousSelectedOption = options.find(opt => String(opt.Id) === String(previousSelectedValue));
        const selectedValue = row[columnName];
        if (selectedValue !== null && selectedValue !== undefined && selectedValue !== '') {
          const matchedOption = options.find(opt => String(opt.Id) === String(selectedValue));
          if ((previousSelectedValue == undefined || previousSelectedOption.Description === "No Materials Required") || matchedOption.Description == "No Materials Required") {
            if (matchedOption.Description == "No Materials Required") {
              this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousFabricationId = row.FabricationId;
              this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousStagedOnSite = row.StagedOnSite;
              this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousLongLead = row.LongLead;

              let fabrications = this.getCommandResponse.Fabrications.filter(r => r.Description == 'Not Required');
              row._dropdownOptions['FabricationId'] = fabrications || [];
              let stagedOnSite = this.getCommandResponse.StagedOnSites.filter(r => r.Description == 'No');
              row._dropdownOptions['StagedOnSite'] = stagedOnSite || [];
              row.LongLead = false;
              row.StagedOnSite = -1;
              row.FabricationId = fabrications[0].Id;
              row.StagedOnSite = stagedOnSite[0].Id;
              row._dropdownOptions['LongLead'] = this.options.filter(x => x.Description == "No");

              this.changedFields[row.ActualRowNumber]['FabricationId'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousFabricationId !== row.FabricationId;
              this.changedFields[row.ActualRowNumber]['StagedOnSite'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousStagedOnSite !== row.StagedOnSite;
              this.changedFields[row.ActualRowNumber]['LongLead'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).PreviousLongLead !== row.LongLead;
            }
            else if (!previousSelectedOption || (previousSelectedOption && previousSelectedOption.Description === "No Materials Required")) {
              row.LongLead = -1;
              row.StagedOnSite = -1;
              row.FabricationId = -1;
              // let fabrications = this.getCommandResponse.Fabrications.filter(r => r.Description !== 'Not Required');
              let fabrications = [...this.getCommandResponse.Fabrications];
              row._dropdownOptions['FabricationId'] = fabrications || [];
              let stagedOnSite = this.getCommandResponse.StagedOnSites;
              row._dropdownOptions['StagedOnSite'] = stagedOnSite || [];
              row._dropdownOptions['LongLead'] = this.options;
              this.changedFields[row.ActualRowNumber]['FabricationId'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).FabricationId !== row.FabricationId;
              this.changedFields[row.ActualRowNumber]['StagedOnSite'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).StagedOnSite !== row.StagedOnSite;
              this.changedFields[row.ActualRowNumber]['LongLead'] = this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber).LongLead !== row.LongLead;
            }
          }
        }
      }
    }
    const sectionMap = {
      "APPROVED For Project/TAR": "ApprovedForProjectSection",
      "TR STATUS": "TRStatusSection",
      "MTL MOC": "MTLMOCSection",
      "WP STATUS": "WPStatusSection",
      "MATERIALS": "MaterialsSection"
    };
    let totalValue = 0;

    const sectionColumns = this.readinessColumnData
      .filter(x => x.SectionName === sectionName && x.RequiredInFormula === true);

    sectionColumns.forEach(x => {
      const options = this.getDropdownOptions(x.ColumnName);
      const selectedValue = row[x.ColumnName];

      if (selectedValue !== null && selectedValue !== undefined && selectedValue !== '') {
        const matchedOption = options.find(opt => String(opt.Id) === String(selectedValue));

        if (matchedOption) {
          if (typeof matchedOption.Id === 'string' && matchedOption.Value == null) {
            const fallback = this.readinessColumnData.find(
              y => y.SectionName === sectionName &&
                y.RequiredInFormula === true &&
                y.ColumnName === x.ColumnName
            );

            if (fallback) {
              totalValue += fallback.Value;
            }
          } else {
            totalValue += matchedOption.Value;
          }
        }
      }

    });
    row[sectionMap[sectionName]] = this.getTrafficLightColorForSection(totalValue, sectionName);
    this.cdr.detectChanges();
  }

  getSectionColorKey(sectionName: string): string {
    switch (sectionName) {
      case "APPROVED For Project/TAR":
        return "ApprovedForProjectSection";
      case "WP STATUS":
        return "WPStatusSection";
      case "TR STATUS":
        return "TRStatusSection";
      case "MATERIALS":
        return "MaterialsSection";
      case "MTL MOC":
        return "MTLMOCSection";
      default:
        return "#e74c3c";
    }
  }

  getTrafficLightColorForSection(totalValue: any, sectionName: string): string {
    const lightsData = this.trafficLightRules.filter(x => x.SectionName === sectionName);

    for (const rule of lightsData) {
      if (this.evaluateCondition(totalValue, rule.Operator, rule.ThresholdValue)) {
        return rule.Color;
      }
    }
    // Default fallback color if no rules matched
    return '#e74c3c'; // Red
  }

  // Helper to evaluate condition safely
  evaluateCondition(value: number, operator: string, threshold: number): boolean {
    switch (operator) {
      case '>':
        return value > threshold;
      case '>=':
        return value >= threshold;
      case '<':
        return value < threshold;
      case '<=':
        return value <= threshold;
      case '==':
        return value == threshold;
      case '!=':
        return value != threshold;
      default:
        return false;
    }
  }

  isInvalid(row: any, column: any): boolean {
    if ((row.IsArchived === true && column.ColumnName === 'PriorityId'))
      return false;
    return column.IsRequired && (
      row[column.ColumnName] === undefined ||
      row[column.ColumnName] === null ||
      row[column.ColumnName] === '' ||
      row[column.ColumnName] === -1
    );
  }

  processColumns() {
    let sectionMap = new Map();

    this.readinessColumnData.forEach(column => {
      if (!sectionMap.has(column.SectionName)) {
        sectionMap.set(column.SectionName, []);
        this.sectionVisibility[column.SectionName] = true;
      }
      sectionMap.get(column.SectionName).push(column);
    });

    this.groupedColumns = Array.from(sectionMap, ([sectionName, columns]) => ({
      sectionName,
      columns: columns.sort((a, b) => a.ColumnOrder - b.ColumnOrder)
    }));

    if (this.groupedColumns && this.groupedColumns.length > 0) {
      this.groupedColumns.forEach((section, index) => {
        this.sectionVisibility[section.sectionName] = index === 0 || section.sectionName === 'Other';
      });
    }

  }

  constructor(public pcs: PageContainerService, private fb: FormBuilder, private route: ActivatedRoute, private cdr: ChangeDetectorRef, private router: Router, private signalRService: SignalRService,
    private elementRef: ElementRef, public timerService: TimerService, private sanitizer: DomSanitizer) {
    super(pcs);
    this.groupedData.forEach((section, index) => {
      this.collapsedSections[section.sectionName] = index !== 0;
    });
    this.userName = pcs.clientSessionService.userName;
    this.currentEditor = this.userName;
  }

  editingUsers: { [key: string]: string[] } = {};
  /** Users currently on this readiness page (even if not focused on a cell). */
  pagePresenceUsers: string[] = [];
  /** cellKey -> who edited it, for cells edited by somebody other than us. */
  remoteChangedCells: { [cellKey: string]: RemoteCellChange } = {};
  currentEditor: string | null = null;
  lockedBy: string | null = null;
  private signalRHandlersRegistered = false;
  private routeSub: Subscription | null = null;
  /** Cell keys the hub already knows we changed, diffed against changedFields on every sync. */
  private broadcastedChangedCells = new Set<string>();
  private changeBroadcastTimer: any = null;
  private userColorIndexCache: { [user: string]: number } = {};

  /**
   * Stable colour slot for a user, hashed from the name so everyone on the page paints
   * the same person the same colour without the server having to hand colours out.
   */
  userColorIndex(user: string): number {
    if (!user) {
      return 0;
    }
    const cached = this.userColorIndexCache[user];
    if (cached !== undefined) {
      return cached;
    }

    let hash = 0;
    for (let i = 0; i < user.length; i++) {
      hash = ((hash << 5) - hash + user.charCodeAt(i)) | 0;
    }

    const index = Math.abs(hash) % COLLAB_COLOR_COUNT;
    this.userColorIndexCache[user] = index;
    return index;
  }

  private buildRemoteCellChange(user: string): RemoteCellChange {
    return {
      user,
      colorIndex: this.userColorIndex(user),
      tooltip: `${user} changed this cell`
    };
  }

  private setRemoteChangedCell(cellKey: string, user: string): void {
    if (!cellKey || !user || user === this.userName) {
      return;
    }

    const existing = this.remoteChangedCells[cellKey];
    if (existing && existing.user === user) {
      return;
    }

    this.remoteChangedCells = {
      ...this.remoteChangedCells,
      [cellKey]: this.buildRemoteCellChange(user)
    };
  }

  private clearRemoteChangedCell(cellKey: string, user: string): void {
    const existing = this.remoteChangedCells[cellKey];
    if (!existing || (user && existing.user !== user)) {
      return;
    }

    const next = { ...this.remoteChangedCells };
    delete next[cellKey];
    this.remoteChangedCells = next;
  }

  private clearRemoteChangesForUser(user: string): void {
    if (!user) {
      return;
    }

    const next: { [cellKey: string]: RemoteCellChange } = {};
    let removedAny = false;
    Object.keys(this.remoteChangedCells).forEach(cellKey => {
      if (this.remoteChangedCells[cellKey].user === user) {
        removedAny = true;
      } else {
        next[cellKey] = this.remoteChangedCells[cellKey];
      }
    });

    if (removedAny) {
      this.remoteChangedCells = next;
    }
  }

  /** Every cell we are currently showing as edited, pending or already auto-saved. */
  private collectLocallyChangedCells(): Set<string> {
    const cells = new Set<string>();
    [this.changedFields, this.autoSavedHighlightFields].forEach(source => {
      if (!source) {
        return;
      }
      Object.keys(source).forEach(rowNumber => {
        const columns = source[+rowNumber];
        if (!columns) {
          return;
        }
        Object.keys(columns).forEach(columnName => {
          if (columns[columnName]) {
            cells.add(rowNumber + '-' + columnName);
          }
        });
      });
    });
    return cells;
  }

  /**
   * Diff our edited cells against what the hub already knows. Diffing rather than sending
   * per keystroke keeps the traffic to one message per cell, and means any code path that
   * resets changedFields is picked up on the next sync without having to hook it.
   */
  private syncRemoteChangeBroadcast(): void {
    this.clearChangeBroadcastTimer();
    if (!this.projectId || !this.userName) {
      return;
    }

    const current = this.collectLocallyChangedCells();

    // Saving or resetting drops everything at once, so say that in one message.
    if (current.size === 0) {
      if (this.broadcastedChangedCells.size > 0) {
        this.broadcastedChangedCells = current;
        this.signalRService.clearFieldChanges(this.projectId, this.userName);
      }
      return;
    }

    this.broadcastedChangedCells.forEach(cellKey => {
      if (!current.has(cellKey)) {
        this.signalRService.notifyFieldChangeReverted(this.projectId, cellKey, this.userName);
      }
    });

    current.forEach(cellKey => {
      if (!this.broadcastedChangedCells.has(cellKey)) {
        this.signalRService.notifyFieldChanged(this.projectId, cellKey, this.userName);
      }
    });

    this.broadcastedChangedCells = current;
  }

  private scheduleRemoteChangeBroadcast(): void {
    if (this.changeBroadcastTimer) {
      return;
    }
    this.changeBroadcastTimer = setTimeout(() => this.syncRemoteChangeBroadcast(), 400);
  }

  private clearChangeBroadcastTimer(): void {
    if (this.changeBroadcastTimer) {
      clearTimeout(this.changeBroadcastTimer);
      this.changeBroadcastTimer = null;
    }
  }

  manageReceivedData(rowId, user) {
    for (const id in this.editingUsers) {
      const idx = this.editingUsers[id].indexOf(user);
      if (idx !== -1) {
        this.editingUsers[id].splice(idx, 1);
        if (this.editingUsers[id].length === 0) {
          delete this.editingUsers[id];
        }
      }
    }
    if (!this.editingUsers[rowId]) {
      this.editingUsers[rowId] = [];
    }
    if (!this.editingUsers[rowId].includes(user)) {
      this.editingUsers[rowId].push(user);
    }
  }

  private addPagePresenceUser(user: string): void {
    if (!user || user === this.userName) {
      return;
    }
    if (!this.pagePresenceUsers.includes(user)) {
      this.pagePresenceUsers = [...this.pagePresenceUsers, user];
    }
  }

  private removePagePresenceUser(user: string): void {
    this.pagePresenceUsers = this.pagePresenceUsers.filter(u => u !== user);
  }

  private removeUserFromEditingCell(rowId: string, user: string): void {
    if (!this.editingUsers[rowId]) {
      return;
    }
    this.editingUsers[rowId] = this.editingUsers[rowId].filter(u => u !== user);
    if (this.editingUsers[rowId].length === 0) {
      delete this.editingUsers[rowId];
    }
  }

  private removeUserFromAllEditingCells(user: string): void {
    Object.keys(this.editingUsers).forEach(rowId => this.removeUserFromEditingCell(rowId, user));
  }

  private joinPagePresence(): void {
    if (!this.projectId || !this.userName) {
      return;
    }
    this.signalRService.joinPresence(this.projectId, this.userName);

    // A reconnect drops our cells from the hub, so re-announce whatever is still edited here.
    this.broadcastedChangedCells = new Set<string>();
    this.syncRemoteChangeBroadcast();
  }

  hasUnsavedChanges() {
    const hasFieldChanges = Object.keys(this.changedFields || {}).some(rowNumber => {
      const rowChanges = this.changedFields[+rowNumber] || {};
      return Object.keys(rowChanges).some(columnName => columnName !== 'Status' && !!rowChanges[columnName]);
    });
    return hasFieldChanges || this.rowDragged || this.isNewRowAdded;
  }
  manageSignalR() {
    if (this.signalRHandlersRegistered) {
      this.signalRService.connect(this.projectId, () => this.joinPagePresence());
      return;
    }
    this.signalRHandlersRegistered = true;

    this.signalRService.onReceiveEditing((rowId, user) => {
      this.manageReceivedData(rowId, user);
      this.addPagePresenceUser(user);
      this.cdr.detectChanges();
    });

    this.signalRService.onUserStoppedEditing((rowId, user) => {
      this.removeUserFromEditingCell(rowId, user);
      this.cdr.detectChanges();
    });

    this.signalRService.onPresenceSnapshot((users) => {
      this.pagePresenceUsers = (users || []).filter(u => !!u && u !== this.userName);
      this.cdr.detectChanges();
    });

    this.signalRService.onUserJoined((user) => {
      this.addPagePresenceUser(user);
      this.cdr.detectChanges();
    });

    this.signalRService.onUserLeft((user) => {
      this.removePagePresenceUser(user);
      this.removeUserFromAllEditingCells(user);
      this.clearRemoteChangesForUser(user);
      this.cdr.detectChanges();
    });

    this.signalRService.onFieldChanged((cellKey, user) => {
      this.setRemoteChangedCell(cellKey, user);
      this.addPagePresenceUser(user);
      this.cdr.detectChanges();
    });

    this.signalRService.onFieldChangeReverted((cellKey, user) => {
      this.clearRemoteChangedCell(cellKey, user);
      this.cdr.detectChanges();
    });

    this.signalRService.onFieldChangesCleared((user) => {
      this.clearRemoteChangesForUser(user);
      this.cdr.detectChanges();
    });

    this.signalRService.onFieldChangesSnapshot((cellKeys, userNames) => {
      const next: { [cellKey: string]: RemoteCellChange } = {};
      (cellKeys || []).forEach((cellKey, index) => {
        const user = (userNames || [])[index];
        if (!cellKey || !user || user === this.userName) {
          return;
        }
        next[cellKey] = this.buildRemoteCellChange(user);
        this.addPagePresenceUser(user);
      });
      this.remoteChangedCells = next;
      this.cdr.detectChanges();
    });

    this.signalRService.onLocked((userName) => {
      this.lockedBy = userName;
      console.log("ReceiveLocked")
    });

    this.signalRService.onUnlocked(async (userName) => {
      this.lockedBy = null;
      console.log("Save button unlocked");

      // Their edits are persisted now, so stop painting them as somebody's pending change.
      this.clearRemoteChangesForUser(userName);
      this.cdr.detectChanges();

      const refreshData = async () => {
        await this.pcs.alert(
          `${userName} saved changes. The form is now going to refresh.`,
          'Form refresh',
          'info'
        );
        this.originalReadinessFormData = [];
        this.readinessData = null;
        this.changedFields = {};
        await this.getAllWorkPacksByProjectId();
        this.prepareDropdownsForRows("manageSignalR");
        this.initializeRowColors();
        // Getting latest threw away our pending edits, so drop our highlights for everyone else.
        this.syncRemoteChangeBroadcast();
      };

      const mergeChanges = async () => {
      };

      if (this.hasUnsavedChanges()) {
        const result = await this.pcs.confirm(
          `${userName} saved changes. You have unsaved edits. Press OK to Merge your changes, or Cancel to Get Latest changes.`,
          'Unsaved edits',
          { confirmButtonText: 'Merge', cancelButtonText: 'Get Latest' }
        );

        if (result) {
          await mergeChanges();
        } else {
          await refreshData();
        }
      } else {
        await refreshData();
      }
    });

    this.signalRService.connect(this.projectId, () => this.joinPagePresence());
  }

  // private lastMouseMove: number = Date.now();
  // private idleTimeout: any;

  // ngAfterViewInit() {
  //   document.addEventListener('mousemove', this.onMouseMove.bind(this));
  // }

  // onMouseMove() {
  //   const now = Date.now();
  //   const rowId = this.projectId;

  //   if (now - this.lastMouseMove > 5000) {
  //     console.log("Mouse is moving")
  //     this.signalRService.notifyEditing(this.projectId, rowId, this.client.userName);
  //   }

  //   this.lastMouseMove = now;

  //   clearTimeout(this.idleTimeout);
  //   this.idleTimeout = setTimeout(() => {
  //     this.signalRService.notifyStoppedEditing(this.projectId, rowId, this.client.userName);
  //   }, 20000);
  // }
  onCellFocus(rowId: string) {
    this.signalRService.notifyEditing(this.projectId, rowId, this.userName);
  }

  onCellBlur(rowId: string) {
    this.signalRService.notifyStoppedEditing(this.projectId, rowId, this.userName);
  }

  ngOnInit() {
    if (this.autoSaveEnabled) {
      this.autoSaveCountdownSec = this.autoSaveIntervalSec;
    }
    this.enablePageHorizontalScroll();
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeyDown);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);

    // Reacts to first load and project switches (same component instance reused for :id changes).
    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = +params.get('id');
      if (!id) {
        return;
      }

      const isFirstLoad = !this.projectId;
      const projectChanged = !!this.projectId && this.projectId !== id;
      if (!isFirstLoad && !projectChanged) {
        return;
      }

      if (projectChanged) {
        this.pagePresenceUsers = [];
        this.editingUsers = {};
        this.remoteChangedCells = {};
        this.broadcastedChangedCells = new Set<string>();
        this.clearChangeBroadcastTimer();
        this.cdr.detectChanges();
      }

      this.projectId = id;
      this.loadProjectData(this.projectId);
      this.loadAllData();
      this.manageSignalR();
    });
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey) {
      this.isCtrlPressed = true;
    }
  };

  handleKeyUp = (event: KeyboardEvent) => {
    if (!event.ctrlKey) {
      this.isCtrlPressed = false;
    }
  };

  onGripMouseDown(event: MouseEvent) {
    this.isGripActive = true;
    event.stopPropagation();
  }

  onGripMouseUp(event: MouseEvent) {
    this.isGripActive = false;
    event.stopPropagation();
  }

  onDragEnd(): void {
    this.isCtrlPressed = false;
    this.isGripActive = false;
    const draggingEls = Array.from(document.querySelectorAll('.dragging'));
    draggingEls.forEach(el => el.classList.remove('dragging'));
    this.draggedRow = null;
    this.draggedRows = null;
    this.originalTaskNumbers = [];
  }

  async loadAllData() {
    try {
      this.isLoading = true;
      // Now load work packs and readiness data
      await Promise.all([
        this.getAllWorkPacksByProjectId(),
        this.loadReadinessData(this.projectId)
      ]);

      // Now, get the traffic lights and column data as they're independent
      await Promise.all([
        this.GetReadinessTrafficLightsData(),
        this.GetReadinessColumnData()
      ]);

      await this.loadDropDownData();

      // Now prepare dropdowns only if we have all required data
      if (this.getCommandResponse && this.readinessData.ReadinessFormData) {
        await this.prepareDropdownsForRows('loadAllData');
        this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
        this.initializeRowColors();
      } else {
        console.error('Missing required data for preparing dropdowns', {
          // hasCommandResponse: !!this.getCommandResponse,
          // hasReadinessData: !!this.readinessData?.ReadinessFormData
        });
      }
      this.startAutoSaveTimer();
      this.readinessLoadError = null;
    } catch (error) {
      console.error('An API call failed:', error);
      this.readinessLoadError = this.describeLoadFailure(error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * A readiness grid can be large enough that the query takes a while. If it does fail, say so
   * plainly rather than leaving an empty table, which reads as "this project has no tasks".
   */
  private describeLoadFailure(error: any): string {
    const text = [
      error?.Message,
      error?.message,
      error?.error?.Message,
      typeof error === 'string' ? error : ''
    ].filter(Boolean).join(' ').toLowerCase();

    if (text.indexOf('timeout') >= 0 || text.indexOf('timed out') >= 0) {
      return 'This project has a lot of readiness data and the request took too long to complete. '
        + 'Nothing you saved has been lost. Please refresh the page to try again.';
    }

    if (text.indexOf('401') >= 0 || text.indexOf('unauthor') >= 0) {
      return 'Your session has expired. Please refresh the page and sign in again.';
    }

    return 'The readiness data could not be loaded. Nothing you saved has been lost. '
      + 'Please refresh the page to try again, and contact support if it keeps happening.';
  }

  async retryLoadAllData() {
    this.readinessLoadError = null;
    await this.loadAllData();
  }

  async GetReadinessTrafficLightsData() {
    try {
      const response = await this.pcs.apiService.GetReadinessTrafficLightRule({ SectionName: null, ProPakProjectId: this.projectId });

      if (response && response.ReadinessTrafficLightData) {
        this.trafficLightRules = response.ReadinessTrafficLightData;
      } else {
        console.error("Invalid API response:", response);
        this.trafficLightRules = [];
      }
    } catch (error) {
      console.error("Error fetching readiness traffic lights data:", error);
    }
  }

  async loadReadinessData(id: number) {
    // Do not toggle isLoading here — loadAllData owns the form loading state.
    // Clearing it here caused the skeleton loader to disappear before form data finished loading.
    let payload: GetReadinessSummaryCommandRequest = { ProjectId: id, ProPakProjectId: id }
    const response: GetReadinessSummaryCommandResponse = await this.pcs.apiService.GetReadinessSummary(payload, false, false);

    if (response.ReadinessSummary.Priorities.length > 0) {
      for (let priority of response.ReadinessSummary.Priorities) {
        if (priority.TaskCountTotal > 0) {
          this.readinessSummary = response.ReadinessSummary;
          break;
        }
      }
    } else {
      console.log("In ProjectSummaryComponent - loadReadinessData(), system could not find any priorities for readiness.");
    }
  }

  async GetReadinessColumnData() {
    try {
      // this.isLoading = true;
      const response = await this.pcs.apiService.ReadinessAdminData({ ProPakProjectId: this.projectId, IncludeHiddenColumns: false });

      if (response && response.ReadinessTableData) {
        this.readinessColumnData = response.ReadinessTableData;
        this.readinessColumnData.forEach(row => {
          if (row.ColumnName == 'PriorityId') {
            row.IsRequired = true;
          }
        });
        this.processColumns();
      } else {
        console.error("Invalid API response:", response);
        this.readinessColumnData = [];
      }
    } catch (error) {
      console.error("Error fetching readiness data:", error);
    }
    finally {
      // this.isLoading = false;
    }
    this.groupData();
  }

  groupData() {
    const grouped = this.readinessColumnData.reduce((acc, item) => {
      let section = acc.find(g => g.sectionName === item.SectionName);
      if (!section) {
        section = { sectionName: item.SectionName, sectionOrder: item.SectionOrder, data: [] };
        acc.push(section);
        this.categoryToggle[item.SectionName] = false; // Default to collapse
      }
      section.data.push(item);
      return acc;
    }, []);

    // Sort sections by SectionOrder
    this.groupedData = grouped.sort((a, b) => a.sectionOrder - b.sectionOrder);

    // Sort data within each section by ColumnOrder
    this.groupedData.forEach(group => {
      group.data.sort((a, b) => a.ColumnOrder - b.ColumnOrder);
    });
  }

  get isAnySectionVisible(): boolean {
    return this.groupedColumns.some((section, index) =>
      index !== 0 && section.sectionName !== 'Other' && this.sectionVisibility[section.sectionName]
    );
  }

  // initializeResizableColumns() {
  //   const tables = document.querySelectorAll('table');

  //   if (!tables.length) {
  //     console.warn('No tables found on the page.');
  //     return;
  //   }

  //   for (let t = 0; t < tables.length; t++) {
  //     const table = tables[t];

  //     const tableHeaders = table.querySelectorAll('thead th');

  //     let currentHeader: HTMLElement | null = null;
  //     let startX = 0;
  //     let startWidth = 0;

  //     for (let i = 0; i < tableHeaders.length; i++) {
  //       const header = tableHeaders[i] as HTMLElement;

  //       const resizeHandle = document.createElement('div');
  //       resizeHandle.className = 'resize-handle';
  //       resizeHandle.style.width = '5px';
  //       resizeHandle.style.height = '100%';
  //       resizeHandle.style.position = 'absolute';
  //       resizeHandle.style.right = '0';
  //       resizeHandle.style.top = '0';
  //       resizeHandle.style.cursor = 'col-resize';
  //       resizeHandle.style.zIndex = '1';

  //       header.style.position = 'relative';
  //       header.appendChild(resizeHandle);

  //       resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
  //         e.preventDefault();
  //         currentHeader = header;
  //         startX = e.pageX;
  //         startWidth = header.offsetWidth;
  //         document.addEventListener('mousemove', resizeColumn);
  //         document.addEventListener('mouseup', stopResizing);
  //       });
  //     }

  //     const resizeColumn = (e: MouseEvent) => {
  //       if (currentHeader) {
  //         const diffX = e.pageX - startX;
  //         const newWidth = startWidth + diffX;
  //         if (newWidth > 50) {
  //           const columnIndex = Array.from(currentHeader.parentElement!.children).indexOf(currentHeader);
  //           for (let i = 0; i < tableHeaders.length; i++) {
  //             const th = tableHeaders[i];
  //             const thIndex = Array.from(th.parentElement!.children).indexOf(th);
  //             if (thIndex === columnIndex) {
  //               (th as HTMLElement).style.width = `${newWidth}px`;
  //             }
  //           }
  //         }
  //       }
  //     };

  //     const stopResizing = () => {
  //       document.removeEventListener('mousemove', resizeColumn);
  //       document.removeEventListener('mouseup', stopResizing);
  //       currentHeader = null;
  //     };
  //   }
  // }

  initializeResizableRowsForTables() {
    const tables = document.querySelectorAll('table');

    const table1Rows = tables[0].querySelectorAll('tbody tr') as NodeListOf<HTMLElement>;

    let currentRowIndex: number | null = null;
    let startY = 0;
    let startHeightTable1 = 0;
    let startHeightTable2 = 0;

    for (let i = 0; i < table1Rows.length; i++) {
      const row1 = table1Rows[i];

      // Create resize handle for both tables
      const resizeHandle1 = document.createElement('div');
      resizeHandle1.className = 'resize-handle';
      resizeHandle1.style.width = '100%';
      resizeHandle1.style.height = '5px';
      resizeHandle1.style.position = 'absolute';
      resizeHandle1.style.bottom = '0';
      resizeHandle1.style.left = '0';
      resizeHandle1.style.cursor = 'row-resize';
      resizeHandle1.style.zIndex = '1';



      row1.style.position = 'relative';
      row1.appendChild(resizeHandle1); // Append to the first table's row


      // Add event listeners for resizing (only on the resize handle)
      resizeHandle1.addEventListener('mousedown', (e: MouseEvent) => {
        // Ensure this only activates when clicking on the resize handle, not other areas of the row.
        if (e.target !== resizeHandle1) return;
        e.preventDefault();
        e.stopPropagation();  // Prevent event from propagating to row and interfering with other interactions
        currentRowIndex = i;
        startY = e.pageY;
        startHeightTable1 = row1.offsetHeight;
        document.addEventListener('mousemove', resizeRows);
        document.addEventListener('mouseup', stopResizing);
      });

    }

    const resizeRows = (e: MouseEvent) => {
      if (currentRowIndex !== null) {
        const diffY = e.pageY - startY;

        const newHeight = Math.max(10, startHeightTable1 + diffY); // Minimum height = 20px
        table1Rows[currentRowIndex].style.height = `${newHeight}px`;
      }
    };

    const stopResizing = () => {
      document.removeEventListener('mousemove', resizeRows);
      document.removeEventListener('mouseup', stopResizing);
      currentRowIndex = null;
    };
  }

  async getArchiveTask() {
    if (!this.isProPakWorkPackAvailable) {
      var tasks = await this.pcs.apiService.GetArchiveTask({ ProjectId: this.projectId });
      this.readinessData.ReadinessFormData = tasks.ArchiveTasks;
    }
    else {
      this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify(this.originalArchiveReadinessFormData));
    }
    let maxRowNumber = Math.max(...this.originalCurrentReadinessFormData.map(x => x.ActualRowNumber));
    this.readinessData.ReadinessFormData.forEach(row => {
      row["ApprovedForProjectSection"] = "#e74c3c";
      row["WPStatusSection"] = "#e74c3c";
      row["TRStatusSection"] = "#e74c3c";
      row["MaterialsSection"] = "#e74c3c";
      row["MTLMOCSection"] = "#e74c3c";
      row["IsArchived"] = true;
      row["ActualNumberSaved"] = row.Number;
      row.Number = ++maxRowNumber;
      row.ActualRowNumber = row.Number;
    });
    // Snapshot originals AFTER ActualRowNumber is remapped so onFieldChange / dirty checks match.
    this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));

    this.fillFormList();
    this.cdr.detectChanges();
    // this.initializeResizableColumns();
    this.initializeResizableRowsForTables();
    // this.setTaskNumber();
    this.initializeRowColors();
  }

  fillFormList() {
    this.formsList = [];
    if (this.readinessData != null && this.readinessData.ReadinessFormData != null && this.readinessData.ReadinessFormData.length > 0) {
      const newForm = {
        isCollapsed: false,
        sortColumn: '',
        sortOrder: 'asc',
        rows: this.readinessData.ReadinessFormData.map((data) => ({
          archiveTaskId: data.ArchiveTaskId,
          number: data.Number,
          actualRowNumber: data.ActualRowNumber,
          actualNumberSaved: data.Number,
          enableDisplay: data["EnableDisplay"],
          taskDetails: {
            taskNumber: data.TaskNumber || '',
            taskDescription: data.TaskDescription || '',
            number: data.ActualRowNumber,
          },
          otherDetails: {
            scope: data.Scope,
            priorityId: data.PriorityId,
            owner: data.Owner,
            discipline: data.Discipline,
            contractor: data.Contractor,
            active: true,
            projectId: data.ProjectId,
            projectPhaseId: data.ProjectPhaseId,
            projectTaskId: data.ProjectTaskId,
            importStart: data.ImportStart,
            importEnd: data.ImportEnd,
            successful: data.Successful,
            importFileLocation: '',
            tarApprovalId: data.TARApprovalId,
            heightAccessId: data.HeightAccessId,
            crane: data.Crane,
            hotWork: data.HotWork,
            isolationPlanId: data.IsolationPlanId,
            workPackRequiredId: data.WorkPackRequiredId,
            revId: data.RevId,
            materialStatusId: data.MaterialStatusId,
            longLead: data.LongLead,
            fabricationId: data.FabricationId,
            stagedOnSite: data.StagedOnSite,
            mocRequired: data.MOCRequired,
            mocNumber: data.MOCNumber,
            mocStatusId: data.MOCStatusId,
            comments: data.Comments,
            approvedProjTAR: data.ApprovedProjTAR,
            trStatus: data.TRStatus,
            wpStatus: data.WPStatus,
            materials: data.Materials,
            materialMOC: data.MaterialMOC,
            status: data.Status,
            shutInRequired: data.ShutInRequired
          },
          isSelected: false
        })),
      }
      this.formsList.push(newForm);
      // this.originalFormsList = JSON.parse(JSON.stringify(this.formsList));
    }
    //   else if (this.allWorkPacks != null && this.allWorkPacks.WorkPacks != null && this.allWorkPacks.WorkPacks.length > 0) {
    //   this.formsList.push({
    //     isCollapsed: false,
    //     sortColumn: '',
    //     sortOrder: 'asc',
    //     rows: this.allWorkPacks.WorkPacks.map((workPack, index) => ({
    //       archiveTaskId: 0,
    //       taskDetails: {
    //         taskNumber: workPack.Number || '',
    //         taskDescription: workPack.Description || '',
    //         number: index + 1
    //       },
    //       otherDetails: {
    //         proPakProjectId: this.projectId,
    //         scope: '',
    //         priorityId: -1,
    //         owner: '',
    //         discipline: '',
    //         contractor: '',
    //         active: true,
    //         projectId: 0,
    //         projectPhaseId: this.projectPhaseId || 0,
    //         importStart: null,
    //         importEnd: null,
    //         successful: false,
    //         importFileLocation: '',
    //         tarApprovalId: -1,
    //         heightAccessId: -1,
    //         crane: false,
    //         hotWork: false,
    //         isolationPlanId: -1,
    //         workPackRequiredId: -1,
    //         revId: 0,
    //         materialStatusId: -1,
    //         longLead: false,
    //         fabricationId: -1,
    //         stagedOnSite: false,
    //         mocRequired: false,
    //         mocNumber: '',
    //         mocStatusId: -1,
    //         comments: '',
    //         approvedProjTAR: 0,
    //         trStatus: 0,
    //         wpStatus: 0,
    //         materials: 0,
    //         materialMOC: 0,
    //         status: '',
    //         shutInRequired: false
    //       },
    //       isSelected: false
    //     })),
    //   });
    // }
    else {
      this.formsList.push({
        isCollapsed: false,
        sortColumn: '',
        sortOrder: 'asc',
        rows: [],
        noDataMessage: "No data available"
      });
    }

  }
  async GetProjectReadinessData() {
    this.readinessData = await this.pcs.apiService.GetReadinessFormData({ ProjectId: this.projectId });
    if (this.readinessData && this.readinessData.ReadinessFormData && this.readinessData.ReadinessFormData.length) {
      await this.loadAttachmentCountsForReadinessData();
      this.loadThumbnailsForReadinessData();
    }
  }

  async getWorkPackFromProPak() {
    this.allWorkPacks = await this.pcs.apiService.GetWorkpacksByProject({ ProjectId: this.projectId });
    this.isProPakWorkPackAvailable = this.allWorkPacks.WorkPacks != null && this.allWorkPacks.WorkPacks.length > 0;
    this.allWorkPacks.WorkPacks = this.allWorkPacks.WorkPacks.sort((a, b) => {
      if (a.IsArchived === b.IsArchived) return 0;
      return a.IsArchived ? 1 : -1;
    });
    this.readinessData.ReadinessFormData = this.allWorkPacks.WorkPacks
      .map((wp, index) => {
        return {
          TaskNumber: wp.Number || '',
          TaskDescription: wp.Description || '',
          Number: index + 1,
          ProPakProjectId: this.projectId,
          Scope: '',
          PriorityId: -1,
          Owner: wp.CreatedBy || '',
          Discipline: '',
          Contractor: '',
          Active: true,
          ProjectPhaseId: this.projectPhaseId || 0,
          ImportStart: null,
          ImportEnd: null,
          ImportFileLocation: '',
          TARApprovalId: -1,
          HeightAccessId: -1,
          Crane: null,
          HotWork: null,
          IsolationPlanId: -1,
          WorkPackRequiredId: -1,
          RevId: -1,
          MaterialStatusId: -1,
          LongLead: null,
          FabricationId: -1,
          StagedOnSite: null,
          MOCRequired: null,
          MOCNumber: '',
          MOCStatusId: -1,
          Comments: '',
          ApprovedProjTAR: 0,
          TRStatus: 0,
          WPStatus: 0,
          Materials: 0,
          MaterialMOC: 0,
          Status: '',
          ShutInRequired: null,
          ArchiveTaskId: 0,
          ProjectId: wp.ProjectId || 0,
          Successful: true,
          ProjectTaskId: 0,
          IsSelected: false,
          ApprovedForProjectSection: "#e74c3c",
          TRStatusSection: "#e74c3c",
          MTLMOCSection: "#e74c3c",
          WPStatusSection: "#e74c3c",
          MaterialsSection: "#e74c3c",
          // IsNew: true,
          IsArchived: wp.IsArchived,
          ActualRowNumber: index + 1,
          ImageBlobUrl: '',
          AttachmentsJson: '',
          PIDAttachmentURL: ''
        };
      });

    this.modifiedCombinedReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
  }
  async getProjectTasks() {
    this.formsList = [];
    this.readinessData = await this.pcs.apiService.GetReadinessFormData({ ProjectId: this.projectId });
    if (this.readinessData == null || this.readinessData.ReadinessFormData.length == 0) {
      this.isDisplayProPakWPToggle = true;
    } else {
      await this.loadAttachmentCountsForReadinessData();
      this.loadThumbnailsForReadinessData();
    }
    return this.readinessData;
  }
  async getAllWorkPacksByProjectId() {
    if (this.isProPakWPEnable) {
      await this.getWorkPackFromProPak();
    }
    else {
      this.isDisplayProPakWPToggle = false;
      await this.getProjectTasks();
    }
    // this.readinessData = await this.pcs.apiService.GetReadinessFormData({ ProjectId: this.projectId });
    // if(this.readinessData == null || this.readinessData.ReadinessFormData.length == 0)
    // {
    // this.allWorkPacks = await this.pcs.apiService.GetWorkpacksByProject({ ProjectId: this.projectId });
    // this.readinessData.ReadinessFormData = this.allWorkPacks.WorkPacks.map((wp, index) => {
    //   const readiness = new ReadinessDataObject();
    //   readiness.ProjectId = wp.ProjectId;
    //   readiness.TaskNumber = wp.Number;
    //   readiness.TaskDescription = wp.Description;
    //   readiness.ApprovedProjTAR = 0;
    //   readiness.WPStatus = 0;
    //   // readiness.ProjectTaskId = wp.WorkPackId;
    //   readiness.Number = index + 1;

    //   readiness.ImportStart = wp.CreatedDate;
    //   // readiness.ImportEnd = wp.ModifiedDate;
    //   readiness.Owner = wp.CreatedBy;
    //   // readiness.Contractor = wp.ModifiedBy;

    //   return readiness;
    // });
    // }
    console.log("In method getAllWorkPacksByProjectId after API call, response: ", this.readinessData);

    this.readinessData.ReadinessFormData.forEach(row => {
      row["ApprovedForProjectSection"] = "#e74c3c";
      row["WPStatusSection"] = "#e74c3c";
      row["TRStatusSection"] = "#e74c3c";
      row["MaterialsSection"] = "#e74c3c";
      row["MTLMOCSection"] = "#e74c3c";
      // row["IsArchived"] = false;
      // row["ActualRowNumber"] = row.Number;
    });
    if (this.isCurrentTaskSelected) {
      this.readinessData.ReadinessFormData.forEach(row => {
        if (row.IsArchived) {
          row["EnableDisplay"] = false;
        }
        else {
          row["EnableDisplay"] = true;
        }
      })
    }
    else if (this.isArchiveTaskSelected) {
      this.readinessData.ReadinessFormData.forEach(row => {
        if (row.IsArchived) {
          row["EnableDisplay"] = true;
        }
        else {
          row["EnableDisplay"] = false;
        }
      })
    }
    else {
      this.readinessData.ReadinessFormData.forEach(row => {
        row["EnableDisplay"] = true;
      });
    }
    if (this.isProPakWPEnable) {
      await this.prepareDropdownsForRows('getAllWorkPacksByProjectId');
    }
    this.applySearchAndFilterVisibility();
    this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    this.fillFormList();
    // this.setTaskNumber();
    this.cdr.detectChanges();
    // this.initializeResizableColumns();
    this.initializeResizableRowsForTables();
    if (this.getCommandResponse)
      this.initializeRowColors();
  }

  setTaskNumber() {
    this.originalTaskNumbers = this.readinessData.ReadinessFormData.map((item, index, array) =>
      array.every(row => row.Number === 0) ? index + 1 : item.Number
    );
  }

  getSectionColor(row: any, sectionName: string): string {
    if (!this.groupedColumns || this.groupedColumns.length === 0) return '#e74c3c';

    if (sectionName === 'APPROVED For Project/TAR') {
      let value = row['ApproveForTAR'];
      if (value === null || value === undefined || value === '') {
        return 'red';
      }
      return 'green';
    }

    let section = this.groupedColumns.find(sec => sec.sectionName === sectionName);
    if (!section) return '#e74c3c';

    let totalColumns = section.columns.length;
    let filledColumns = section.columns.filter(col => row[col.ColumnName] && row[col.ColumnName] !== '').length;

    if (filledColumns === 0) {
      return '#e74c3c';
    } else if (filledColumns === totalColumns) {
      return '#8bc193';
    } else {
      return '#f1c40f';
    }
  }

  async loadDropDownData() {
    this.getCommandResponse = null;
    this.getCommandResponse = await this.pcs.apiService.Admin({ ProPakProjectId: this.projectId });
    console.log("In method loadDropDownData after API call, response: ", this.getCommandResponse);
    if (this.getCommandResponse) {
      this.dropdownOptions = {
        PriorityId: this.getCommandResponse.Priorities,
        TARApprovalId: this.getCommandResponse.TARApprovals,
        ShutInRequired: this.options,
        HeightAccessId: this.getCommandResponse.HeightAccesses,
        IsolationPlanId: this.getCommandResponse.IsolationPlans,
        Crane: this.options,
        HotWork: this.options,
        MaterialStatusId: this.getCommandResponse.MaterialStatuses,
        FabricationId: this.getCommandResponse.Fabrications,
        MOCStatusId: this.getCommandResponse.MOCStatuses,
        ProjectPhases: this.getCommandResponse.ProjectPhases,
        RevId: this.getCommandResponse.Revs,
        WorkPackRequiredId: this.getCommandResponse.WorkPackRequired,
        StagedOnSite: this.getCommandResponse.StagedOnSites,
        LongLead: this.options,
        MOCRequired: this.options
      };

      const readinessPhase = this.getCommandResponse.ProjectPhases.find(x => x.PhaseName === 'Readiness');
      this.projectPhaseId = readinessPhase ? readinessPhase.ProjectPhaseId : null;

      await this.prepareDropdownsForRows('loadDropDownData');
      this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
      this.initializeRowColors();
    }

    return this.getCommandResponse;
  }

  addNewTask(parentIndex: number) {
    this.isNewRowAdded = true;
    let taskNumber = '';
    let rowLength = 0;

    const selectedRows = this.readinessData.ReadinessFormData.filter(row => row["IsSelected"]);


    // Helper to extract prefix, numeric, and optional trailing letter
    function extractTaskParts(taskNumber: string): { prefix: string, number: number, numberLength: number, letter: string } {
      const match = taskNumber.match(/^(.*?)(\d+)([A-Za-z]?)$/);
      if (match) {
        return {
          prefix: match[1],
          number: parseInt(match[2], 10),
          numberLength: match[2].length,
          letter: match[3] || ''
        };
      }
      return { prefix: taskNumber, number: 0, numberLength: 0, letter: '' };
    }

    let maxParts = { prefix: '', number: 0, numberLength: 0, letter: '' };
    let found = false;
    this.readinessData.ReadinessFormData.forEach(row => {
      const parts = extractTaskParts(row.TaskNumber);
      if (!found ||
        parts.prefix > maxParts.prefix ||
        (parts.prefix === maxParts.prefix && parts.number > maxParts.number) ||
        (parts.prefix === maxParts.prefix && parts.number === maxParts.number && parts.letter > maxParts.letter)
      ) {
        maxParts = parts;
        found = true;
      }
    });

    let newTaskNumber = '';
    if (maxParts.letter) {
      let nextLetter = '';
      const lastChar = maxParts.letter;
      if (/^[A-Za-z]$/.test(lastChar)) {
        if (lastChar.toUpperCase() === 'Z') {
          nextLetter = lastChar === lastChar.toUpperCase() ? 'AA' : 'aa';
        } else {
          nextLetter = String.fromCharCode(lastChar.charCodeAt(0) + 1);
        }
      }
      newTaskNumber = `${maxParts.prefix}${maxParts.number.toString().padStart(maxParts.numberLength, '0')}${nextLetter}`;
    } else {
      newTaskNumber = `${maxParts.prefix}${(maxParts.number + 1).toString().padStart(maxParts.numberLength, '0')}`;
    }
    taskNumber = this.makeTaskNumberUnique(newTaskNumber);

    let maxRowNumber = this.readinessData.ReadinessFormData.length > 0
      ? Math.max(...this.readinessData.ReadinessFormData.map(x => x.ActualRowNumber))
      : 0;
    let newRow = {
      TaskNumber: taskNumber || '',
      TaskDescription: '',
      Number: maxRowNumber + 1,
      ActualRowNumber: maxRowNumber + 1,
      ProPakProjectId: this.projectId,
      Scope: '',
      PriorityId: -1,
      Owner: '',
      Discipline: '',
      Contractor: '',
      Active: true,
      ProjectPhaseId: this.projectPhaseId || 0,
      ImportStart: null,
      ImportEnd: null,
      ImportFileLocation: '',
      TARApprovalId: -1,
      HeightAccessId: -1,
      Crane: null,
      HotWork: null,
      IsolationPlanId: -1,
      WorkPackRequiredId: -1,
      RevId: -1,
      MaterialStatusId: -1,
      LongLead: null,
      FabricationId: -1,
      StagedOnSite: null,
      MOCRequired: null,
      MOCNumber: '',
      MOCStatusId: -1,
      Comments: '',
      ApprovedProjTAR: 0,
      TRStatus: 0,
      WPStatus: 0,
      Materials: 0,
      MaterialMOC: 0,
      Status: '0%',
      ShutInRequired: null,
      ArchiveTaskId: 0,
      ProjectId: 0,
      Successful: true,
      ProjectTaskId: 0,
      IsSelected: false,
      ApprovedForProjectSection: "#e74c3c",
      TRStatusSection: "#e74c3c",
      MTLMOCSection: "#e74c3c",
      WPStatusSection: "#e74c3c",
      MaterialsSection: "#e74c3c",
      IsNew: true,
      IsArchived: false,
      EnableDisplay: true,
      ImageBlobUrl: '',
      AttachmentsJson: '',
      PIDAttachmentURL: ''
    };
    this.originalReadinessFormData.push(JSON.parse(JSON.stringify(newRow)));
    if (selectedRows.length > 0) {
      const selectedIndex = this.readinessData.ReadinessFormData.findIndex(row => row["IsSelected"]);
      this.readinessData.ReadinessFormData.splice(selectedIndex + 1, 0, newRow);
    } else {
      this.readinessData.ReadinessFormData.push(newRow);
    }
    this.onFieldChange(newRow.ActualRowNumber, 'Status', newRow.Status, false)
    this.prepareDropdownsForRows('addNewTask');
    this.prepareDropdownsForSingleRow(newRow);
    if (this.isGroupingEnabled)
      this.groupRowsByColumns(this.groupedByColumns);
  }

  /**
   * Every task number already in the grid, lower-cased so a generated number
   * cannot differ from an existing one by case alone.
   */
  private getTakenTaskNumbers(): Set<string> {
    const taken = new Set<string>();
    const rows = (this.readinessData && this.readinessData.ReadinessFormData) || [];
    rows.forEach(row => {
      const taskNumber = (row.TaskNumber || '').toString().trim();
      if (taskNumber) {
        taken.add(taskNumber.toLowerCase());
      }
    });
    return taken;
  }

  /**
   * Keeps a generated task number off one the grid already uses, so the plus
   * button never inserts a duplicate that then blocks the save. Inserting below
   * 5 gives 6 while 6 is free; once 6 is taken it gives 6-new, then 6-new-1,
   * 6-new-2, and so on for each further click.
   */
  private makeTaskNumberUnique(candidate: string): string {
    const baseNumber = (candidate || '').toString().trim();
    if (!baseNumber) {
      return candidate;
    }

    const taken = this.getTakenTaskNumbers();
    if (!taken.has(baseNumber.toLowerCase())) {
      return baseNumber;
    }

    const suffixedNumber = `${baseNumber}-new`;
    if (!taken.has(suffixedNumber.toLowerCase())) {
      return suffixedNumber;
    }

    let counter = 1;
    while (taken.has(`${suffixedNumber}-${counter}`.toLowerCase())) {
      counter++;
    }
    return `${suffixedNumber}-${counter}`;
  }

  addRowBelow(parentIndex: number) {

    // Mark selected row
    this.readinessData.ReadinessFormData[parentIndex]["IsSelected"] = true;

    const parentTaskNumber: string =
      this.readinessData.ReadinessFormData[parentIndex].TaskNumber || '';

    let taskNumber = '';

    if (parentTaskNumber.includes('-')) {
      const parts = parentTaskNumber.split('-');
      const lastPart = parts[parts.length - 1];

      const numericValue = parseInt(lastPart, 10);
      const paddedLength = lastPart.length;

      if (!isNaN(numericValue)) {
        const newNumber = (numericValue + 1)
          .toString()
          .padStart(paddedLength, '0');

        const prefix = parts.slice(0, -1).join('-');
        taskNumber = `${prefix}-${newNumber}`;
      } else {
        taskNumber = parentTaskNumber + '-01';
      }
    } else {
      const numericValue = parseInt(parentTaskNumber, 10);
      const paddedLength = parentTaskNumber.length;

      if (!isNaN(numericValue)) {
        taskNumber = (numericValue + 1)
          .toString()
          .padStart(paddedLength, '0');
      } else {
        taskNumber = parentTaskNumber + '-01';
      }
    }

    taskNumber = this.makeTaskNumberUnique(taskNumber);

    const newRow = {
      TaskNumber: taskNumber,
      TaskDescription: '',
      Number: 0,
      ActualRowNumber: 0,
      ProPakProjectId: this.projectId,
      Scope: '',
      PriorityId: -1,
      Owner: '',
      Discipline: '',
      Contractor: '',
      Active: true,
      ProjectPhaseId: this.projectPhaseId || 0,
      ImportStart: null,
      ImportEnd: null,
      ImportFileLocation: '',
      TARApprovalId: -1,
      HeightAccessId: -1,
      Crane: null,
      HotWork: null,
      IsolationPlanId: -1,
      WorkPackRequiredId: -1,
      RevId: -1,
      MaterialStatusId: -1,
      LongLead: null,
      FabricationId: -1,
      StagedOnSite: null,
      MOCRequired: null,
      MOCNumber: '',
      MOCStatusId: -1,
      Comments: '',
      ApprovedProjTAR: 0,
      TRStatus: 0,
      WPStatus: 0,
      Materials: 0,
      MaterialMOC: 0,
      Status: '0%',
      ShutInRequired: null,
      ArchiveTaskId: 0,
      ProjectId: 0,
      Successful: true,
      ProjectTaskId: 0,
      IsSelected: false,
      ApprovedForProjectSection: "#e74c3c",
      TRStatusSection: "#e74c3c",
      MTLMOCSection: "#e74c3c",
      WPStatusSection: "#e74c3c",
      MaterialsSection: "#e74c3c",
      IsNew: true,
      IsArchived: false,
      EnableDisplay: true,
      ImageBlobUrl: '',
      AttachmentsJson: '',
      PIDAttachmentURL: ''
    };

    this.readinessData.ReadinessFormData.splice(parentIndex + 1, 0, newRow);

    this.readinessData.ReadinessFormData.forEach((row, idx) => {
      row.Number = idx + 1;
      row.ActualRowNumber = idx + 1;
    });

    this.originalReadinessFormData =
      JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));

    this.prepareDropdownsForRows('addRowBelow');
    this.prepareDropdownsForSingleRow(newRow);

    if (this.isGroupingEnabled) {
      this.groupRowsByColumns(this.groupedByColumns);
    }

    this.readinessData.ReadinessFormData[parentIndex]["IsSelected"] = false;

    this.onFieldChange(newRow.ActualRowNumber, 'Status', newRow.Status, false);
  }




  extractTaskNumberParts(taskNumber: string): (string | number)[] {
    const parts: (string | number)[] = [];
    const regex = /(\d+|[a-zA-Z]+)/g;
    const matches = taskNumber.match(regex);

    if (matches) {
      for (const match of matches) {
        const numPart = parseInt(match, 10);
        if (!isNaN(numPart)) {
          parts.push(numPart); // Add as a number
        } else {
          parts.push(match.toUpperCase()); // Add as a string (convert to uppercase for case-insensitive comparison)
        }
      }
    }
    return parts;
  }

  compareTaskNumbers(a: ReadinessData, b: ReadinessData): number {
    const partsA = this.extractTaskNumberParts(a.TaskNumber);
    const partsB = this.extractTaskNumberParts(b.TaskNumber);

    const minLength = Math.min(partsA.length, partsB.length);

    for (let i = 0; i < minLength; i++) {
      if (typeof partsA[i] === 'number' && typeof partsB[i] === 'number') {
        const numCompare = (partsA[i] as number) - (partsB[i] as number);
        if (numCompare !== 0) return numCompare;
      } else {
        const strCompare = String(partsA[i]).localeCompare(String(partsB[i]));
        if (strCompare !== 0) return strCompare;
      }
    }

    return partsA.length - partsB.length;
  }

  getGreatestTaskNumber(data: ReadinessData[]): string | undefined {
    const sortedData = [...data].sort(this.compareTaskNumbers).reverse();
    return sortedData[0].TaskNumber;
  }

  /**
   * Identifies a row for the duplicate highlight. Current and archived tasks are
   * checked against each other separately, so the group belongs in the key: the
   * same number in both lists is not a duplicate.
   */
  private getDuplicateRowKey(taskNumber: string, isArchived: boolean): string {
    const normalized = (taskNumber || '').toString().trim().toLowerCase();
    return (isArchived ? 'archive|' : 'current|') + normalized;
  }

  /** True while the last save attempt still reports this row as a duplicate. */
  isDuplicateTaskRow(row: any): boolean {
    if (!row || this.duplicateTaskRows.size === 0) {
      return false;
    }
    return this.duplicateTaskRows.has(
      this.getDuplicateRowKey(row.TaskNumber, row.ArchiveTaskId !== 0));
  }

  /**
   * Drops the highlights a save attempt leaves on rows. Called at the start of every
   * attempt, so a resolved duplicate stops being blue as soon as the next save gets
   * past the duplicate check instead of staying highlighted for the rest of the
   * session.
   */
  private clearSaveRowHighlights(): void {
    if (this.duplicateTaskRows.size > 0) {
      this.duplicateTaskRows = new Set<string>();
    }
    // The ProPak path still adds highlight-invalid imperatively.
    const staleInvalidRows = document.querySelectorAll('.highlight-invalid');
    for (let i = 0; i < staleInvalidRows.length; i++) {
      staleInvalidRows[i].classList.remove('highlight-invalid');
    }
  }

  /**
   * Public entry point for saving. Holds a lock so a second manual Save (or an autosave
   * tick landing mid-save) cannot start a concurrent save of the same rows.
   */
  async saveAllTasks(options?: { silent?: boolean; skipIdSync?: boolean; showLoader?: boolean }): Promise<boolean> {
    if (this.saveInProgress) {
      if (!(options && options.silent === true)) {
        this.pcs.notificationService.info('Save in progress', 'Please wait for the current save to finish.');
      }
      return false;
    }
    this.saveInProgress = true;
    try {
      return await this.saveAllTasksInternal(options);
    } finally {
      this.saveInProgress = false;
    }
  }

  private async saveAllTasksInternal(options?: { silent?: boolean; skipIdSync?: boolean; showLoader?: boolean }): Promise<boolean> {
    const silent = options && options.silent === true;
    const skipIdSync = options && options.skipIdSync === true;
    const showLoader = options && typeof options.showLoader === 'boolean' ? options.showLoader : !silent;
    this.clearSaveRowHighlights();
    if (this.isProPakWorkPackAvailable) {
      return await this.saveProPakLoadedWP(silent, skipIdSync, showLoader);
    }
    const updatedColumnData = this.readinessColumnData.map(column => {
      // Create a shallow copy of each column
      const copiedColumn = { ...column };

      // If column is PriorityId and not required, mark it as required
      if (copiedColumn.ColumnName === 'PriorityId' && !copiedColumn.IsRequired) {
        copiedColumn.IsRequired = true;
      }

      return copiedColumn;
    });
    let taskIdMap = new Map<string, number[]>();
    let archiveTaskIds = new Map<string, number[]>();
    let currentTaskIds = new Map<string, number[]>();

    let duplicateRows = [];
    let invalidRows = [];

    // // Identify duplicate TaskNumbers 
    // if(this.isCurrentTaskSelected || this.isArchiveTaskSelected)
    // {
    //   // Clear previous highlights
    //   this.readinessData.ReadinessFormData.forEach((row, index) => {
    //     const rowElement = document.getElementById(`row-${index}`);
    //     if (rowElement) {
    //       rowElement.classList.remove('highlight-duplicate', 'highlight-invalid');
    //     }
    //   });

    //   this.readinessData.ReadinessFormData.forEach((row, index) => {
    //     if (!taskIdMap.has(row.TaskNumber)) {
    //       taskIdMap.set(row.TaskNumber, [index]);
    //     } else {
    //       taskIdMap.get(row.TaskNumber).push(index);
    //       duplicateRows.push(row.TaskNumber);
    //     }
    //   });
    // }
    // else
    // {
    let numberCounter = 0;

    // nonArchivedReadinessData.forEach(task => { task.Number = numberCounter++; });

    // archivedReadinessData.forEach(task => { task.Number = numberCounter++; });
    const nonArchivedTasks = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId === 0);
    nonArchivedTasks.forEach((row, index) => {
      // row.Number = numberCounter++;
      if (!currentTaskIds.has(row.TaskNumber)) {
        currentTaskIds.set(row.TaskNumber, [row.ActualRowNumber]);
      } else {
        currentTaskIds.get(row.TaskNumber).push(row.ActualRowNumber);
        duplicateRows.push(row.TaskNumber);
      }
    });

    if (taskIdMap.size === 0 && duplicateRows.length > 0) {
      taskIdMap = currentTaskIds;
    }

    const archieveTasks = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId !== 0);
    archieveTasks.forEach((row, index) => {
      // row.Number = numberCounter++;
      if (!archiveTaskIds.has(row.TaskNumber)) {
        archiveTaskIds.set(row.TaskNumber, [row.ActualRowNumber]);
      } else {
        archiveTaskIds.get(row.TaskNumber).push(row.ActualRowNumber);
        duplicateRows.push(row.TaskNumber);
      }
    });

    if (taskIdMap.size === 0 && duplicateRows.length > 0) {
      taskIdMap = archiveTaskIds;
    }
    // }

    if (duplicateRows.length > 0) {
      this.showValidationErrors = true;
      duplicateRows.forEach(duplicateTaskNumber => {
        const actualRowNumbers = taskIdMap.get(duplicateTaskNumber) || [];
        actualRowNumbers.forEach(actualRowNumber => {
          const duplicateRow = this.readinessData.ReadinessFormData
            .find(row => row.ActualRowNumber === actualRowNumber);
          if (duplicateRow) {
            this.duplicateTaskRows.add(
              this.getDuplicateRowKey(duplicateRow.TaskNumber, duplicateRow.ArchiveTaskId !== 0));
          }
        });
        if (!silent) {
          this.pcs.notificationService.danger(`Duplicate Task ID detected: ${duplicateTaskNumber}.`);
        }
      });

      if (silent) {
        this.pcs.notificationService.warning('Auto-save failed', 'Duplicate task numbers must be resolved before saving.');
      }

      // Scroll to the first duplicate row
      if (!silent) {
        const firstDuplicateActualRow = taskIdMap.get(duplicateRows[0])[0];
        const firstDuplicateIndex = this.readinessData.ReadinessFormData
          .findIndex(row => row.ActualRowNumber === firstDuplicateActualRow);
        if (firstDuplicateIndex >= 0) {
          const firstDuplicateElement = document.getElementById(`row-${firstDuplicateIndex}`);
          if (firstDuplicateElement) {
            firstDuplicateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }

      return false;
    }

    // Identify invalid rows
    const invalidRowsWithColumns = this.readinessData.ReadinessFormData.map((row, index) => {
      const invalidColumnNames = updatedColumnData
        .filter(column => {
          if (!column.IsRequired)
            return false;

          if (row.IsArchived === true && column.ColumnName === 'PriorityId')
            return false;

          return row[column.ColumnName] === undefined ||
            row[column.ColumnName] === null ||
            row[column.ColumnName] === '' ||
            row[column.ColumnName] === -1;
        })
        .map(column => column.DisplayName);

      if (invalidColumnNames.length > 0) {
        invalidRows.push(index);

        this.groupedColumns.forEach(section => {
          if (section.columns.some(col => invalidColumnNames.includes(col.DisplayName))) {
            this.sectionVisibility[section.sectionName] = true;
          }
        });
      }

      return {
        taskNumber: row.TaskNumber,
        index: index,
        invalidColumns: invalidColumnNames
      };
    }).filter(item => item.invalidColumns.length > 0);

    const invalidActualRowSet = new Set<number>();
    if (invalidRowsWithColumns.length > 0) {
      for (let ii = 0; ii < invalidRowsWithColumns.length; ii++) {
        const invItem = invalidRowsWithColumns[ii];
        const invRow = this.readinessData.ReadinessFormData[invItem.index];
        if (invRow) {
          invalidActualRowSet.add(invRow.ActualRowNumber);
        }
      }
    }

    if (invalidRowsWithColumns.length > 0 && !silent) {
      this.showValidationErrors = true;

      // Highlight all invalid rows first
      invalidRows.forEach(index => {
        const rowElement = document.getElementById(`row-${index}`);
        // if (rowElement) {
        //   rowElement.classList.add('highlight-invalid');
        // }
      });

      // Scroll to the first invalid row
      const firstInvalidElement = document.getElementById(`row-${invalidRows[0]}`);
      if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      const messages = invalidRowsWithColumns.map(item =>
        `Task ${item.taskNumber}: ${item.invalidColumns.join(', ')} is Required`
      );
      this.pcs.notificationService.dangerList(messages, false);

      return false;
    }

    if (invalidRowsWithColumns.length > 0 && silent) {
      this.showValidationErrors = true;
    }

    // if (this.isCurrentTaskSelected) {
    //   const changedFieldsDto = Object.entries(this.changedFields).map(([rowNumber, cols]) => {
    //     return {
    //       RowNumber: +rowNumber, // backend expects RowNumber
    //       Fields: Object.entries(cols).map(([colName, isChanged]) => ({
    //         ColumnName: colName,  // backend expects ColumnName
    //         IsChanged: isChanged  // backend expects IsChanged
    //       }))
    //     };
    //   });
    //   const saveReadinessCommandRequest = {
    //     Readiness: this.readinessData.ReadinessFormData,
    //     CreateNewTask: false,
    //     ChangedFields: changedFieldsDto,
    //     ProPakProjectId: this.projectId
    //   };

    //   let response = await this.saveReadinessTasks(saveReadinessCommandRequest);
    //   if (response) {
    //     if (response.Message && response.Id == -1) {
    //       const confirmation = window.confirm(response.Message + " Do you want to create a new one?");
    //       if (confirmation) {
    //         saveReadinessCommandRequest.CreateNewTask = true;
    //         let response = await this.saveReadinessTasks(saveReadinessCommandRequest);
    //         if (response) {
    //           this.getAllWorkPacksByProjectId();
    //           this.originalReadinessFormData = [];
    //           await this.prepareDropdownsForRows('SaveAllTasks');
    //           this.router.navigate([this.router.url]);
    //         }
    //       }
    //     }
    //     else {
    //       this.processSaveResponse(response)
    //       this.originalReadinessFormData = [];
    //       this.changedFields = {};
    //       await this.getAllWorkPacksByProjectId();
    //       await this.prepareDropdownsForRows('SaveAllTasks');
    //       // this.router.navigate([this.router.url]);
    //     }
    //   }
    // }
    // else if (this.isArchiveTaskSelected) {
    //   const changedFieldsDto = Object.entries(this.changedFields).map(([rowNumber, cols]) => {
    //     return {
    //       RowNumber: +rowNumber, // backend expects RowNumber
    //       Fields: Object.entries(cols).map(([colName, isChanged]) => ({
    //         ColumnName: colName,  // backend expects ColumnName
    //         IsChanged: isChanged  // backend expects IsChanged
    //       }))
    //     };
    //   });
    //   const saveArchiveCommandRequest = {
    //     ArchiveTasks: this.readinessData.ReadinessFormData,
    //     ChangedFields: changedFieldsDto,
    //     ProPakProjectId: this.projectId
    //   };
    //   let response = await this.saveArchiveTasks(saveArchiveCommandRequest);
    //   if (response) {
    //     await this.getArchiveTask();
    //   }
    // } else {
    let nonArchivedReadinessData = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId === 0 && !task.IsArchived);
    let archivedReadinessData = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId > 0 && task.IsArchived);
    if (silent && invalidActualRowSet.size > 0) {
      nonArchivedReadinessData = nonArchivedReadinessData.filter((task) => !invalidActualRowSet.has(task.ActualRowNumber));
      archivedReadinessData = archivedReadinessData.filter((task) => !invalidActualRowSet.has(task.ActualRowNumber));
    }
    if (nonArchivedReadinessData.length === 0 && archivedReadinessData.length === 0) {
      if (silent && invalidRowsWithColumns.length > 0) {
        this.pcs.notificationService.warning(
          'Auto-save',
          'No tasks could be saved until required fields are completed.'
        );
      }
      return false;
    }
    // let numberCounter = 1;

    // nonArchivedReadinessData.forEach(task => { task.Number = numberCounter++; });

    // archivedReadinessData.forEach(task => { task.Number = numberCounter++; });
    let projectTaskResponse;
    let archiveTaskResponse;

    if (nonArchivedReadinessData.length > 0) {
      let changedFieldsDto = Object.entries(this.changedFields).map(([rowNumber, cols]) => {
        const currentTask = nonArchivedReadinessData.find(x => x.ActualRowNumber == +rowNumber);
        if (currentTask) {
          return {
            RowNumber: currentTask.ActualRowNumber,
            Id: currentTask.ProjectTaskId,
            Fields: Object.entries(cols).map(([colName, isChanged]) => ({
              ColumnName: colName,  // backend expects ColumnName
              IsChanged: isChanged  // backend expects IsChanged
            }))
          };
        }
      });
      changedFieldsDto = changedFieldsDto.filter(Boolean);
      this.appendReorderedNumberChangesToDto(nonArchivedReadinessData, changedFieldsDto, 'ProjectTaskId');
      nonArchivedReadinessData.forEach(task => { task.ActualRowNumber = task.ActualRowNumber; });
      const saveReadinessCommandRequest = {
        Readiness: nonArchivedReadinessData,
        CreateNewTask: true,
        ChangedFields: changedFieldsDto,
        ProPakProjectId: this.projectId
      };
      projectTaskResponse = await this.saveReadinessTasks(saveReadinessCommandRequest, false, showLoader, silent);
    }

    if (archivedReadinessData.length > 0 && (projectTaskResponse == undefined || (projectTaskResponse && projectTaskResponse.Id != -1))) {
      let changedFieldsDto = Object.entries(this.changedFields).map(([rowNumber, cols]) => {
        const archivedRow = archivedReadinessData.find(x => x.ActualRowNumber == +rowNumber);
        if (archivedRow) {
          return {
            RowNumber: archivedRow.ActualRowNumber, // backend expects RowNumber
            Id: archivedRow.ArchiveTaskId,
            Fields: Object.entries(cols).map(([colName, isChanged]) => ({
              ColumnName: colName,  // backend expects ColumnName
              IsChanged: isChanged,  // backend expects IsChanged

            }))
          };
        }
      });
      changedFieldsDto = changedFieldsDto.filter(Boolean);
      this.appendReorderedNumberChangesToDto(archivedReadinessData, changedFieldsDto, 'ArchiveTaskId');

      archivedReadinessData.forEach(task => { task.ActualRowNumber = task.ActualRowNumber; });
      const saveArchiveCommandRequest = {
        ArchiveTasks: archivedReadinessData,
        ChangedFields: changedFieldsDto,
        ProPakProjectId: this.projectId
      };
      archiveTaskResponse = await this.saveArchiveTasks(saveArchiveCommandRequest, false, showLoader, silent);
    }

    const attemptedNon = nonArchivedReadinessData.length > 0;
    const attemptedArch = archivedReadinessData.length > 0;
    const okNon = !attemptedNon || !!(projectTaskResponse && projectTaskResponse.Id != -1);
    const okArch = !attemptedArch || !!archiveTaskResponse;
    if (okNon && okArch) {
      const partialSilent = silent && invalidActualRowSet.size > 0;
      if (partialSilent) {
        const savedSet = new Set<number>();
        for (let si = 0; si < nonArchivedReadinessData.length; si++) {
          savedSet.add(nonArchivedReadinessData[si].ActualRowNumber);
        }
        for (let sj = 0; sj < archivedReadinessData.length; sj++) {
          savedSet.add(archivedReadinessData[sj].ActualRowNumber);
        }
        this.applyPostSaveWithoutRefresh(savedSet, { silent: true, includeProPakArchiveFieldMap: false });
        const skipped = invalidRowsWithColumns.length;
        this.pcs.notificationService.info(
          'Auto-save',
          'Saved tasks with complete data. ' + skipped + ' task(s) skipped (missing required fields).'
        );
      } else {
        this.applyPostSaveWithoutRefresh('all', { silent: silent, includeProPakArchiveFieldMap: false });
      }
      if (!skipIdSync) {
        await this.syncProjectTaskIdsAfterSave();
      }
      return true;
    }
    // this.prepareDropdownsForRows();
    // }

    return false;
  }

  async saveProPakLoadedWP(silent: boolean = false, skipIdSync: boolean = false, showLoader: boolean = !silent): Promise<boolean> {
    let invalidRows = [];
    const updatedColumnData = this.readinessColumnData.map(column => {
      // Create a shallow copy of each column
      const copiedColumn = { ...column };

      // If column is PriorityId and not required, mark it as required
      if (copiedColumn.ColumnName === 'PriorityId' && !copiedColumn.IsRequired) {
        copiedColumn.IsRequired = true;
      }

      return copiedColumn;
    });
    let taskIdMap = new Map<string, number[]>();
    let duplicateRows = [];
    let nonArchivedReadinessData = [];
    let archivedReadinessData = [];
    let projectTaskResponse;
    let archiveTaskResponse;
    // if(this.isProPakWorkPackAvailable)
    // {
    // if(this.isCurrentTaskSelected)
    // {
    //   nonArchivedReadinessData = this.readinessData.ReadinessFormData;
    //   // Clear previous highlights
    //   this.readinessData.ReadinessFormData.forEach((row, index) => {
    //     const rowElement = document.getElementById(`row-${index}`);
    //     if (rowElement) {
    //       rowElement.classList.remove('highlight-duplicate', 'highlight-invalid');
    //     }
    //   });

    //   this.readinessData.ReadinessFormData.forEach((row, index) => {
    //     if (!taskIdMap.has(row.TaskNumber)) {
    //       taskIdMap.set(row.TaskNumber, [index]);
    //     } else {
    //       taskIdMap.get(row.TaskNumber).push(index);
    //       duplicateRows.push(row.TaskNumber);
    //     }
    //   });
    //   archivedReadinessData = this.originalArchiveReadinessFormData;
    // }
    // else if(this.isArchiveTaskSelected)
    // {
    //   archivedReadinessData = this.readinessData.ReadinessFormData; // check if user changes anything then it needs to be up to date.
    //   nonArchivedReadinessData = this.modifiedCurrentReadinessFormData != null ? this.modifiedCurrentReadinessFormData : this.originalCurrentReadinessFormData;
    // }
    // else
    // {
    nonArchivedReadinessData = this.readinessData.ReadinessFormData.filter((task) => task["IsArchived"] === false);
    archivedReadinessData = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId > 0 || task["IsArchived"] === true);
    // }
    // }
    // else
    // {
    //   nonArchivedReadinessData = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId === 0);
    //   archivedReadinessData = this.readinessData.ReadinessFormData.filter((task) => task.ArchiveTaskId > 0);
    // }
    if (duplicateRows.length > 0) {
      this.showValidationErrors = true;
      duplicateRows.forEach(duplicateTaskNumber => {
        const actualRowNumbers = taskIdMap.get(duplicateTaskNumber) || [];
        actualRowNumbers.forEach(actualRowNumber => {
          const duplicateRow = this.readinessData.ReadinessFormData
            .find(row => row.ActualRowNumber === actualRowNumber);
          if (duplicateRow) {
            this.duplicateTaskRows.add(
              this.getDuplicateRowKey(duplicateRow.TaskNumber, duplicateRow.ArchiveTaskId !== 0));
          }
        });
        this.pcs.notificationService.danger(`Duplicate Task ID detected: ${duplicateTaskNumber}.`);
      });

      // Scroll to the first duplicate row
      const firstDuplicateIndex = taskIdMap.get(duplicateRows[0])[0];
      if (firstDuplicateIndex !== undefined) {
        const firstDuplicateElement = document.getElementById(`row-${firstDuplicateIndex}`);
        if (firstDuplicateElement) {
          firstDuplicateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      return false;
    }

    // let isArchiveTasksIncluded = false;
    // let tasksData = this.readinessData.ReadinessFormData;
    // if(this.isArchiveTaskSelected)
    // {
    //   tasksData = this.modifiedCurrentReadinessFormData.length > 0 ? tasksData.concat(this.modifiedCurrentReadinessFormData) : tasksData.concat(this.originalCurrentReadinessFormData);
    //   // tasksData =  this.modifiedCurrentReadinessFormData != null ? this.modifiedCurrentReadinessFormData : this.originalCurrentReadinessFormData;
    //   isArchiveTasksIncluded = true;
    // }
    // else if(this.isCurrentTaskSelected)
    // {
    //   tasksData = this.modifiedArchiveReadinessFormData.length > 0 ? tasksData.concat(this.modifiedArchiveReadinessFormData) : tasksData.concat(this.originalArchiveReadinessFormData);
    //   // tasksData = this.modifiedCurrentReadinessFormData.length > 0 ? tasksData.concat(this.modifiedCurrentReadinessFormData) : tasksData.concat(this.originalCurrentReadinessFormData);
    // }

    // Identify invalid rows
    const invalidRowsWithColumns = this.readinessData.ReadinessFormData.map((row, index) => {
      const invalidColumnNames = updatedColumnData
        .filter(column => {
          if (!column.IsRequired)
            return false;

          if (row.IsArchived === true && column.ColumnName === 'PriorityId')
            return false;

          return row[column.ColumnName] === undefined ||
            row[column.ColumnName] === null ||
            row[column.ColumnName] === '' ||
            row[column.ColumnName] === -1;
        })
        .map(column => column.DisplayName);

      if (invalidColumnNames.length > 0) {
        invalidRows.push(index);

        this.groupedColumns.forEach(section => {
          if (section.columns.some(col => invalidColumnNames.includes(col.DisplayName))) {
            this.sectionVisibility[section.sectionName] = true;
          }
        });
      }

      return {
        taskNumber: row.TaskNumber,
        index: index,
        invalidColumns: invalidColumnNames
      };
    }).filter(item => item.invalidColumns.length > 0);

    const proPakInvalidActualRowSet = new Set<number>();
    if (invalidRowsWithColumns.length > 0) {
      for (let pi = 0; pi < invalidRowsWithColumns.length; pi++) {
        const pInv = invalidRowsWithColumns[pi];
        const pRow = this.readinessData.ReadinessFormData[pInv.index];
        if (pRow) {
          proPakInvalidActualRowSet.add(pRow.ActualRowNumber);
        }
      }
    }

    if (invalidRowsWithColumns.length > 0 && !silent) {
      this.showValidationErrors = true;

      invalidRows.forEach(index => {
        const rowElement = document.getElementById(`row-${index}`);
      });

      const firstInvalidElement = document.getElementById(`row-${invalidRows[0]}`);
      if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      const messages = invalidRowsWithColumns.map(item =>
        `Task ${item.taskNumber}: ${item.invalidColumns.join(', ')} is Required`
      );

      this.pcs.notificationService.dangerList(
        messages,
        false
      );

      return false;
    }

    if (invalidRowsWithColumns.length > 0 && silent) {
      this.showValidationErrors = true;
      nonArchivedReadinessData = nonArchivedReadinessData.filter((task) => !proPakInvalidActualRowSet.has(task.ActualRowNumber));
      archivedReadinessData = archivedReadinessData.filter((task) => !proPakInvalidActualRowSet.has(task.ActualRowNumber));
    }

    if (nonArchivedReadinessData.length === 0 && archivedReadinessData.length === 0) {
      if (silent && invalidRowsWithColumns.length > 0) {
        this.pcs.notificationService.warning(
          'Auto-save',
          'No tasks could be saved until required fields are completed.'
        );
      }
      return false;
    }

    if (nonArchivedReadinessData.length > 0) {
      let changedObject = this.changedFields;
      // if(this.isArchiveTaskSelected)
      // {
      //   changedObject = this.currentTaskChangedFields;
      // }
      let changedFieldsDto = Object.entries(changedObject).map(([rowNumber, cols]) => {
        const currentTask = nonArchivedReadinessData.find(x => x.ActualRowNumber == +rowNumber);
        if (currentTask) {
          return {
            RowNumber: currentTask.ActualRowNumber,
            Id: 0,
            Fields: Object.entries(cols).map(([colName, isChanged]) => ({
              ColumnName: colName,  // backend expects ColumnName
              IsChanged: isChanged  // backend expects IsChanged
            }))
          };
        }
      });
      changedFieldsDto = changedFieldsDto.filter(Boolean);
      nonArchivedReadinessData.forEach(task => { task.ActualRowNumber = task.ActualRowNumber; });
      const saveReadinessCommandRequest = {
        Readiness: nonArchivedReadinessData,
        CreateNewTask: false,
        ChangedFields: changedFieldsDto,
        ProPakProjectId: this.projectId
      };
      projectTaskResponse = await this.saveReadinessTasks(saveReadinessCommandRequest, false, showLoader, silent);
      if (projectTaskResponse) {
        if (projectTaskResponse.Message && projectTaskResponse.Id == -1) {
          this.notifySaveFailed(projectTaskResponse.Message, silent);
          if (silent || /already exists/i.test(projectTaskResponse.Message)) {
            return false;
          }
          const confirmation = await this.pcs.confirm(
            projectTaskResponse.Message + ' Do you want to create a new one?',
            'Create new task'
          );
          if (confirmation) {
            saveReadinessCommandRequest.CreateNewTask = true;
            let response = await this.saveReadinessTasks(saveReadinessCommandRequest, true, showLoader, silent);
            projectTaskResponse = response;
          } else {
            return false;
          }
        }
      }
    }

    if (archivedReadinessData.length > 0 && (projectTaskResponse == undefined || (projectTaskResponse && projectTaskResponse.Id != -1))) {
      let changedObject = this.archiveTaskChangedFields;
      // if(this.isCurrentTaskSelected)
      // {
      //   changedObject = this.changedFields; 
      // }
      let changedFieldsDto = Object.entries(changedObject).map(([rowNumber, cols]) => {
        const archivedRow = archivedReadinessData.find(x => x.ActualRowNumber == +rowNumber);
        if (archivedRow) {
          return {
            RowNumber: archivedRow.ActualRowNumber, // backend expects RowNumber
            Id: 0,
            Fields: Object.entries(cols).map(([colName, isChanged]) => ({
              ColumnName: colName,  // backend expects ColumnName
              IsChanged: isChanged  // backend expects IsChanged
            }))
          };
        }
      });
      changedFieldsDto = changedFieldsDto.filter(Boolean);

      archivedReadinessData.forEach(task => { task.ActualRowNumber = task.ActualRowNumber; });
      const saveArchiveCommandRequest = {
        ArchiveTasks: archivedReadinessData,
        ChangedFields: changedFieldsDto,
        ProPakProjectId: this.projectId
      };
      archiveTaskResponse = await this.saveArchiveTasks(saveArchiveCommandRequest, false, showLoader, silent);
    }

    const proPakAttemptedNon = nonArchivedReadinessData.length > 0;
    const proPakAttemptedArch = archivedReadinessData.length > 0;
    const proPakOkNon = !proPakAttemptedNon || !!(projectTaskResponse && projectTaskResponse.Id != -1);
    const proPakOkArch = !proPakAttemptedArch || !!archiveTaskResponse;
    if (proPakOkNon && proPakOkArch) {
      const proPakPartialSilent = silent && proPakInvalidActualRowSet.size > 0;
      if (proPakPartialSilent) {
        const proPakSavedSet = new Set<number>();
        for (let pk = 0; pk < nonArchivedReadinessData.length; pk++) {
          proPakSavedSet.add(nonArchivedReadinessData[pk].ActualRowNumber);
        }
        for (let pl = 0; pl < archivedReadinessData.length; pl++) {
          proPakSavedSet.add(archivedReadinessData[pl].ActualRowNumber);
        }
        this.applyPostSaveWithoutRefresh(proPakSavedSet, { silent: true, includeProPakArchiveFieldMap: true });
        this.pcs.notificationService.info(
          'Auto-save',
          'Saved tasks with complete data. ' + invalidRowsWithColumns.length + ' task(s) skipped (missing required fields).'
        );
      } else {
        this.applyPostSaveWithoutRefresh('all', { silent: silent, includeProPakArchiveFieldMap: true });
      }
      if (!skipIdSync) {
        await this.syncProjectTaskIdsAfterSave();
      }
      return true;
    }
    return false;
  }
  toggleExportDropdown(): void {
    this.showProPakDropdown = false;
    this.showExportDropdown = !this.showExportDropdown;
  }

  toggleProPakDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showExportDropdown = false;
    this.showProPakDropdown = !this.showProPakDropdown;
  }

  launchProPakFromDropdown(): void {
    this.showProPakDropdown = false;
    this.openWorkPack();
  }

  launchAiAssistedProPak(): void {
    this.showProPakDropdown = false;
    window.open('https://propak.solutions/app/work-packs', '_blank');
  }

  async exportReadinessReport(format: 'excel' | 'pdf'): Promise<void> {
    this.showExportDropdown = false;
    const commandName = format === 'pdf' ? 'CreateReadinessPdfReport' : 'CreateReadinessExcelReport';
    const mimeType = format === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const useBusyIndicator = false;
    const taskExportType = this.resolveExportTaskType();
    const exportIncludesArchived = taskExportType === 'archived' || taskExportType === 'both';
    const exportRows = this.getExportOrderedRows(taskExportType);
    const payload: any = {
      ProjectId: this.projectId,
      TaskExportType: taskExportType,
      SectionVisibility: { ...this.sectionVisibility },
      IncludeTaskImages: this.includeTaskImages,
      IncludeArchivedTaskImages: this.includeTaskImages && exportIncludesArchived,
      ApplyClientTaskFilter: true,
      OrderedExportTasks: exportRows.map((row: any) => ({
        ProjectTaskId: row.ProjectTaskId > 0 ? row.ProjectTaskId : 0,
        ArchiveTaskId: row.ArchiveTaskId > 0 ? row.ArchiveTaskId : 0,
        TaskNumber: row.TaskNumber || '',
        IsArchived: this.isArchivedTaskRow(row)
      }))
    };

    this.exportInProgress = format;
    const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';
    this.pcs.notificationService.info(
      `Exporting ${formatLabel}`,
      `Your ${formatLabel} is being generated. You can continue working.`
    );

    try {
      const response: any = await this.pcs.apiService.executeCommandService.executeCommand(
        commandName,
        payload,
        useBusyIndicator,
        false
      );

      const result = (response && response.Payload) ? response.Payload : response;
      if (!result) {
        this.pcs.notificationService.danger('Export Failed', 'No report data was returned from the server.');
        return;
      }

      const reportDataBase64 = result.ReportData || result.reportData;
      if (!reportDataBase64) {
        this.pcs.notificationService.danger('Export Failed', 'No report data was returned from the server.');
        return;
      }

      const byteCharacters = atob(reportDataBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const rawFileName = (result.FileName || result.fileName || `readiness_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`)
        .replace(/,/g, '')
        .replace(/[<>:"/\\|?*]/g, '_');
      this.downloadFile(blob, rawFileName);

      this.pcs.notificationService.success('Export complete', `${formatLabel} downloaded successfully.`);
    } catch {
      this.pcs.notificationService.danger(
        'Export Failed',
        `${format === 'pdf' ? 'PDF' : 'Excel'} export failed. Please try again.`
      );
    } finally {
      this.exportInProgress = null;
    }
  }

  /** Resolves export task scope from the active tab + optional "include other" toggle. */
  private resolveExportTaskType(): 'current' | 'archived' | 'both' {
    if (this.selectedTaskType === 'both') {
      return 'both';
    }
    if (this.exportIncludeOtherTaskType) {
      return 'both';
    }
    if (this.selectedTaskType === 'archived') {
      return 'archived';
    }
    return 'current';
  }

  private getExportOrderedRows(taskExportType: 'current' | 'archived' | 'both'): any[] {
    const visibleRows = this.getExportOrderedVisibleRows();
    if (taskExportType !== 'both' || this.selectedTaskType === 'both') {
      return visibleRows;
    }

    if (!this.readinessData || !this.readinessData.ReadinessFormData) {
      return visibleRows;
    }

    const otherRows = this.readinessData.ReadinessFormData.filter((row: any) => {
      if (this.selectedTaskType === 'current') {
        return this.isArchivedTaskRow(row);
      }
      if (this.selectedTaskType === 'archived') {
        return !this.isArchivedTaskRow(row);
      }
      return false;
    });

    const seen = new Set<any>();
    const combined: any[] = [];
    visibleRows.concat(otherRows).forEach((row: any) => {
      if (row && !seen.has(row)) {
        seen.add(row);
        combined.push(row);
      }
    });
    return combined;
  }

  private getExportOrderedVisibleRows(): any[] {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) {
      return [];
    }

    if (this.isGroupingEnabled && this.groupedRows && this.groupedRows.length > 0) {
      const ordered: any[] = [];
      this.groupedRows.forEach((group: any) => {
        if (!group || !group.rows) {
          return;
        }
        group.rows.forEach((row: any) => {
          if (row && row['EnableDisplay']) {
            ordered.push(row);
          }
        });
      });
      return ordered;
    }

    return this.readinessData.ReadinessFormData.filter((row: any) => !!row['EnableDisplay']);
  }

  async ExportReadinessReport(_projectName?: string) {
    await this.exportReadinessReport('excel');
  }

  private downloadFile(blob: Blob, fileName: string): void {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  validateTasks(tasksToValidate: any[]) {
    let invalidRows = [];
    const updatedColumnData = this.readinessColumnData.map(column => {
      // Create a shallow copy of each column
      const copiedColumn = { ...column };

      // If column is PriorityId and not required, mark it as required
      if (copiedColumn.ColumnName === 'PriorityId' && !copiedColumn.IsRequired) {
        copiedColumn.IsRequired = true;
      }

      return copiedColumn;
    });

    // Identify invalid rows
    const invalidRowsWithColumns = this.readinessData.ReadinessFormData.map((row, index) => {
      const invalidColumnNames = updatedColumnData
        .filter(column => column.IsRequired && (
          row[column.ColumnName] === undefined ||
          row[column.ColumnName] === null ||
          row[column.ColumnName] === '' ||
          row[column.ColumnName] === -1
        ))
        .map(column => column.DisplayName);

      if (invalidColumnNames.length > 0) {
        invalidRows.push(index);

        this.groupedColumns.forEach(section => {
          if (section.columns.some(col => invalidColumnNames.includes(col.DisplayName))) {
            this.sectionVisibility[section.sectionName] = true;
          }
        });
      }

      return {
        taskNumber: row.TaskNumber,
        index: index,
        invalidColumns: invalidColumnNames
      };
    }).filter(item => item.invalidColumns.length > 0);

    if (invalidRowsWithColumns.length > 0) {
      this.showValidationErrors = true;

      // Highlight all invalid rows first
      invalidRows.forEach(index => {
        const rowElement = document.getElementById(`row-${index}`);
        if (rowElement) {
          rowElement.classList.add('highlight-invalid');
        }
      });

      // Scroll to the first invalid row
      const firstInvalidElement = document.getElementById(`row-${invalidRows[0]}`);
      if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Display notifications for invalid rows
      invalidRowsWithColumns.forEach(item => {
        this.pcs.notificationService.danger(`Following fields in Task ${item.taskNumber} are required: ${item.invalidColumns.join(', ')}`);
      });

      return false;
    }
    return true;
  }
  async archiveTask() {
    const taskToArchive = this.readinessData.ReadinessFormData.filter(x => x["IsSelected"]);
    if (!this.validateTasks(taskToArchive)) {
      return;
    }

    const text = await this.getConfirmationText('archive');
    const confirmation = await this.pcs.confirm(text, 'Archive tasks');

    if (confirmation) {
      const selectedTaskNumbers = new Set<string>(
        this.readinessData.ReadinessFormData
          .filter(x => x["IsSelected"])
          .map(x => x.TaskNumber)
      );
      const pendingCurrentChanges = this.capturePendingCurrentTaskChanges(selectedTaskNumbers);
      if (this.isProPakWorkPackAvailable) {
        let unsavedTasks = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData.filter(x => x["IsSelected"])));
        unsavedTasks.forEach(x => {
          x["IsSelected"] = false;
          x["IsArchived"] = true;
        });
        this.originalArchiveReadinessFormData.push(...unsavedTasks);
        this.modifiedArchiveReadinessFormData.push(...unsavedTasks);
        this.originalCurrentReadinessFormData = this.originalCurrentReadinessFormData.filter(row => !unsavedTasks.some(unsaved => unsaved.TaskNumber === row.TaskNumber));
        this.modifiedCurrentReadinessFormData = this.modifiedCurrentReadinessFormData.filter(row => !unsavedTasks.some(unsaved => unsaved.TaskNumber === row.TaskNumber));

        if (unsavedTasks.length > 0) {
          this.readinessData.ReadinessFormData = this.readinessData.ReadinessFormData.filter(x => !x["IsSelected"]);
        }
        // if(!this.isArchiveTaskSelected && !this.isCurrentTaskSelected)
        // {
        //   this.loadCombinedData();
        // }
        return;
      }
      const selectedRowNumbers = new Set<number>(
        this.readinessData.ReadinessFormData
          .filter(x => x["IsSelected"])
          .map(x => x.ActualRowNumber)
      );
      const changedFieldsDto = Object.entries(this.changedFields)
        .filter(([rowNumber]) => selectedRowNumbers.has(+rowNumber))
        .map(([rowNumber, cols]) => {
          return {
            RowNumber: +rowNumber,
            Id: 0,
            Fields: Object.entries(cols)
              .filter(([, isChanged]) => !!isChanged)
              .map(([colName, isChanged]) => ({
                ColumnName: colName,  // backend expects ColumnName
                IsChanged: isChanged  // backend expects IsChanged
              }))
          };
        })
        .filter(x => x.Fields.length > 0);
      const saveArchiveCommandRequest = {
        ArchiveTasks: this.readinessData.ReadinessFormData.filter(x => x["IsSelected"]),
        ChangedFields: changedFieldsDto,
        ProPakProjectId: this.projectId
      };
      let response = await this.saveArchiveTasks(saveArchiveCommandRequest, true, true);
      if (response) {
        await this.resetAllData();
        // if (this.selectedTaskType == 'both') {
        //   this.originalReadinessFormData = [];
        //   await this.loadCombinedData(true);
        //   await this.prepareDropdownsForRows('ArchiveTasks');
        // } else {
        await this.getAllWorkPacksByProjectId();
        this.restorePendingCurrentTaskChanges(pendingCurrentChanges);
        await this.prepareDropdownsForRows('archiveTask');
        // }
        // this.isArchiveTaskSelected = false;
        // this.isCurrentTaskSelected = false;
        this.isRowSelected = false;
        this.showArchiveButton = false;
        this.showUnarchiveButton = false;
      }
    }
  }

  isAnyRowSelected(): boolean {
    return this.readinessData.ReadinessFormData.some((row) => row["IsSelected"]);
  }

  resetAllData() {
    // this.modifiedArchiveReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    // this.originalArchiveReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData));
    // this.archiveTaskChangedFields = JSON.parse(JSON.stringify(this.changedFields));
    // this.modifiedCurrentReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    // this.originalCurrentReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData));
    // this.currentTaskChangedFields = JSON.parse(JSON.stringify(this.changedFields));
    // this.modifiedCombinedReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    // this.originalCombinedReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData));
    // this.combinedTaskChangedFields = JSON.parse(JSON.stringify(this.changedFields));
    this.changedFields = {};
    this.autoSavedHighlightFields = {};
    this.syncRemoteChangeBroadcast();
    this.tasksWithUnsavedImages.clear(); // Clear tracked unsaved images after saving
    this.originalTaskNumbers = [];
    this.modifiedArchiveReadinessFormData = [];
    this.originalArchiveReadinessFormData = [];
    this.archiveTaskChangedFields = [];
    this.modifiedCurrentReadinessFormData = [];
    this.originalCurrentReadinessFormData = [];
    this.currentTaskChangedFields = [];
    this.modifiedCombinedReadinessFormData = [];
    this.originalCombinedReadinessFormData = [];
    this.combinedTaskChangedFields = [];
    this.undoStack = [];
    this.redoStack = [];
    this.rowDragged = false;
    this.isDisplayProPakWPToggle = false;
    this.isProPakWPEnable = false;
    this.isNewRowAdded = false;
    this.isRowSorted = false;
  }

  async deleteSelectedRows() {
    const text = await this.getConfirmationText('delete');
    const confirmation = await this.pcs.confirm(text, 'Delete tasks', { icon: 'warning' });

    if (confirmation) {
      // this.resetAllData();
      const selectedRows = this.readinessData.ReadinessFormData.filter(row => row["IsSelected"]);

      // Separate rows with and without projectTaskId
      const rowsWithId = selectedRows.filter(row => row.ProjectTaskId);
      const rowsWithoutId = selectedRows.filter(row => !row.ProjectTaskId);

      // Extract valid task IDs for deletion
      const selectedTaskIds = rowsWithId.map(row => row.ProjectTaskId);

      if (selectedTaskIds.length > 0) {
        const request: DeleteReadinessTaskCommandRequest = { ReadinessTasksIds: selectedTaskIds };

        this.deleteTask(request).then(() => {
          this.readinessData.ReadinessFormData = this.readinessData.ReadinessFormData.filter(row => !row["IsSelected"]);
          this.ShowOrHideRemoveRowButton();
        });
      }

      if (rowsWithoutId.length > 0) {
        var archiveTaskIds = this.readinessData.ReadinessFormData.filter(x => x["IsSelected"] && (x.ArchiveTaskId > 0 || x["IsArchived"])).map(x => x.ArchiveTaskId);
        if (archiveTaskIds.length > 0) {
          // var archiveTaskIds = this.readinessData.ReadinessFormData.filter(x => x["IsSelected"]).map(x => x.ArchiveTaskId);
          var result = await this.pcs.apiService.DeleteArchiveProjectTasks({ taskIds: archiveTaskIds });
        }
        // var result = this.pcs.apiService.DeleteArchiveProjectTasks({ taskIds: archiveTaskIds });
        this.readinessData.ReadinessFormData = this.readinessData.ReadinessFormData.filter(row => !row["IsSelected"]);
      }

      if (selectedTaskIds.length === 0 && rowsWithoutId.length === 0) {
        await this.pcs.alert('No valid tasks selected for deletion.', 'Delete tasks', 'warning');
      }
      if (this.isAnyRowSelected()) {
        this.isRowSelected = true;
      }
      else {
        this.isRowSelected = false;
      }
    }
  }

  async deleteRows() {
    const selectedRows = this.readinessData.ReadinessFormData.filter(row => row["IsSelected"]);

    if (selectedRows.length > 0) {
      const archieveTasks = selectedRows.filter(row => row["IsArchived"] == true || row.ArchiveTaskId);
      if (archieveTasks.length > 0) {
        await this.deleteArchiveTasks(false);
      }
      const currentTasks = selectedRows.filter(row => !row["IsArchived"] && row.ArchiveTaskId == 0);
      if (currentTasks.length > 0) {
        await this.deleteSelectedRows();
      }
    }
  }

  toggleSection(section: string): void {
    this.sectionVisibility[section] = !this.sectionVisibility[section];
    this.syncPageHorizontalScroll();
    this.cdr.detectChanges();
    // this.initializeResizableColumns();
  }

  async saveReadinessTasks(
    request: SaveReadinessCommandRequest,
    showAlert: boolean = true,
    showLoader: boolean = true,
    silent: boolean = false
  ) {
    try {
      const response: SaveReadinessCommandResponse = await this.pcs.apiService.SaveReadiness(request, showLoader, false);
      return response;
    } catch (error) {
      const message = this.extractValidationMessage(error);
      if (message) {
        this.notifySaveFailed(message, silent);
      }
      return null;
    }
  }

  async saveArchiveTasks(request: SaveArchiveTaskCommandRequest, showAlert: boolean = true, showLoader: boolean = true, silent: boolean = false) {
    try {
      const response: SaveArchiveTaskCommandResponse = await this.pcs.apiService.SaveArchiveTask(request, showLoader, false);
      if (!response) {
        return false;
      }
      return showAlert ? this.processSaveResponse(response) : (response.Id > 0);
    } catch (error) {
      const message = this.extractValidationMessage(error);
      if (message) {
        this.notifySaveFailed(message, silent);
      }
      return false;
    }
  }

  async deleteTask(request: DeleteReadinessTaskCommandRequest) {
    try {
      const response: SaveArchiveTaskCommandResponse = await this.pcs.apiService.DeleteReadinessTask(request, true, false);
      return this.processSaveResponse(response);
    } catch (error) {
      const message = this.extractValidationMessage(error);
      if (message) {
        this.notifySaveFailed(message, false);
      }
      return false;
    }
  }

  /** Same toast style as auto-save: orange warning with title + server message. */
  private notifySaveFailed(message: string, silent: boolean = false, title?: string): void {
    this.pcs.notificationService.removePersistents();
    this.pcs.notificationService.warning(
      title || (silent ? 'Auto-save failed' : 'Save failed'),
      message
    );
  }

  private extractValidationMessage(error: any): string {
    if (!error) {
      return '';
    }
    if (Array.isArray(error.message)) {
      const parts = error.message.filter((m: any) => !!m && String(m).trim() !== '');
      return parts.length ? parts.map((m: any) => String(m)).join(' ') : '';
    }
    if (error.ValidationResults && Array.isArray(error.ValidationResults) && error.ValidationResults.length > 0) {
      return error.ValidationResults
        .map((v: any) => v && (v.ErrorMessage || v.errorMessage))
        .filter((m: any) => !!m && String(m).trim() !== '')
        .join(' ');
    }
    if (typeof error.message === 'string' && error.message.trim() !== '' && error.message.indexOf('response.json') < 0) {
      return error.message;
    }
    if (error.Message && String(error.Message).trim() !== '') {
      return String(error.Message);
    }
    return '';
  }

  processSaveResponse(response: any) {
    if (response && response.Id > 0) {
      this.changedFields = {};
      // NotifyUnlocked already drops our cells on the hub, so just forget what we sent;
      // re-announcing here would re-colour cells the save has just persisted.
      this.clearChangeBroadcastTimer();
      this.broadcastedChangedCells = new Set<string>();
      this.signalRService.notifyUnlocked(this.projectId, this.userName);
      this.lockedBy = null;
      void this.pcs.alert('Your changes have been saved successfully!', 'Saved', 'success');
      return true;
    }
    else {
      return false;
    }
  }

  successRedirect() {
    this.pcs.notificationService.success('', '', {
      timeOut: 3000, onHidden: () => {
        this.pcs.router.navigate(['/projects']);
      }
    });
  }


  draggedRow: { formIndex: number; rowIndex: number } | null = null;
  // When multiple rows are selected and dragged, store their indices here
  draggedRows: { formIndex: number; rowIndex: number }[] | null = null;

  onDragStart(event: DragEvent, formIndex: number, rowIndex: number) {
    this.originalTaskNumbers = this.isRowSorted
      ? this.readinessData.ReadinessFormData.map(item => item.Number)
      : this.readinessData.ReadinessFormData.map(item => item.ActualRowNumber);
    // If the row being dragged is selected and there are multiple selected rows,
    // treat this as a multi-row drag operation
    const selectedIndices: number[] = this.readinessData.ReadinessFormData
      .map((r, idx) => r["IsSelected"] ? idx : -1)
      .filter(idx => idx >= 0);

    if (selectedIndices.length > 1 && this.readinessData.ReadinessFormData[rowIndex]["IsSelected"]) {
      this.draggedRows = selectedIndices.map(idx => ({ formIndex, rowIndex: idx }));
      this.draggedRow = null;
      // add dragging class to all selected row elements
      this.draggedRows.forEach(d => {
        const el = document.getElementById('row-' + d.rowIndex);
        if (el) el.classList.add('dragging');
      });
      if (event.dataTransfer) {
        event.dataTransfer.setData('text/plain', JSON.stringify(this.draggedRows));
      }
    } else {
      this.draggedRow = { formIndex, rowIndex };
      this.draggedRows = null;
      if (event.dataTransfer) {
        event.dataTransfer.setData('text/plain', JSON.stringify(this.draggedRow));
        (event.target as HTMLElement).classList.add('dragging');
      }
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent, targetFormIndex: number, targetRowIndex: number) {
    event.preventDefault();
    // remove dragging class from any elements
    const draggingEls = Array.from(document.querySelectorAll('.dragging'));
    draggingEls.forEach(el => el.classList.remove('dragging'));

    // Try to parse transferred data (could be single or multiple rows)
    let payload: any = null;
    try {
      const data = event.dataTransfer ? event.dataTransfer.getData('text/plain') : null;
      if (data) payload = JSON.parse(data);
    } catch (e) {
      // ignore parse errors
    }

    // Determine indices to move
    let indicesToMove: number[] = [];

    if (this.draggedRows && this.draggedRows.length > 0) {
      indicesToMove = this.draggedRows.map(d => d.rowIndex);
    } else if (Array.isArray(payload)) {
      indicesToMove = payload.map((p: any) => p.rowIndex);
    } else if (this.draggedRow) {
      indicesToMove = [this.draggedRow.rowIndex];
    } else if (payload && payload.rowIndex !== undefined) {
      indicesToMove = [payload.rowIndex];
    }

    if (indicesToMove.length === 0) {
      // nothing to do
      this.draggedRow = null;
      this.draggedRows = null;
      return;
    }

    // Only handle moves within the same form
    const anyFormIndex = (this.draggedRows && this.draggedRows.length > 0) ? this.draggedRows[0].formIndex : (this.draggedRow ? this.draggedRow.formIndex : (payload && payload.formIndex !== undefined ? payload.formIndex : null));
    if (anyFormIndex !== targetFormIndex) {
      this.draggedRow = null;
      this.draggedRows = null;
      return;
    }

    // Normalize and sort indices ascending
    indicesToMove = Array.from(new Set(indicesToMove)).sort((a, b) => a - b);

    // Collect row objects to move in original order
    const rowsToMove = indicesToMove.map(idx => this.readinessData.ReadinessFormData[idx]);

    // Remove rows starting from highest index to avoid shifting
    for (let i = indicesToMove.length - 1; i >= 0; i--) {
      this.readinessData.ReadinessFormData.splice(indicesToMove[i], 1);
    }

    // Adjust target index if any removed indices were before it
    const removedBefore = indicesToMove.filter(idx => idx < targetRowIndex).length;
    let insertIndex = targetRowIndex - removedBefore;
    if (insertIndex < 0) insertIndex = 0;

    // Insert the rows at the target position preserving their order
    for (let i = 0; i < rowsToMove.length; i++) {
      this.readinessData.ReadinessFormData.splice(insertIndex + i, 0, rowsToMove[i]);
    }

    // Recompute numbers and rowDragged flag
    const originalTaskNumbers = this.originalTaskNumbers;
    let isRowDragged = false;
    this.readinessData.ReadinessFormData.forEach((item, index) => {
      if (item.Number != originalTaskNumbers[index]) {
        isRowDragged = true;
      }
      item.Number = originalTaskNumbers[index];
    });
    this.rowDragged = isRowDragged;
    if (isRowDragged) {
      this.trackNumberChangesAfterReorder();
    }
    this.draggedRow = null;
    this.draggedRows = null;
    this.originalTaskNumbers = [];
  }

  /** After drag-and-drop reorder, mark Number as changed so save persists row position. */
  private trackNumberChangesAfterReorder(): void {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) {
      return;
    }
    this.readinessData.ReadinessFormData.forEach((item) => {
      const rowNum = item.ActualRowNumber;
      const original = this.originalReadinessFormData
        ? this.originalReadinessFormData.find((x) => x.ActualRowNumber === rowNum)
        : null;
      const numberChanged = !original || original.Number !== item.Number;
      if (numberChanged) {
        if (!this.changedFields[rowNum]) {
          this.changedFields[rowNum] = {};
        }
        this.changedFields[rowNum]['Number'] = true;
      } else if (this.changedFields[rowNum]) {
        delete this.changedFields[rowNum]['Number'];
        if (Object.keys(this.changedFields[rowNum]).length === 0) {
          delete this.changedFields[rowNum];
        }
      }
    });
  }

  /** Ensures reordered Number values are included in save payload even if change tracking was missed. */
  private appendReorderedNumberChangesToDto(
    tasks: any[],
    changedFieldsDto: Array<{ RowNumber: number; Id: number; Fields: Array<{ ColumnName: string; IsChanged: boolean }> }>,
    idField: 'ProjectTaskId' | 'ArchiveTaskId'
  ): void {
    if (!this.rowDragged) {
      return;
    }
    tasks.forEach((task) => {
      const original = this.originalReadinessFormData
        ? this.originalReadinessFormData.find((x) => x.ActualRowNumber === task.ActualRowNumber)
        : null;
      if (original && original.Number === task.Number) {
        return;
      }
      let entry = changedFieldsDto.find((x) => x.RowNumber === task.ActualRowNumber);
      if (!entry) {
        entry = {
          RowNumber: task.ActualRowNumber,
          Id: task[idField] || 0,
          Fields: []
        };
        changedFieldsDto.push(entry);
      }
      if (!entry.Fields.some((f) => f.ColumnName === 'Number')) {
        entry.Fields.push({ ColumnName: 'Number', IsChanged: true });
      }
    });
  }

  private isArchivedTaskRow(task: any): boolean {
    return !!(task && (task.IsArchived === true || (task.ArchiveTaskId != null && task.ArchiveTaskId > 0)));
  }

  globalSortColumn: string = '';
  globalSortOrder: string = 'asc';

  sortAllRows(columnName: string) {
    if (this.globalSortColumn === columnName) {
      this.globalSortOrder = this.globalSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.globalSortColumn = columnName;
      this.globalSortOrder = 'asc';
    }

    let dropdownOptions: any[] = [];
    const sortOrderMap: { [key: string]: number } = {};
    if (columnName === "PriorityId") {
      dropdownOptions = this.getDropdownOptions(columnName);

      dropdownOptions.forEach((opt: any) => {
        if (opt.Id !== undefined && opt.Order !== undefined) {
          sortOrderMap[String(opt.Id).toLowerCase()] = opt.Order;
        }
      });
    }

    this.readinessData.ReadinessFormData.sort((a: any, b: any) => {
      const valueA = a[columnName];
      const valueB = b[columnName];

      if (dropdownOptions.length > 0) {
        const keyA = valueA ? String(valueA).toLowerCase() : '';
        const keyB = valueB ? String(valueB).toLowerCase() : '';

        const orderA =
          sortOrderMap.hasOwnProperty(keyA) ? sortOrderMap[keyA] : Number.MAX_SAFE_INTEGER;
        const orderB =
          sortOrderMap.hasOwnProperty(keyB) ? sortOrderMap[keyB] : Number.MAX_SAFE_INTEGER;

        return this.globalSortOrder === 'asc'
          ? orderA - orderB
          : orderB - orderA;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.globalSortOrder === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else {
        return this.globalSortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      }
    });

    if (this.isGroupingEnabled) {
      this.groupRowsByColumns(this.groupedByColumns);
    }
    // After sorting, renumber tasks so saved order matches the sorted order
    try {
      this.readinessData.ReadinessFormData.forEach((row: any, idx: number) => {
        const newNumber = idx + 1;
        row.Number = newNumber;
        // row.ActualRowNumber = newNumber;
        // row.TaskNumber = String(newNumber);
      });
      this.isRowSorted = true;
      // this.originalReadinessFormData.forEach((row: any, idx: number) => {
      //   const newNumber = idx + 1;
      //   row.Number = newNumber;
      // });
    } catch (e) {
      console.error('Error renumbering tasks after sort:', e);
    }
  }

  groupByColumn(columnName: string): void {
    if (!this.groupedByColumns) {
      this.groupedByColumns = [];
    }
    if (!this.groupedByColumns.includes(columnName)) {
      this.groupedByColumnValues.push(this.readinessColumnData.find(x => x.ColumnName === columnName).DisplayName);
      this.groupedByColumns.push(columnName);
    }
    this.groupRowsByColumns(this.groupedByColumns);
    this.isGroupingEnabled = true;
  }

  groupRowsByColumns(columns: string[]): void {
    if (!columns || columns.length === 0) {
      console.error('Column names are required for grouping.');
      return;
    }
    let data = [];
    if (this.isCurrentTaskSelected) {
      data = this.readinessData.ReadinessFormData.filter(x => !x.IsArchived);
    }
    else if (this.isArchiveTaskSelected) {
      data = this.readinessData.ReadinessFormData.filter(x => x.IsArchived);
    }
    else {
      data = this.readinessData.ReadinessFormData;
    }

    let groups = data.reduce((groups, row) => {
      const key = this.buildGroupKeyForRow(row);

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(row);
      return groups;
    }, {});

    this.groupedRows = Object.keys(groups).map(key => ({
      groupKey: key,
      rows: groups[key],
      expanded: true
    }));
    this.applySearchAndFilterVisibility();
  }

  removeGrouping(): void {
    this.groupedByColumns = [];
    this.groupedByColumnValues = [];
    this.groupedRows = {};
    this.isGroupingEnabled = false;
  }

  removeColumnFromGrouping(column: string): void {
    const index = this.groupedByColumns.indexOf(column);
    if (index !== -1) {
      this.groupedByColumns.splice(index, 1);
      this.groupedByColumnValues.splice(index, 1)
    }

    if (this.groupedByColumns.length > 0) {
      this.groupRowsByColumns(this.groupedByColumns);
    } else {
      this.removeGrouping();
    }

  }

  toggleSelectAll(): void {
    if (this.selectAll) {
      if (this.isArchiveTaskSelected) {
        this.readinessData.ReadinessFormData.forEach(x => {
          if (x.IsArchived) {
            x["IsSelected"] = true;
          }
        });
      }
      else if (this.isCurrentTaskSelected) {
        this.readinessData.ReadinessFormData.forEach(x => {
          if (!x.IsArchived) {
            x["IsSelected"] = true;
          }
        });
      }
      else {
        this.readinessData.ReadinessFormData.forEach(x => x["IsSelected"] = true);
      }
    }
    else {
      this.readinessData.ReadinessFormData.forEach(x => x["IsSelected"] = false);
    }
    this.ShowOrHideRemoveRowButton();
  }
  ShowOrHideRemoveRowButton(): void {

    if (this.isAnyRowSelected()) {
      this.isRowSelected = true;
      this.selectAll = this.readinessData.ReadinessFormData.every(row => row["IsSelected"]);
    }
    else {
      this.selectAll = false;
      this.isRowSelected = false;
    }

    this.selectedRows = this.readinessData.ReadinessFormData.filter(row => row["IsSelected"]);

    const hasArchived = this.selectedRows.some(row => row.ArchiveTaskId > 0 || row["IsArchived"] == true);
    let hasNonArchived = false;
    if (!this.isProPakWorkPackAvailable) {
      hasNonArchived = this.selectedRows.some(row => row.ArchiveTaskId == 0);
    }
    else {
      hasNonArchived = this.selectedRows.some(row => row["IsArchived"] == false);
    }

    if (hasArchived && hasNonArchived) {
      this.showUnarchiveButton = false;
      this.showArchiveButton = false;
    } else if (hasArchived) {
      this.showUnarchiveButton = true;
      this.showArchiveButton = false;
    } else if (hasNonArchived) {
      this.showUnarchiveButton = false;
      this.showArchiveButton = true;
    } else {
      this.showUnarchiveButton = false;
      this.showArchiveButton = false;
    }

  }

  getNextTaskNumber(currentTask: string): string {
    const prefixMatch = currentTask.match(/^(\d+)-(.+)$/);
    const postfixMatch = currentTask.match(/^(.+?)(\d+)$/);

    if (prefixMatch) {
      const prefixNumber = parseInt(prefixMatch[1], 10) + 1;
      const textPart = prefixMatch[2];
      return `${prefixNumber}-${textPart}`;
    }

    if (postfixMatch) {
      const textPart = postfixMatch[1];
      const numberPart = postfixMatch[2];
      const numberLength = numberPart.length;
      const nextNumber = (parseInt(numberPart, 10) + 1).toString().padStart(numberLength, '0');
      return `${textPart}${nextNumber}`;
    }

    throw new Error("Invalid task format");
  }

  async setTaskType(type: string) {
    // if(this.isArchiveTaskSelected)
    // {
    //   this.modifiedArchiveReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    //   this.originalArchiveReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData));
    //   this.archiveTaskChangedFields = JSON.parse(JSON.stringify(this.changedFields));
    // }
    // else if(this.isCurrentTaskSelected)
    // {
    //   this.modifiedCurrentReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    //   this.originalCurrentReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData));
    //   this.currentTaskChangedFields = JSON.parse(JSON.stringify(this.changedFields));
    // }
    // else if (!this.isArchiveTaskSelected && !this.isCurrentTaskSelected) {
    //   this.modifiedCombinedReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    //   this.originalCombinedReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData));
    //   this.combinedTaskChangedFields = JSON.parse(JSON.stringify(this.changedFields));
    // }

    // this.originalReadinessFormData = [];
    // this.changedFields = {};
    this.selectedFilters = {};
    this.isArchiveTaskSelected = false;
    this.isCurrentTaskSelected = false;
    this.isRowSelected = false;
    this.formsList = [];
    if (type == 'archived') {
      this.isArchiveTaskSelected = true;
      this.isCurrentTaskSelected = false;
      this.readinessData.ReadinessFormData.forEach(row => {
        row["IsSelected"] = false;
        if (row.IsArchived) {
          row["EnableDisplay"] = true;
        }
        else {
          row["EnableDisplay"] = false;
        }
      })
      this.applySearchAndFilterVisibility();
      this.fillFormList();
      // if(this.modifiedArchiveReadinessFormData && this.modifiedArchiveReadinessFormData.length > 0)
      // {
      //   // this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify(this.modifiedArchiveReadinessFormData));
      //   // this.originalReadinessFormData = JSON.parse(JSON.stringify(this.originalArchiveReadinessFormData));
      //   // this.changedFields = JSON.parse(JSON.stringify(this.archiveTaskChangedFields));
      //   this.readinessData.ReadinessFormData = this.modifiedArchiveReadinessFormData;
      //   this.originalReadinessFormData = this.originalArchiveReadinessFormData;
      //   this.changedFields = this.archiveTaskChangedFields;
      //   this.fillFormList();
      // }
      // else
      // {
      //   await this.getArchiveTask();
      // }
      this.showUnarchiveButton = false;
      this.showArchiveButton = false;
    } else if (type == 'current') {
      this.isCurrentTaskSelected = true;
      // if(this.modifiedCurrentReadinessFormData && this.modifiedCurrentReadinessFormData.length > 0)
      // {
      //   // this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify(this.modifiedCurrentReadinessFormData));
      //   // this.originalReadinessFormData = JSON.parse(JSON.stringify(this.originalCurrentReadinessFormData));
      //   // this.changedFields = JSON.parse(JSON.stringify(this.currentTaskChangedFields));
      //   this.readinessData.ReadinessFormData = this.modifiedCurrentReadinessFormData;
      //   this.originalReadinessFormData = this.originalCurrentReadinessFormData;
      //   this.changedFields = this.currentTaskChangedFields;
      //   this.fillFormList();
      // }
      // else
      // {
      //   await this.getAllWorkPacksByProjectId();
      // }
      this.readinessData.ReadinessFormData.forEach(row => {
        row["IsSelected"] = false;
        if (row.IsArchived) {
          row["EnableDisplay"] = false;
        }
        else {
          row["EnableDisplay"] = true;
        }
      })
      this.showUnarchiveButton = false;
      this.showArchiveButton = false;
      this.applySearchAndFilterVisibility();
      this.fillFormList();
    } else if (type == 'both') {
      this.showUnarchiveButton = false;
      this.showArchiveButton = false;
      // if(this.modifiedCombinedReadinessFormData && this.modifiedCombinedReadinessFormData.length > 0)
      // {
      //   this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify(this.modifiedCombinedReadinessFormData));
      //   this.originalReadinessFormData = JSON.parse(JSON.stringify(this.originalCombinedReadinessFormData));
      //   this.changedFields = JSON.parse(JSON.stringify(this.combinedTaskChangedFields));
      //   this.fillFormList();
      //   // this.cdr.detectChanges();
      //   // this.initializeResizableColumns();
      //   // this.initializeResizableRowsForTables();
      //   // this.initializeRowColors();
      // }
      // else
      // {
      // await this.loadCombinedData();
      // }
      this.readinessData.ReadinessFormData.forEach(row => {
        row["IsSelected"] = false;
        row["EnableDisplay"] = true;
      });
      this.applySearchAndFilterVisibility();
      this.fillFormList();
    }
    // this.prepareDropdownsForRows('setTaskType');
    // this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
    this.selectedTaskType = type;
    this.ShowOrHideRemoveRowButton();
  }

  async loadCombinedData(reloadAll: boolean = false) {
    try {
      this.changedFields = {};
      // const maxCurrentRow = Math.max(0, ...Object.keys(this.originalCurrentReadinessFormData || {}).map(x => +x));

      // const shiftedArchiveChangedFields: { [rowNumber: number]: { [columnName: string]: boolean } } = {};
      // for (const [rowNumber, cols] of Object.entries(this.archiveTaskChangedFields || {}) as [string, { [columnName: string]: boolean }][]) {
      //   shiftedArchiveChangedFields[maxCurrentRow + +rowNumber] = cols;
      // }

      // this.changedFields = { ...(this.currentTaskChangedFields || {}), ...shiftedArchiveChangedFields };
      this.originalReadinessFormData = [];
      if (this.modifiedArchiveReadinessFormData.length > 0 && this.modifiedCurrentReadinessFormData.length > 0) {
        // this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify([ ...(this.modifiedCurrentReadinessFormData || []), ...(this.modifiedArchiveReadinessFormData || []) ]));
        // this.originalReadinessFormData = JSON.parse(JSON.stringify([ ...(this.originalCurrentReadinessFormData || []), ...(this.originalArchiveReadinessFormData || []) ]));
        // this.changedFields = { ...(this.currentTaskChangedFields || {}), ...(this.archiveTaskChangedFields || {}) };
        this.readinessData.ReadinessFormData = [...(this.modifiedCurrentReadinessFormData || []), ...(this.modifiedArchiveReadinessFormData || [])];
        this.originalReadinessFormData = [...(this.originalCurrentReadinessFormData || []), ...(this.originalArchiveReadinessFormData || [])];
      }
      else if (this.modifiedArchiveReadinessFormData.length == 0 && this.modifiedCurrentReadinessFormData.length > 0) {
        const archiveTasks = await this.pcs.apiService.GetArchiveTask({ ProjectId: this.projectId });
        this.originalReadinessFormData = JSON.parse(JSON.stringify(archiveTasks.ArchiveTasks || []));
        let maxRowNumber = Math.max(...this.originalCurrentReadinessFormData.map(x => x.ActualRowNumber));
        this.originalReadinessFormData.forEach(row => {
          row["ApprovedForProjectSection"] = "#e74c3c";
          row["WPStatusSection"] = "#e74c3c";
          row["TRStatusSection"] = "#e74c3c";
          row["MaterialsSection"] = "#e74c3c";
          row["MTLMOCSection"] = "#e74c3c";
          row["IsArchived"] = true;
          row["ActualNumberSaved"] = row.ActualRowNumber;
          row.Number = ++maxRowNumber;
          row.ActualRowNumber = row.Number;
        });
        this.modifiedArchiveReadinessFormData = JSON.parse(JSON.stringify(this.originalReadinessFormData || []));
        // this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify([ ...(this.modifiedCurrentReadinessFormData || []), ...(archiveTasks.ArchiveTasks || []) ]));
        // this.originalReadinessFormData = JSON.parse(JSON.stringify([ ...(this.originalCurrentReadinessFormData || []), ...(archiveTasks.ArchiveTasks || []) ]));
        // this.changedFields = { ...(this.currentTaskChangedFields || {}), ...(this.archiveTaskChangedFields || {}) };
        this.readinessData.ReadinessFormData = [...(this.modifiedCurrentReadinessFormData || []), ...(this.modifiedArchiveReadinessFormData || [])];
        this.originalReadinessFormData = [...(this.originalCurrentReadinessFormData || []), ...(this.originalArchiveReadinessFormData || [])];
      }
      else if (this.modifiedArchiveReadinessFormData.length > 0 && this.modifiedCurrentReadinessFormData.length == 0) {
        const readinessData = await this.getProjectTasks();
        this.readinessData.ReadinessFormData = JSON.parse(JSON.stringify([...(readinessData.ReadinessFormData || []), ...(this.modifiedArchiveReadinessFormData || [])]));
        this.originalReadinessFormData = JSON.parse(JSON.stringify([...(readinessData.ReadinessFormData || []), ...(this.originalArchiveReadinessFormData || [])]));
        // this.changedFields = { ...(this.currentTaskChangedFields || {}), ...(this.archiveTaskChangedFields || {}) };
      }
      // this.changedFields = Object.assign(this.currentTaskChangedFields || {}, this.archiveTaskChangedFields || {});
      this.changedFields = new Proxy({}, {
        get: (_, prop) => {
          if (this.currentTaskChangedFields && prop in this.currentTaskChangedFields) {
            return this.currentTaskChangedFields[prop];
          }
          if (this.archiveTaskChangedFields && prop in this.archiveTaskChangedFields) {
            return this.archiveTaskChangedFields[prop];
          }
          return undefined;
        },
        set: (_, prop, value) => {
          if (this.currentTaskChangedFields && prop in this.currentTaskChangedFields) {
            this.currentTaskChangedFields[prop] = value;
          }
          if (this.archiveTaskChangedFields && prop in this.archiveTaskChangedFields) {
            this.archiveTaskChangedFields[prop] = value;
          }
          return true;
        }
      });
      // if(this.originalReadinessFormData.length > 0 )
      // {
      //   this.originalReadinessFormData.forEach((row, index) => {
      //     row.ActualRowNumber = index + 1;
      //     row.Number = index + 1;
      //   });
      // }
      if (reloadAll) {
        this.resetAllData();
        this.changedFields = {};
        this.originalReadinessFormData = [];
        const [archiveTasks, readinessData] = await Promise.all([
          this.pcs.apiService.GetArchiveTask({ ProjectId: this.projectId }),
          this.getProjectTasks()
        ]);

        const combinedTasks = [
          ...(readinessData.ReadinessFormData || []),
          ...(archiveTasks.ArchiveTasks || [])
        ];

        this.readinessData.ReadinessFormData = combinedTasks;
      }


      let count = 1;
      this.readinessData.ReadinessFormData.forEach(row => {
        row["ActualNumberSaved"] = row.Number;
        row.Number = count++;
        row["ApprovedForProjectSection"] = "#e74c3c";
        row["WPStatusSection"] = "#e74c3c";
        row["TRStatusSection"] = "#e74c3c";
        row["MaterialsSection"] = "#e74c3c";
        row["MTLMOCSection"] = "#e74c3c";
        row.ActualRowNumber = row.Number;

        if (row.ArchiveTaskId > 0) {
          row["IsArchived"] = true;
        }
      });
      this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
      this.fillFormList();
      this.cdr.detectChanges();
      // this.initializeResizableColumns();
      this.initializeResizableRowsForTables();
      this.initializeRowColors();
    } catch (error) {
      console.error("Error loading combined data: ", error);
    }
  }

  async getConfirmationText(operation: string) {
    const hasArchivedTaskChanges = Object.keys(this.archiveTaskChangedFields).length > 0;
    const hasCurrentTaskChanges = Object.keys(this.currentTaskChangedFields).length > 0;
    const hasCombinedTaskChanges = Object.keys(this.combinedTaskChangedFields).length > 0;

    let text = `Are you sure you want to ${operation} selected rows?`;

    if (hasArchivedTaskChanges || hasCurrentTaskChanges || hasCombinedTaskChanges) {
      let changes: string[] = [];

      if (hasArchivedTaskChanges) {
        changes.push('archived tab');
      }
      if (hasCurrentTaskChanges) {
        changes.push('current tab');
      }
      if (hasCombinedTaskChanges) {
        changes.push('both tab');
      }

      text = `You have unsaved changes in ${changes.join(', ')}. All the changes will be reset. Are you sure you want to delete the selected rows?`;
    }
    return text;
  }
  async deleteArchiveTasks(reload: boolean = true) {
    const text = await this.getConfirmationText('delete');
    const confirmation = await this.pcs.confirm(text, 'Delete archive tasks', { icon: 'warning' });

    if (confirmation) {
      try {
        // this.resetAllData();
        var archiveTaskIds = this.readinessData.ReadinessFormData.filter(x => x["IsSelected"]).map(x => x.ArchiveTaskId);
        var result = await this.pcs.apiService.DeleteArchiveProjectTasks({ taskIds: archiveTaskIds });
        if (result && reload) {
          // this.resetAllData();
          // this.getAllWorkPacksByProjectId();
          // this.getArchiveTask();
          this.readinessData.ReadinessFormData = this.readinessData.ReadinessFormData.filter(row => !row["IsSelected"]);
          this.ShowOrHideRemoveRowButton();
        }
      } catch (error) {
        const message = this.extractValidationMessage(error);
        if (message) {
          this.notifySaveFailed(message, false, 'Delete failed');
        }
      }
    }
  }

  async UnarchiveTasks() {
    // Commit any in-progress cell edit (e.g. Task # still focused) before change detection / save.
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }

    const selectedBeforeSave = this.readinessData.ReadinessFormData.filter(x => x["IsSelected"]);
    if (!selectedBeforeSave.length) {
      return;
    }

    // Rebuild changedFields from row vs original so Task # edits are not missed
    // (tracking can fail when ActualRowNumber / original snapshots drift).
    this.syncChangedFieldsFromOriginal(selectedBeforeSave);

    const hasPendingChanges =
      this.hasUnsavedChanges() ||
      this.selectedRowsHaveUnsavedChanges(selectedBeforeSave) ||
      this.rowsDifferFromOriginal(selectedBeforeSave);

    let busyHeld = false;
    const startBusy = () => {
      if (busyHeld) {
        return;
      }
      busyHeld = true;
      // Keep loader visible across save + unarchive + reload (avoids flicker between calls).
      this.pcs.busyService.increment(Promise.resolve());
      // BusyService.increment delays UI by 100ms; force immediate show for this flow.
      this.pcs.busyService.isBusy = true;
    };
    const stopBusy = () => {
      if (!busyHeld) {
        return;
      }
      busyHeld = false;
      this.pcs.busyService.decrement();
    };

    try {
      if (hasPendingChanges) {
        const saveAndUnarchive = await this.pcs.confirm(
          'You have unsaved changes. Save the changes and then unarchive the selected tasks?',
          'Save and unarchive'
        );
        if (!saveAndUnarchive) {
          return;
        }

        this.syncChangedFieldsFromOriginal(selectedBeforeSave);
        startBusy();
        // Quiet save under the held loader: no success toast / id-sync refresh.
        const saved = await this.saveAllTasks({ silent: true, skipIdSync: true, showLoader: false });
        if (!saved) {
          this.pcs.notificationService.warning(
            'Unarchive cancelled',
            'Save failed or required fields are missing. Fix the issues, save, then try Unarchive again.'
          );
          return;
        }
      } else {
        const text = this.getUnarchiveConfirmationText();
        const confirmation = await this.pcs.confirm(text, 'Unarchive tasks');
        if (!confirmation) {
          return;
        }
      }

      const pendingCurrentChanges = this.capturePendingCurrentTaskChanges(new Set<string>());
      // Re-read selection after optional save (selection flags are preserved on rows).
      const readinessData = this.readinessData.ReadinessFormData.filter(x => x["IsSelected"]);
      if (!readinessData.length) {
        this.pcs.notificationService.warning(
          'Unarchive',
          'No selected archived tasks were found after save. Please select the tasks and try again.'
        );
        return;
      }

      if (this.isProPakWorkPackAvailable) {
        let unsavedTasks = readinessData.filter(row => row.ArchiveTaskId == 0);
        if (unsavedTasks.length > 0) {
          this.readinessData.ReadinessFormData = this.readinessData.ReadinessFormData
            .filter(row => !(row["IsSelected"] && row.ArchiveTaskId === 0));
          unsavedTasks.forEach(x => {
            x["IsSelected"] = false;
            x["IsArchived"] = false;
          });
          this.originalCurrentReadinessFormData.push(...unsavedTasks);
          this.modifiedCurrentReadinessFormData.push(...unsavedTasks);
          this.originalArchiveReadinessFormData = this.originalArchiveReadinessFormData.filter(row => !unsavedTasks.some(unsaved => unsaved.TaskNumber === row.TaskNumber));
          this.modifiedArchiveReadinessFormData = this.modifiedArchiveReadinessFormData.filter(row => !unsavedTasks.some(unsaved => unsaved.TaskNumber === row.TaskNumber));
          if (!this.isArchiveTaskSelected && !this.isCurrentTaskSelected) {
            // this.loadCombinedData();
          }
        }
      }
      if (this.isProPakWorkPackAvailable) {
        let unsavedTasks = readinessData.filter(row => row.ArchiveTaskId > 0);
        if (unsavedTasks.length == 0) {
          return;
        }
      }
      let taskNumbers = '';
      let invalidData = false;
      readinessData.forEach(x => {
        if (x.PriorityId === undefined || x.PriorityId === null || String(x.PriorityId) === '' || x.PriorityId === -1) {
          taskNumbers += x.TaskNumber + ', ';
          invalidData = true;
        }
      });
      if (invalidData) {
        this.pcs.notificationService.danger('', 'Please select Priority for Task Numbers: ' + taskNumbers.slice(0, -2) + ' to unarchive tasks.');
        return;
      }

      startBusy();
      // useBusyIndicator false — loader is already held for the whole operation.
      var result = await this.pcs.apiService.UnArchiveProjectTasks({ ArchiveTasks: readinessData }, false, false);
      if (result) {
        await this.resetAllData();
        await this.getAllWorkPacksByProjectId();
        this.restorePendingCurrentTaskChanges(pendingCurrentChanges);
        await this.prepareDropdownsForRows('UnarchiveTasks');
        this.isRowSelected = false;
        this.showArchiveButton = false;
        this.showUnarchiveButton = false;
      }
    } catch (error) {
      const message = this.extractValidationMessage(error);
      if (message) {
        this.notifySaveFailed(message, false, 'Unarchive failed');
      }
    } finally {
      stopBusy();
    }
  }

  /** True when any of the given rows has tracked field edits (excluding Status). */
  private selectedRowsHaveUnsavedChanges(rows: any[]): boolean {
    if (!rows || !rows.length) {
      return false;
    }
    return rows.some(row => {
      const rowChanges = this.changedFields && this.changedFields[row.ActualRowNumber];
      if (!rowChanges) {
        return false;
      }
      return Object.keys(rowChanges).some(columnName => columnName !== 'Status' && !!rowChanges[columnName]);
    });
  }

  private getUnarchiveConfirmationText(): string {
    return 'Are you sure you want to unarchive selected rows?';
  }

  /** Resolve the original snapshot for a row (Archive/Project task ids first, then ActualRowNumber). */
  private findOriginalRow(row: any): any {
    if (!row || !this.originalReadinessFormData || !this.originalReadinessFormData.length) {
      return null;
    }
    if (row.ArchiveTaskId > 0) {
      const byArchive = this.originalReadinessFormData.find(x => x.ArchiveTaskId == row.ArchiveTaskId);
      if (byArchive) {
        return byArchive;
      }
    }
    if (row.ProjectTaskId > 0) {
      const byProject = this.originalReadinessFormData.find(x => x.ProjectTaskId == row.ProjectTaskId);
      if (byProject) {
        return byProject;
      }
    }
    return this.originalReadinessFormData.find(x => x.ActualRowNumber == row.ActualRowNumber) || null;
  }

  private getComparableColumnNames(): string[] {
    const fromMeta = (this.readinessColumnData || [])
      .map(c => c.ColumnName)
      .filter(name => !!name && name !== 'Status');
    if (fromMeta.length) {
      return fromMeta;
    }
    return [
      'TaskNumber', 'TaskDescription', 'Scope', 'PriorityId', 'Owner', 'Discipline', 'Contractor',
      'TARApprovalId', 'HeightAccessId', 'Crane', 'HotWork', 'IsolationPlanId', 'WorkPackRequiredId',
      'RevId', 'MaterialStatusId', 'LongLead', 'FabricationId', 'StagedOnSite', 'MOCRequired',
      'MOCNumber', 'MOCStatusId', 'Comments', 'ShutInRequired', 'Number'
    ];
  }

  private normalizeComparableValue(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  }

  /** True when any row value differs from the original snapshot (excludes Status). */
  private rowsDifferFromOriginal(rows: any[]): boolean {
    if (!rows || !rows.length) {
      return false;
    }
    const columns = this.getComparableColumnNames();
    return rows.some(row => {
      const original = this.findOriginalRow(row);
      if (!original) {
        return false;
      }
      return columns.some(columnName =>
        this.normalizeComparableValue(row[columnName]) !== this.normalizeComparableValue(original[columnName])
      );
    });
  }

  /**
   * Ensures changedFields includes real diffs so SaveArchiveTasks persists Task # etc.
   * Backend only applies columns present in ChangedFields for existing archive rows.
   */
  private syncChangedFieldsFromOriginal(rows: any[]): void {
    if (!rows || !rows.length) {
      return;
    }
    if (!this.changedFields) {
      this.changedFields = {};
    }
    const columns = this.getComparableColumnNames();
    rows.forEach(row => {
      const original = this.findOriginalRow(row);
      if (!original) {
        return;
      }
      const rowNum = row.ActualRowNumber;
      columns.forEach(columnName => {
        const hasChanged =
          this.normalizeComparableValue(row[columnName]) !== this.normalizeComparableValue(original[columnName]);
        if (hasChanged) {
          if (!this.changedFields[rowNum]) {
            this.changedFields[rowNum] = {};
          }
          this.changedFields[rowNum][columnName] = true;
        } else if (this.changedFields[rowNum]) {
          delete this.changedFields[rowNum][columnName];
          if (Object.keys(this.changedFields[rowNum]).length === 0) {
            delete this.changedFields[rowNum];
          }
        }
      });
    });
  }

  getDropdownOptions(columnName: string, row?: any): any[] {
    if (!this.dropdownOptions && this.getCommandResponse) {
      this.dropdownOptions = {
        PriorityId: this.getCommandResponse.Priorities,
        TARApprovalId: this.getCommandResponse.TARApprovals,
        ShutInRequired: this.options,
        HeightAccessId: this.getCommandResponse.HeightAccesses,
        IsolationPlanId: this.getCommandResponse.IsolationPlans,
        Crane: this.options,
        HotWork: this.options,
        MaterialStatusId: this.getCommandResponse.MaterialStatuses,
        FabricationId: this.getCommandResponse.Fabrications,
        MOCStatusId: this.getCommandResponse.MOCStatuses,
        ProjectPhases: this.getCommandResponse.ProjectPhases,
        RevId: this.getCommandResponse.Revs,
        WorkPackRequiredId: this.getCommandResponse.WorkPackRequired,
        StagedOnSite: this.getCommandResponse.StagedOnSites,
        LongLead: this.options,
        MOCRequired: this.options
      };
    }

    if (row) {
      if (row.MaterialStatusId <= 0 && (columnName === 'LongLead' || columnName === 'StagedOnSite')) {
        return [];
      }
      if (row.WorkPackRequiredId <= 0 && columnName === 'RevId') {
        return [];
      }
      else {
        this.getCommandResponse.WorkPackRequired.filter(x => x.Id === row.WorkPackRequiredId).forEach(x => {
          if (x.Description === 'No') {
            return this.getCommandResponse.Revs.filter(x => x.Description == 'N/A');
          }
        })
      }
    }
    return this.dropdownOptions[columnName] || [];
  }

  async prepareDropdownsForRows(methodName: string) {
    console.log(`prepareDropdownsForRows() called from method ${methodName}.`);
    this.readinessData.ReadinessFormData.forEach(row => {
      const readinessRow = row as ReadinessData;
      readinessRow._dropdownOptions = {};

      // Default dropdowns
      readinessRow._dropdownOptions['PriorityId'] = this.getCommandResponse.Priorities || [];
      readinessRow._dropdownOptions['TARApprovalId'] = this.getCommandResponse.TARApprovals || [];
      readinessRow._dropdownOptions['ShutInRequired'] = this.options;
      readinessRow._dropdownOptions['HeightAccessId'] = this.getCommandResponse.HeightAccesses || [];
      readinessRow._dropdownOptions['IsolationPlanId'] = this.getCommandResponse.IsolationPlans || [];
      readinessRow._dropdownOptions['Crane'] = this.options;
      readinessRow._dropdownOptions['HotWork'] = this.options;
      readinessRow._dropdownOptions['MaterialStatusId'] = this.getCommandResponse.MaterialStatuses || [];
      readinessRow._dropdownOptions['FabricationId'] = this.getCommandResponse.Fabrications || [];
      readinessRow._dropdownOptions['MOCStatusId'] = this.getCommandResponse.MOCStatuses || [];
      readinessRow._dropdownOptions['ProjectPhases'] = this.getCommandResponse.ProjectPhases || [];
      readinessRow._dropdownOptions['WorkPackRequiredId'] = this.getCommandResponse.WorkPackRequired || [];
      readinessRow._dropdownOptions['MOCRequired'] = this.options;
      readinessRow._dropdownOptions['StagedOnSite'] = this.getCommandResponse.StagedOnSites || [];
      readinessRow._dropdownOptions['LongLead'] = this.options;
      readinessRow._dropdownOptions['RevId'] = this.getCommandResponse.Revs || [];
      this.prepareDropdownsForSingleRow(readinessRow);
    });
  }

  prepareDropdownsForSingleRow(row: any, onChange: boolean = false) {
    row._dropdownOptions = row._dropdownOptions || {};

    row._dropdownOptions['RevId'] = [];

    const selectedWorkPack = this.getCommandResponse.WorkPackRequired.find(x => x.Id == row.WorkPackRequiredId);
    if (selectedWorkPack) {
      if (selectedWorkPack && selectedWorkPack.Description == 'No') {
        let RevOptions = this.getCommandResponse.Revs.filter(r => r.Description == 'N/A');
        row.RevId = RevOptions[0].Id;
        row._dropdownOptions['RevId'] = RevOptions;
      } else {
        let RevOptions = this.getCommandResponse.Revs.filter(r => r.Description != 'N/A');
        row._dropdownOptions['RevId'] = RevOptions;
        let selectedOption = RevOptions.find(r => r.Id == row.RevId);
        if (!selectedOption) {
          row.RevId = -1;
        }
      }
    }

    if (row.MaterialStatusId <= 0) {
      row._dropdownOptions['StagedOnSite'] = [];
      row._dropdownOptions['LongLead'] = [];
    }
    // else {
    //   // row._dropdownOptions['StagedOnSite'] = this.getCommandResponse.StagedOnSites;
    //   row._dropdownOptions['LongLead'] = this.options;
    // }
  }


  // Trigger the file input click
  onImport() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle file selection
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files[0];

    if (file) {
      this.readFileContent(file);
    }
  }

  // Read file content
  readFileContent(file: File) {
    const reader = new FileReader();


    reader.readAsText(file); // Or use readAsArrayBuffer or readAsDataURL based on file type
  }

  getSectionText(sectionName: string): string {
    switch (sectionName) {
      case 'TR STATUS':
        return 'Task Requirements';
      case 'WP STATUS':
        return 'Work Package';
      case 'MATERIALS':
        return 'Material'
      case 'MTL MOC':
        return 'MOC'
      case 'APPROVED For Project/TAR':
        return 'Approved For Project/TAR';
      default:
        return '';
    }
  }

  /** Display lines for vertical section headers (multiline wraps beside each other when rotated). */
  getSectionDisplayLines(sectionName: string): string[] {
    const normalized = (sectionName || '').trim().toUpperCase();
    if (normalized === 'APPROVED FOR PROJECT/TAR' || normalized.includes('APPROVED') && normalized.includes('PROJECT/TAR')) {
      return ['APPROVED For', 'Project/TAR'];
    }
    return [this.getSectionDisplayName(sectionName)];
  }

  /** Display label for section headers; keeps underlying SectionName unchanged for logic. */
  getSectionDisplayName(sectionName: string): string {
    if (sectionName === 'MTL MOC' || sectionName === 'Task List MOC') {
      return 'MOC';
    }
    return sectionName;
  }

  /** True when vertical section label wraps to more than one line. */
  isMultilineSectionHeader(sectionName: string): boolean {
    return this.getSectionDisplayLines(sectionName).length > 1;
  }


  changedFields: { [rowNumber: number]: { [columnName: string]: boolean } } = {};
  /** Cells to keep highlighted after auto-save (navigation treated as clean; UI still shows edited cells). */
  autoSavedHighlightFields: { [rowNumber: number]: { [columnName: string]: boolean } } = {};

  /** Highlight for template: pending edits OR cells last auto-saved (cleared on manual save). */
  isFieldHighlightChanged(rowNumber: number, columnName: string): boolean {
    const ch = this.changedFields[rowNumber] && this.changedFields[rowNumber][columnName];
    const ah = this.autoSavedHighlightFields[rowNumber] && this.autoSavedHighlightFields[rowNumber][columnName];
    return !!(ch || ah);
  }

  private mergeChangedFieldMapIntoAutoSaved(fields: { [rowNumber: number]: { [columnName: string]: boolean } }): void {
    if (!fields) {
      return;
    }
    for (const rowStr in fields) {
      if (!fields.hasOwnProperty(rowStr)) {
        continue;
      }
      const rowNum = +rowStr;
      const cols = fields[rowNum];
      if (!cols) {
        continue;
      }
      for (const colName in cols) {
        if (!cols.hasOwnProperty(colName) || !cols[colName]) {
          continue;
        }
        if (!this.autoSavedHighlightFields[rowNum]) {
          this.autoSavedHighlightFields[rowNum] = {};
        }
        this.autoSavedHighlightFields[rowNum][colName] = true;
      }
    }
  }

  private mergeAutoSavedHighlightsFromChangedFields(): void {
    this.mergeChangedFieldMapIntoAutoSaved(this.changedFields);
  }

  private mergeAutoSavedHighlightsForActualRows(actualRowNumbers: Set<number>): void {
    for (const rowStr in this.changedFields) {
      if (!this.changedFields.hasOwnProperty(rowStr)) {
        continue;
      }
      const rowNum = +rowStr;
      if (!actualRowNumbers.has(rowNum)) {
        continue;
      }
      const cols = this.changedFields[rowNum];
      if (!cols) {
        continue;
      }
      for (const colName in cols) {
        if (!cols.hasOwnProperty(colName) || !cols[colName]) {
          continue;
        }
        if (!this.autoSavedHighlightFields[rowNum]) {
          this.autoSavedHighlightFields[rowNum] = {};
        }
        this.autoSavedHighlightFields[rowNum][colName] = true;
      }
    }
  }

  private mergeArchiveTaskChangedHighlightsForActualRows(actualRowNumbers: Set<number>): void {
    if (!this.archiveTaskChangedFields) {
      return;
    }
    for (const rowStr in this.archiveTaskChangedFields) {
      if (!this.archiveTaskChangedFields.hasOwnProperty(rowStr)) {
        continue;
      }
      const rowNum = +rowStr;
      if (!actualRowNumbers.has(rowNum)) {
        continue;
      }
      const cols = this.archiveTaskChangedFields[rowNum];
      if (!cols) {
        continue;
      }
      for (const colName in cols) {
        if (!cols.hasOwnProperty(colName) || !cols[colName]) {
          continue;
        }
        if (!this.autoSavedHighlightFields[rowNum]) {
          this.autoSavedHighlightFields[rowNum] = {};
        }
        this.autoSavedHighlightFields[rowNum][colName] = true;
      }
    }
  }

  /**
   * After save, Task # renames can create a new DB ProjectTaskId while the UI still holds the old one.
   * Refresh IDs by TaskNumber so later drag/order saves match the active row.
   */
  private async syncProjectTaskIdsAfterSave(): Promise<void> {
    if (!this.readinessData || !this.readinessData.ReadinessFormData || !this.projectId) {
      return;
    }
    try {
      const fresh = await this.pcs.apiService.GetReadinessFormData({ ProjectId: this.projectId }, false, false);
      if (!fresh || !fresh.ReadinessFormData || !fresh.ReadinessFormData.length) {
        return;
      }
      const byTaskNumber: { [key: string]: any } = {};
      for (const row of fresh.ReadinessFormData) {
        if (row.IsArchived || !row.TaskNumber) {
          continue;
        }
        byTaskNumber[String(row.TaskNumber).trim().toLowerCase()] = row;
      }
      for (const row of this.readinessData.ReadinessFormData) {
        if (row.IsArchived || !row.TaskNumber) {
          continue;
        }
        const match = byTaskNumber[String(row.TaskNumber).trim().toLowerCase()];
        if (match && match.ProjectTaskId && match.ProjectTaskId !== row.ProjectTaskId) {
          row.ProjectTaskId = match.ProjectTaskId;
        }
      }
      if (this.originalReadinessFormData && this.originalReadinessFormData.length) {
        for (const row of this.originalReadinessFormData) {
          if (row.IsArchived || !row.TaskNumber) {
            continue;
          }
          const match = byTaskNumber[String(row.TaskNumber).trim().toLowerCase()];
          if (match && match.ProjectTaskId && match.ProjectTaskId !== row.ProjectTaskId) {
            row.ProjectTaskId = match.ProjectTaskId;
          }
        }
      }
    } catch (e) {
      console.warn('Unable to sync ProjectTaskIds after save', e);
    }
  }

  /**
   * After save without reloading work packs: update originals / dirty state like auto-save.
   * @param mode 'all' = full form snapshot; otherwise only rows in the set are synced and cleared from changed maps.
   */
  private applyPostSaveWithoutRefresh(
    mode: 'all' | Set<number>,
    options: { silent: boolean; includeProPakArchiveFieldMap?: boolean }
  ): void {
    const includeArch = options.includeProPakArchiveFieldMap === true;
    const silent = options.silent;
    if (mode === 'all') {
      if (silent) {
        this.mergeAutoSavedHighlightsFromChangedFields();
        if (includeArch) {
          this.mergeChangedFieldMapIntoAutoSaved(this.archiveTaskChangedFields);
        }
      } else {
        this.autoSavedHighlightFields = {};
      }
      if (this.readinessData && this.readinessData.ReadinessFormData) {
        this.originalReadinessFormData = JSON.parse(JSON.stringify(this.readinessData.ReadinessFormData));
      }
      this.changedFields = {};
      if (includeArch) {
        this.archiveTaskChangedFields = {};
      }
      this.rowDragged = false;
      this.isNewRowAdded = false;
    } else {
      this.mergeAutoSavedHighlightsForActualRows(mode);
      if (includeArch) {
        this.mergeArchiveTaskChangedHighlightsForActualRows(mode);
      }
      const savedList: number[] = [];
      mode.forEach((ar) => {
        savedList.push(ar);
      });
      for (let i = 0; i < savedList.length; i++) {
        const ar = savedList[i];
        const cur = this.readinessData.ReadinessFormData.find((x) => x.ActualRowNumber === ar);
        let oi = -1;
        for (let j = 0; j < this.originalReadinessFormData.length; j++) {
          if (this.originalReadinessFormData[j].ActualRowNumber === ar) {
            oi = j;
            break;
          }
        }
        if (cur && oi >= 0) {
          this.originalReadinessFormData[oi] = JSON.parse(JSON.stringify(cur));
        }
        delete this.changedFields[ar];
        if (includeArch) {
          delete this.archiveTaskChangedFields[ar];
        }
      }
      if (!this.hasUnsavedFormChanges()) {
        this.rowDragged = false;
        this.isNewRowAdded = false;
      }
    }
    if (!silent) {
      this.undoStack = [];
      this.redoStack = [];
      if (this.readinessData && this.readinessData.ReadinessFormData) {
        this.readinessData.ReadinessFormData.forEach((row: any) => {
          row.IsNew = false;
        });
      }
      if (this.originalReadinessFormData) {
        this.originalReadinessFormData.forEach((row: any) => {
          row.IsNew = false;
        });
      }
    }
    // Uploaded images are associated once a save cycle succeeds, so clear the warning tracker.
    this.tasksWithUnsavedImages.clear();
    // Saving moves cells between the changed maps, so re-diff what other users should see.
    this.syncRemoteChangeBroadcast();
    this.lastAutoSavedChangeKey = this.getCurrentChangeKey();
    if (this.autoSaveEnabled) {
      this.autoSaveCountdownSec = this.autoSaveIntervalSec;
    }
    if (!silent) {
      this.pcs.notificationService.success('Saved', 'Your changes have been saved successfully.');
    }
  }

  private capturePendingCurrentTaskChanges(excludedTaskNumbers: Set<string>): Array<{ taskNumber: string; isNew: boolean; rowSnapshot: any; originalRowSnapshot: any; changedColumns: string[] }> {
    const pending: Array<{ taskNumber: string; isNew: boolean; rowSnapshot: any; originalRowSnapshot: any; changedColumns: string[] }> = [];
    const capturedRowNumbers: { [rowNumber: number]: boolean } = {};
    for (const rowStr in this.changedFields) {
      if (!this.changedFields.hasOwnProperty(rowStr)) {
        continue;
      }
      const rowNum = +rowStr;
      const cols = this.changedFields[rowNum];
      if (!cols) {
        continue;
      }
      const currentRow: any = this.readinessData && this.readinessData.ReadinessFormData
        ? this.readinessData.ReadinessFormData.find((x) => x.ActualRowNumber === rowNum)
        : null;
      if (!currentRow || currentRow.IsArchived) {
        continue;
      }
      if (excludedTaskNumbers && excludedTaskNumbers.has(currentRow.TaskNumber)) {
        continue;
      }
      const changedColumns = Object.keys(cols).filter((col) => !!cols[col]);
      if (changedColumns.length === 0) {
        continue;
      }
      const originalRow = this.originalReadinessFormData
        ? this.originalReadinessFormData.find((x) => x.ActualRowNumber === rowNum)
        : null;
      pending.push({
        taskNumber: currentRow.TaskNumber,
        isNew: !!currentRow.IsNew || currentRow.ProjectTaskId === 0,
        rowSnapshot: JSON.parse(JSON.stringify(currentRow)),
        originalRowSnapshot: originalRow ? JSON.parse(JSON.stringify(originalRow)) : null,
        changedColumns
      });
      capturedRowNumbers[rowNum] = true;
    }

    // Also keep unsaved "new rows" even if there are no tracked changed columns yet.
    const rows = this.readinessData && this.readinessData.ReadinessFormData
      ? this.readinessData.ReadinessFormData
      : [];
    for (let i = 0; i < rows.length; i++) {
      const currentRow: any = rows[i];
      if (!currentRow || currentRow.IsArchived) {
        continue;
      }
      if (!(!!currentRow.IsNew || currentRow.ProjectTaskId === 0)) {
        continue;
      }
      if (excludedTaskNumbers && excludedTaskNumbers.has(currentRow.TaskNumber)) {
        continue;
      }
      if (capturedRowNumbers[currentRow.ActualRowNumber]) {
        continue;
      }
      const originalRow = this.originalReadinessFormData
        ? this.originalReadinessFormData.find((x) => x.ActualRowNumber === currentRow.ActualRowNumber)
        : null;
      pending.push({
        taskNumber: currentRow.TaskNumber,
        isNew: true,
        rowSnapshot: JSON.parse(JSON.stringify(currentRow)),
        originalRowSnapshot: originalRow ? JSON.parse(JSON.stringify(originalRow)) : null,
        changedColumns: []
      });
      capturedRowNumbers[currentRow.ActualRowNumber] = true;
    }
    return pending;
  }

  private restorePendingCurrentTaskChanges(pending: Array<{ taskNumber: string; isNew: boolean; rowSnapshot: any; originalRowSnapshot: any; changedColumns: string[] }>): void {
    if (!pending || pending.length === 0 || !this.readinessData || !this.readinessData.ReadinessFormData) {
      return;
    }
    if (!this.changedFields) {
      this.changedFields = {};
    }
    let restoredNewRows = 0;
    for (let i = 0; i < pending.length; i++) {
      const item = pending[i];
      let targetRow: any = this.readinessData.ReadinessFormData.find((x: any) => !x.IsArchived && x.TaskNumber === item.taskNumber);
      if (!targetRow && item.isNew) {
        const maxActualRowNumber = this.readinessData.ReadinessFormData.length > 0
          ? Math.max(...this.readinessData.ReadinessFormData.map(x => x.ActualRowNumber))
          : 0;
        const maxNumber = this.readinessData.ReadinessFormData.length > 0
          ? Math.max(...this.readinessData.ReadinessFormData.map(x => x.Number))
          : 0;
        const newActualRowNumber = maxActualRowNumber + 1;
        targetRow = JSON.parse(JSON.stringify(item.rowSnapshot));
        targetRow.ActualRowNumber = newActualRowNumber;
        targetRow.Number = maxNumber + 1;
        targetRow.IsSelected = false;
        targetRow.IsArchived = false;
        targetRow.ArchiveTaskId = 0;
        targetRow.ProjectTaskId = 0;
        targetRow.IsNew = true;
        targetRow.EnableDisplay = true;
        let insertIndex = this.readinessData.ReadinessFormData.findIndex((x: any) => x.IsArchived || x.ArchiveTaskId > 0);
        if (insertIndex < 0) {
          insertIndex = this.readinessData.ReadinessFormData.length;
        }
        this.readinessData.ReadinessFormData.splice(insertIndex, 0, targetRow);
        const originalSnapshot = item.originalRowSnapshot ? JSON.parse(JSON.stringify(item.originalRowSnapshot)) : JSON.parse(JSON.stringify(targetRow));
        originalSnapshot.ActualRowNumber = newActualRowNumber;
        originalSnapshot.Number = targetRow.Number;
        const originalInsertIndex = insertIndex > this.originalReadinessFormData.length
          ? this.originalReadinessFormData.length
          : insertIndex;
        this.originalReadinessFormData.splice(originalInsertIndex, 0, originalSnapshot);
        this.prepareDropdownsForSingleRow(targetRow);
        restoredNewRows++;
      }
      if (!targetRow) {
        continue;
      }
      if (!this.changedFields[targetRow.ActualRowNumber]) {
        this.changedFields[targetRow.ActualRowNumber] = {};
      }
      for (let j = 0; j < item.changedColumns.length; j++) {
        const col = item.changedColumns[j];
        targetRow[col] = item.rowSnapshot[col];
        this.changedFields[targetRow.ActualRowNumber][col] = true;
      }
    }
    if (restoredNewRows > 0) {
      this.isNewRowAdded = true;
      this.fillFormList();
      this.cdr.detectChanges();
    }
  }

  onFieldChange(rowNumber: number, columnName: string, newValue: any, isUndo: boolean = false): void {
    if (!this.readinessData || !this.readinessData.ReadinessFormData || !this.originalReadinessFormData) return;

    const currentRow = this.readinessData.ReadinessFormData.find(x => x.ActualRowNumber == rowNumber);
    if (!currentRow) return;
    const original = this.findOriginalRow(currentRow);
    if (!original) return;

    const originalValue = original[columnName];
    let previousValue = currentRow[columnName]; // store what it was before this change
    const hasChanged = String(newValue) !== String(originalValue);

    var existingIndex = -1;
    for (var i = this.undoStack.length - 1; i >= 0; i--) {
      var change = this.undoStack[i];
      if (change.rowNumber === rowNumber && change.columnName === columnName) {
        existingIndex = i;
        break; // first found from the end = last occurrence
      }
    }

    // Do not track Status column in undo/redo stacks
    if (columnName !== 'Status') {
      if (!isUndo) {
        if (existingIndex === -1) {
          previousValue = original[columnName]; // store what it was before this change
        } else {
          previousValue = this.undoStack[existingIndex].newValue;
        }
        this.undoStack.push({
          rowNumber,
          columnName,
          oldValue: previousValue,
          newValue
        });
      }
      // this.redoStack = [];
    }

    // Apply the change
    currentRow[columnName] = newValue;

    // let actualRowNumber = currentRow["ActualRowNumber"];
    // if(currentRow["IsArchived"])
    // {
    //   actualRowNumber = "A-" + actualRowNumber;
    // }
    // else
    // {
    //   actualRowNumber = "C-" + actualRowNumber;
    // }
    // Track changed fields
    if (!this.changedFields[rowNumber]) {
      this.changedFields[rowNumber] = {};
    }
    this.changedFields[rowNumber][columnName] = hasChanged;

    // Let everyone else paint this cell in our colour (debounced, so typing sends once).
    this.scheduleRemoteChangeBroadcast();

    // Lock the row if needed
    if (!this.lockedBy) {
      this.lockedBy = this.currentEditor;
      this.signalRService.notifyLocked(this.projectId, this.currentEditor);
    }

    if (columnName !== 'Status') {
      this.refreshRowVisibilityAfterEdit();
    }
  }





  // isFieldChanged(rowIndex: number, columnName: string): boolean {
  //   if (!this.readinessData || !this.readinessData.ReadinessFormData) return false;
  //   if (!this.originalReadinessFormData) return false;
  //   if (rowIndex >= this.readinessData.ReadinessFormData.length || rowIndex >= this.originalReadinessFormData.length)
  //     return false;

  //   let rowNumber = this.readinessData.ReadinessFormData[rowIndex].Number;
  //   var current = this.readinessData.ReadinessFormData[rowIndex];
  //   var original = this.originalReadinessFormData.find(x => x.Number === rowNumber);

  //   if (!current || !original) return false;

  //   return String(current[columnName]) !== String(original[columnName]);
  // }

  // openWorkPack() {
  //   const selectedRows = this.readinessData.ReadinessFormData.filter(row => row["IsSelected"]);

  //   let taskNumber = selectedRows.length === 1 ? selectedRows[0].TaskNumber : '';
  //   let url = `${this.readinessSummary.ProPakProjectUrl}#${this.userName},${this.readinessSummary.PassThroughAuthKey},${this.projectId}`;
  //   if (taskNumber) {
  //     url += `,${taskNumber}`;
  //   }

  //   window.open(url, "_blank");
  // }

  openWorkPack() {
    const selectedRows = this.readinessData.ReadinessFormData.filter(row => row["IsSelected"]);
    const hasChanges = Object.keys(this.changedFields).some(rowNumber => {
      return Object.keys(this.changedFields[+rowNumber])
        .some(columnName => columnName !== 'Status' && this.changedFields[+rowNumber][columnName]);
    }) || this.isNewRowAdded;

    if (hasChanges && selectedRows.length > 0) {
      this.pcs.notificationService.warning(
        '',
        'You have unsaved changes. Please save your changes before opening the Work Pack.'
      );
      return;
    }
    const projectId = this.projectId;
    const userName = this.userName;
    const token = this.readinessSummary.PassThroughAuthKey;

    const workPackIds = selectedRows.map(row => row.ProjectTaskId).filter(Boolean);
    let params: { [key: string]: string } = {
      projectId: projectId.toString(),
      userName: userName,
      token: token
    };
    if (workPackIds.length > 0) {
      params.workPackIds = workPackIds.join(",");
    }

    const paramString = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    const url = `${this.readinessSummary.ProPakProjectUrl}#${paramString}`;
    window.open(url, "_blank");
  }

  private isCellInSection(cellId: string, sectionName: string): boolean {
    const columnName = cellId.split('-')[1];

    return this.groupedColumns.some(section =>
      section.sectionName === sectionName &&
      section.columns.some(col => col.ColumnName === columnName)
    );
  }

  getUsersBySection(sectionName: string): string[] {
    const users = new Set<string>();

    for (const cellId in this.editingUsers) {
      if (this.isCellInSection(cellId, sectionName)) {
        this.editingUsers[cellId].forEach(user => {
          if (user !== this.userName) {
            users.add(user);
          }
        });
      }
    }

    // A collapsed section hides the coloured cells, so surface who edited inside it.
    for (const cellId in this.remoteChangedCells) {
      const user = this.remoteChangedCells[cellId].user;
      if (user !== this.userName && this.isCellInSection(cellId, sectionName)) {
        users.add(user);
      }
    }

    return Array.from(users);
  }


  private lastResizerMouseDownTime = 0;

  onMouseDown(event: MouseEvent) {
    const now = Date.now();
    if (now - this.lastResizerMouseDownTime < 300) {
      this.lastResizerMouseDownTime = 0;
      return;
    }
    this.lastResizerMouseDownTime = now;

    if (!this.isCtrlPressed) {
      event.preventDefault();
    }
    const resizer = event.target as HTMLElement;
    const th = resizer.closest('th') as HTMLElement;

    if (!th) return;

    const table = th.closest('table') as HTMLTableElement;
    if (!table) return;

    const columnIndex = Array.from(th.parentElement!.children).indexOf(th);
    const startX = event.pageX;
    const startWidth = th.offsetWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
      this.applyColumnWidth(table, columnIndex, newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  onResizerDoubleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.lastResizerMouseDownTime = 0;

    const resizer = event.target as HTMLElement;
    const th = resizer.closest('th') as HTMLElement;
    if (!th) return;

    const table = th.closest('table') as HTMLTableElement;
    if (!table) return;

    const columnIndex = Array.from(th.parentElement!.children).indexOf(th);
    const padding = 24;
    const minWidth = 50;

    let maxWidth = this.measureElementContentWidth(th);

    Array.from(table.querySelectorAll('tbody tr')).forEach(row => {
      const cell = row.children[columnIndex] as HTMLElement;
      if (cell && cell.tagName === 'TD' && !cell.hasAttribute('colspan')) {
        maxWidth = Math.max(maxWidth, this.measureElementContentWidth(cell));
      }
    });

    this.applyColumnWidth(table, columnIndex, Math.max(minWidth, Math.ceil(maxWidth) + padding));
  }

  private applyColumnWidth(table: HTMLTableElement, columnIndex: number, width: number): void {
    const widthPx = `${width}px`;

    const headerRow = table.querySelector('thead tr:last-child');
    const headerCell = headerRow ? headerRow.children[columnIndex] as HTMLElement : null;
    if (headerCell && headerCell.tagName === 'TH') {
      headerCell.style.width = widthPx;
      headerCell.style.minWidth = widthPx;
      headerCell.style.maxWidth = widthPx;
    }

    Array.from(table.querySelectorAll('tbody tr')).forEach(row => {
      const cell = row.children[columnIndex] as HTMLElement;
      if (cell && cell.tagName === 'TD' && !cell.hasAttribute('colspan')) {
        cell.style.width = widthPx;
        cell.style.minWidth = widthPx;
        cell.style.maxWidth = widthPx;
      }
    });
  }

  private measureElementContentWidth(cell: HTMLElement): number {
    let maxWidth = 0;

    const input = cell.querySelector('input:not([type="checkbox"])') as HTMLInputElement | null;
    const textarea = cell.querySelector('textarea') as HTMLTextAreaElement | null;
    const select = cell.querySelector('select') as HTMLSelectElement | null;
    const progressBar = cell.querySelector('.progress-bar') as HTMLElement | null;

    if (input) {
      [input.value, input.placeholder].filter(Boolean).forEach(text => {
        maxWidth = Math.max(maxWidth, this.measureTextWidth(text, input));
      });
    }

    if (textarea) {
      const text = textarea.value || textarea.placeholder || '';
      text.split('\n').forEach(line => {
        maxWidth = Math.max(maxWidth, this.measureTextWidth(line, textarea));
      });
    }

    if (select) {
      Array.from(select.options).forEach(option => {
        maxWidth = Math.max(maxWidth, this.measureTextWidth(option.text, select));
      });
    }

    if (progressBar) {
      maxWidth = Math.max(maxWidth, this.measureTextWidth((progressBar.textContent || '').trim(), progressBar));
    }

    if (cell.tagName === 'TH') {
      const headerLabel = cell.querySelector('.resizable-th > span:first-of-type');
      if (headerLabel) {
        maxWidth = Math.max(maxWidth, this.measureTextWidth((headerLabel.textContent || '').trim(), headerLabel as HTMLElement));
      }
      maxWidth += 50;
    }

    Array.from(cell.querySelectorAll('a')).forEach(link => {
      maxWidth = Math.max(maxWidth, this.measureTextWidth((link.textContent || '').trim(), link as HTMLElement));
    });

    if (cell.querySelector('.image-upload-icons')) {
      maxWidth = Math.max(maxWidth, 80);
    }

    if (maxWidth === 0) {
      maxWidth = this.measureElementScrollWidth(cell);
    }

    return maxWidth;
  }

  private measureTextWidth(text: string, referenceElement: HTMLElement): number {
    if (!text) return 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text.length * 8;
    const style = window.getComputedStyle(referenceElement);
    context.font = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    return context.measureText(text).width;
  }

  private measureElementScrollWidth(cell: HTMLElement): number {
    const clone = cell.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.width = 'auto';
    clone.style.maxWidth = 'none';
    clone.style.whiteSpace = 'nowrap';
    clone.style.height = 'auto';
    Array.from(clone.querySelectorAll('input, select, textarea')).forEach(el => {
      (el as HTMLElement).style.width = 'auto';
    });

    document.body.appendChild(clone);
    const width = clone.scrollWidth;
    document.body.removeChild(clone);
    return width;
  }

  private lastRowResizerMouseDownTime = 0;

  onRowMouseDown(event: MouseEvent): void {
    const now = Date.now();
    if (now - this.lastRowResizerMouseDownTime < 300) {
      this.lastRowResizerMouseDownTime = 0;
      return;
    }
    this.lastRowResizerMouseDownTime = now;

    if (!this.isCtrlPressed) {
      event.preventDefault();
    }
    event.stopPropagation();

    const resizer = event.target as HTMLElement;
    const rowElement = resizer.closest('tr') as HTMLElement;
    if (!rowElement) {
      return;
    }

    const startY = event.pageY;
    const startHeight = rowElement.offsetHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newHeight = Math.max(30, startHeight + (moveEvent.pageY - startY));
      this.applyRowHeight(rowElement, newHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  onRowResizerDoubleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.lastRowResizerMouseDownTime = 0;

    const resizer = event.target as HTMLElement;
    const rowElement = resizer.closest('tr') as HTMLElement;
    if (!rowElement) {
      return;
    }

    const contentHeight = this.measureRowContentHeight(rowElement);
    this.applyRowHeight(rowElement, Math.max(30, contentHeight));
  }

  private applyRowHeight(rowElement: HTMLElement, height: number): void {
    const heightPx = `${height}px`;
    rowElement.style.height = heightPx;

    Array.from(rowElement.querySelectorAll('textarea')).forEach((ta) => {
      (ta as HTMLTextAreaElement).style.height = heightPx;
    });
  }

  private measureRowContentHeight(rowElement: HTMLElement): number {
    const clone = rowElement.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.width = rowElement.offsetWidth + 'px';
    clone.style.left = '-9999px';

    const resizer = clone.querySelector('.row-resizer');
    if (resizer && resizer.parentNode) {
      resizer.parentNode.removeChild(resizer);
    }

    Array.from(clone.querySelectorAll('textarea, input, select, td')).forEach((el) => {
      (el as HTMLElement).style.height = 'auto';
      (el as HTMLElement).style.minHeight = 'auto';
    });

    document.body.appendChild(clone);
    const height = clone.scrollHeight;
    document.body.removeChild(clone);
    return height + 8;
  }




  clientLogo(client: ClientDto) {
    if (!this.project.ProPakProject.Client.hasOwnProperty('logo'))
      return 'app/assets/images/project-placeholder.png';

    return '';
  }

  get activeCollaborators(): string[] {
    const fromCells = Object.values(this.editingUsers).reduce((acc: string[], val: string[]) => acc.concat(val || []), []);
    return Array.from(new Set<string>(
      [...this.pagePresenceUsers, ...fromCells].filter(user => !!user && user !== this.userName)
    ));
  }

  redoChange() {
    if (this.redoStack.length === 0) return;
    const nextChange = this.redoStack.pop();
    console.log('Redoing change:', nextChange);
    const row = this.readinessData.ReadinessFormData.find(x => x.ActualRowNumber === nextChange.rowNumber);
    this.onFieldChange(nextChange.rowNumber, nextChange.columnName, nextChange.oldValue, true)
    if (row && nextChange) {
      // Save current value to undo stack before reapplying
      this.undoStack.push({
        rowNumber: nextChange.rowNumber,
        columnName: nextChange.columnName,
        oldValue: nextChange.newValue,
        newValue: nextChange.oldValue
      });

      // Apply the redo value
      // row[nextChange.columnName] = nextChange.oldValue;
    }
  }

  undoChange() {
    if (this.undoStack.length === 0) return;
    const lastChange = this.undoStack.pop();

    console.log('Undoing change:', lastChange);
    const row = this.readinessData.ReadinessFormData.find(x => x.ActualRowNumber === lastChange.rowNumber);
    this.onFieldChange(lastChange.rowNumber, lastChange.columnName, lastChange.oldValue, true)
    if (row && lastChange) {
      // Save current value to redo stack before reverting
      this.redoStack.push({
        rowNumber: lastChange.rowNumber,
        columnName: lastChange.columnName,
        oldValue: lastChange.newValue,
        newValue: lastChange.oldValue
      });

      // Revert to old value
      // row[lastChange.columnName] = lastChange.oldValue;
    }
  }

  private hasUnsavedFormChanges(): boolean {
    const hasFieldChanges = Object.keys(this.changedFields).some(rowNumber =>
      Object.keys(this.changedFields[+rowNumber]).some(
        columnName => columnName !== 'Status' && this.changedFields[+rowNumber][columnName]
      )
    );
    return hasFieldChanges || this.rowDragged || this.isNewRowAdded;
  }

  private getCurrentChangeKey(): string {
    const keyRows = Object.keys(this.changedFields)
      .sort((a, b) => (+a) - (+b))
      .map(rowNumber => {
        const cols = this.changedFields[+rowNumber] || {};
        const colKey = Object.keys(cols)
          .sort()
          .map(c => c + ':' + (cols[c] ? '1' : '0'))
          .join(',');
        return rowNumber + '[' + colKey + ']';
      })
      .join('|');
    return keyRows + '|rowDragged:' + (this.rowDragged ? '1' : '0') + '|newRow:' + (this.isNewRowAdded ? '1' : '0');
  }

  private captureFocusState(): { el: HTMLElement | null; start?: number; end?: number; selectionRange?: Range } {
    const active = document.activeElement as HTMLElement | null;
    if (!active) {
      return { el: null };
    }
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      const start = active.selectionStart;
      const end = active.selectionEnd;
      return {
        el: active,
        start: start === null ? undefined : start,
        end: end === null ? undefined : end
      };
    }
    const sel = document.getSelection();
    if (sel && sel.rangeCount > 0 && active.isContentEditable) {
      return { el: active, selectionRange: sel.getRangeAt(0).cloneRange() };
    }
    return { el: active };
  }

  private restoreFocusState(state: { el: HTMLElement | null; start?: number; end?: number; selectionRange?: Range }): void {
    if (!state.el || typeof document.body.contains !== 'function' || !document.body.contains(state.el)) {
      return;
    }
    try {
      state.el.focus();
      if (
        (state.el instanceof HTMLInputElement || state.el instanceof HTMLTextAreaElement) &&
        state.start != null &&
        state.end != null
      ) {
        state.el.setSelectionRange(state.start, state.end);
      } else if (state.selectionRange && state.el.isContentEditable) {
        const sel = document.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(state.selectionRange);
        }
      }
    } catch (e) {
      /* ignore focus errors from detached controls */
    }
  }

  onAutoSaveToggle(): void {
    if (this.autoSaveEnabled) {
      this.autoSaveCountdownSec = this.autoSaveIntervalSec;
      this.autoSaveFailureCount = 0;
      this.startAutoSaveTimer();
    } else {
      this.stopAutoSaveTimer();
    }
    this.cdr.markForCheck();
  }

  private startAutoSaveTimer(): void {
    this.stopAutoSaveTimer();
    if (!this.autoSaveEnabled) {
      return;
    }
    this.autoSaveIntervalId = setInterval(() => {
      if (!this.autoSaveEnabled || this.isLoading) {
        return;
      }
      if (this.autoSaveInProgress || this.saveInProgress) {
        return;
      }
      if (this.autoSaveCountdownSec > 0) {
        this.autoSaveCountdownSec--;
      }
      if (this.autoSaveCountdownSec <= 0) {
        void this.runAutoSaveCycle();
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopAutoSaveTimer(): void {
    if (this.autoSaveIntervalId != null) {
      clearInterval(this.autoSaveIntervalId);
      this.autoSaveIntervalId = null;
    }
  }

  private async runAutoSaveCycle(): Promise<void> {
    if (!this.autoSaveEnabled || this.autoSaveInProgress || this.saveInProgress) {
      return;
    }
    if (!this.hasUnsavedFormChanges()) {
      this.autoSaveCountdownSec = this.autoSaveIntervalSec;
      this.autoSaveFailureCount = 0;
      this.cdr.markForCheck();
      return;
    }
    const currentChangeKey = this.getCurrentChangeKey();
    if (currentChangeKey === this.lastAutoSavedChangeKey) {
      this.autoSaveCountdownSec = this.autoSaveIntervalSec;
      this.cdr.markForCheck();
      return;
    }

    this.autoSaveInProgress = true;
    this.cdr.markForCheck();
    const focusState = this.captureFocusState();

    try {
      const ok = await this.saveAllTasks({ silent: true });
      if (ok) {
        this.autoSaveFailureCount = 0;
        this.autoSaveCountdownSec = this.autoSaveIntervalSec;
      } else {
        this.autoSaveCountdownSec = this.autoSaveIntervalSec;
      }
    } catch (error) {
      this.autoSaveFailureCount++;
      const message = this.extractValidationMessage(error);
      if (message) {
        this.notifySaveFailed(message, true);
        this.autoSaveFailureCount = 0;
        this.autoSaveCountdownSec = this.autoSaveIntervalSec;
      } else if (this.autoSaveFailureCount <= 1) {
        this.autoSaveCountdownSec = 30;
        this.pcs.notificationService.warning(
          'Auto-save',
          'Could not save. Retrying in 30 seconds.'
        );
      } else {
        this.autoSaveFailureCount = 0;
        this.autoSaveCountdownSec = this.autoSaveIntervalSec;
        this.pcs.notificationService.warning(
          'Auto-save',
          'Save failed. Will try again in 2 minutes.'
        );
      }
    } finally {
      this.autoSaveInProgress = false;
      setTimeout(() => this.restoreFocusState(focusState), 0);
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.routeSub = null;
    this.clearChangeBroadcastTimer();
    // Fully disconnect so server OnDisconnected clears presence and cell highlights for other users.
    this.signalRService.disconnect(this.userName);
    this.disablePageHorizontalScroll();
    this.stopAutoSaveTimer();
    document.removeEventListener('keydown', this.boundHandleKeyDown);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    this.timerService.stopIdleMonitor();
    if (this.timerService.isTimerRunning()) {
      this.timerService.stopTimer();
      this.pcs.apiService.LogTimeOut({ ProjectId: this.projectId, IsAutoRouted: this.isAutoRouted, TimeOut: this.timerService.lastActivity, AccessArea: ProjectAccessArea.Readiness } as any, false, false);
    }
  }

  private appScrollEl: HTMLElement | null = null;

  private enablePageHorizontalScroll() {
    this.appScrollEl = document.querySelector('.app-container') as HTMLElement | null;
    this.appScrollEl?.classList.add('rd-page-fixed');
    this.appScrollEl?.classList.remove('rd-page-hscroll');
  }

  private syncPageHorizontalScroll() {
    // Page is locked; table pane handles both scroll axes.
    if (!this.appScrollEl) {
      this.appScrollEl = document.querySelector('.app-container') as HTMLElement | null;
    }
    this.appScrollEl?.classList.add('rd-page-fixed');
    this.appScrollEl?.classList.remove('rd-page-hscroll');
  }

  private disablePageHorizontalScroll() {
    this.appScrollEl?.classList.remove('rd-page-fixed');
    this.appScrollEl?.classList.remove('rd-page-hscroll');
    this.appScrollEl = null;
  }

  // Image upload methods
  openImageUpload(row: any) {
    this.editTaskImage(row);
  }

  /** Edit / replace task image: open file picker, then preview + Select to save. */
  editTaskImage(row: any) {
    if (!row) {
      return;
    }
    this.selectedImageRow = row;
    this.tempImageBase64 = null;
    this.imageLoadFailed = false;
    const fileInput = document.getElementById('imageUploadInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  async onImageFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (images only)
    if (!file.type.startsWith('image/')) {
      this.pcs.notificationService.danger('Invalid File Type', 'Please select an image file only (PNG, JPG, GIF, etc.)');
      return;
    }

    // Convert file to base64
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      const base64String = e.target.result;

      // Show preview but DO NOT upload yet
      this.tempImageBase64 = base64String;
      this.imagePreviewUrl = base64String;
      this.showImagePreview = true;
      this.cdr.detectChanges();
    };

    reader.readAsDataURL(file);

    // Clear the input so same file can be selected again if needed
    event.target.value = '';
  }

  async saveImage() {
    if (!this.tempImageBase64 || !this.selectedImageRow) return;

    this.isUploadingImage = true;
    this.imageLoadFailed = false;
    try {
      const projectTaskId = this.selectedImageRow.ProjectTaskId;
      let savedUrl: string = null;

      // Prefer attachment API when the task is already persisted
      if (projectTaskId && projectTaskId > 0) {
        savedUrl = await this.replaceTaskImageAttachment(projectTaskId, this.tempImageBase64);
      }

      // Legacy fallback: upload image blob and set ImageBlobUrl on the row
      if (!savedUrl) {
        const response = await this.pcs.apiService.UploadReadinessImage({ ImageBase64: this.tempImageBase64 }, false, false);
        if (response && response.ImageUrl) {
          savedUrl = response.ImageUrl;
          this.selectedImageRow.ImageBlobUrl = response.ImageUrl;
          this.onFieldChange(this.selectedImageRow.ActualRowNumber, 'ImageBlobUrl', response.ImageUrl);
          const taskNumber = this.selectedImageRow.TaskNumber;
          if (taskNumber) {
            this.tasksWithUnsavedImages.add(taskNumber);
          }
        }
      }

      if (savedUrl) {
        const cacheBustUrl = savedUrl + (savedUrl.includes('?') ? '&' : '?') + 'v=' + Date.now();
        this.selectedImageRow._thumbnailUrl = cacheBustUrl;
        this.imagePreviewUrl = cacheBustUrl;
        this.tempImageBase64 = null;

        setTimeout(() => {
          this.cdr.detectChanges();
        }, 200);

        setTimeout(() => {
          this.closeImagePreview();
        }, 1500);
      } else {
        this.pcs.notificationService.danger('Upload Failed', 'Failed to save image. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      this.imageLoadFailed = true;
      this.pcs.notificationService.danger('Upload Failed', 'Failed to upload image. Please try again.');
    } finally {
      this.isUploadingImage = false;
      this.cdr.detectChanges();
    }
  }

  /** Replace the task-image attachment for a saved task; returns the new blob URL. */
  private async replaceTaskImageAttachment(projectTaskId: number, imageDataUrl: string): Promise<string | null> {
    const base64 = imageDataUrl.indexOf(',') >= 0 ? imageDataUrl.split(',')[1] : imageDataUrl;
    const deleteIds: number[] = [];

    try {
      const res = await this.pcs.apiService.executeCommandService.executeCommand(
        'GetReadinessTaskAttachments',
        { ProjectTaskId: projectTaskId },
        false,
        false
      ) as any;
      const payload = (res && res.Payload) ? res.Payload : res;
      const items = ((payload && payload.Attachments) ? payload.Attachments : (payload && payload.attachments) ? payload.attachments : []) as any[];
      items.forEach((x: any) => {
        if (x && x.IsTaskImage && x.ReadinessTaskAttachmentId) {
          deleteIds.push(x.ReadinessTaskAttachmentId);
        }
      });
    } catch (_) { }

    const saveResponse = await this.pcs.apiService.executeCommandService.executeCommand('SaveReadinessTaskAttachments', {
      ProjectTaskId: projectTaskId,
      NewAttachments: [{
        FileName: 'Task Image',
        FileType: 'image',
        Source: 'UserUpload',
        Base64Data: base64,
        IsTaskImage: true
      }],
      DeleteAttachmentIds: deleteIds
    }, false, false) as any;

    const savePayload = (saveResponse && saveResponse.Payload) ? saveResponse.Payload : saveResponse;
    const savedItems = ((savePayload && savePayload.Attachments) ? savePayload.Attachments : (savePayload && savePayload.attachments) ? savePayload.attachments : []) as any[];
    const taskImage = savedItems.find((x: any) => x && x.IsTaskImage && x.BlobUrl)
      || savedItems.find((x: any) => x && x.BlobUrl && (x.FileType || '').toLowerCase().startsWith('image'));

    if (taskImage && taskImage.BlobUrl) {
      this.selectedImageRow._thumbnailUrl = taskImage.BlobUrl;
      this.selectedImageRow._taskImageSource = 'UserUpload';
      this.selectedImageRow._attachmentCount = savedItems.length;
      const rowInData = this.readinessData && this.readinessData.ReadinessFormData
        && this.readinessData.ReadinessFormData.find((r: any) => r.ProjectTaskId === projectTaskId);
      if (rowInData) {
        (rowInData as any)._thumbnailUrl = taskImage.BlobUrl;
        (rowInData as any)._taskImageSource = 'UserUpload';
        (rowInData as any)._attachmentCount = savedItems.length;
      }
      this.pcs.notificationService.success('Saved', 'Task image updated.');
      return taskImage.BlobUrl as string;
    }
    return null;
  }

  async deleteImage(row?: any) {
    const targetRow = row || this.selectedImageRow;
    if (!targetRow || !this.hasImage(targetRow)) {
      return;
    }

    const confirmed = await this.pcs.confirm(
      'Are you sure you want to delete this task image?',
      'Delete image',
      { icon: 'warning', confirmButtonText: 'Delete' }
    );
    if (!confirmed) {
      return;
    }

    this.isDeletingImage = true;
    this.deletingImageRowNumber = targetRow.ActualRowNumber;
    try {
      const projectTaskId = targetRow.ProjectTaskId;
      const thumbnailUrl = (targetRow._thumbnailUrl || '').trim();
      const legacyImageUrl = (targetRow.ImageBlobUrl || '').trim();
      let deleted = false;

      if (projectTaskId && projectTaskId > 0) {
        const res = await this.pcs.apiService.executeCommandService.executeCommand(
          'GetReadinessTaskAttachments',
          { ProjectTaskId: projectTaskId },
          false,
          false
        ) as any;
        const payload = (res && res.Payload) ? res.Payload : res;
        const items = ((payload && payload.Attachments) ? payload.Attachments : (payload && payload.attachments) ? payload.attachments : []) as any[];
        const taskImageAtt = items.find((x: any) => x && x.IsTaskImage)
          || items.find((x: any) => x && x.BlobUrl && (x.BlobUrl === thumbnailUrl || x.BlobUrl === legacyImageUrl));

        if (taskImageAtt && taskImageAtt.ReadinessTaskAttachmentId) {
          await this.pcs.apiService.executeCommandService.executeCommand('SaveReadinessTaskAttachments', {
            ProjectTaskId: projectTaskId,
            NewAttachments: [],
            DeleteAttachmentIds: [taskImageAtt.ReadinessTaskAttachmentId]
          }, false, false);
          deleted = true;
        }
      }

      if (!deleted && legacyImageUrl) {
        const commandResponse: any = await this.pcs.apiService.executeCommandService.executeCommand(
          'DeleteReadinessImage',
          { ImageUrl: legacyImageUrl },
          true,
          false
        );
        const payload = commandResponse && commandResponse.Payload ? commandResponse.Payload : null;
        deleted = !!(payload && payload.IsDeleted);
        if (!deleted) {
          this.pcs.notificationService.warning('Delete Image', 'Image was not found in blob storage.');
        }
      }

      this.clearTaskImageFromRow(targetRow);

      if (this.selectedImageRow && this.selectedImageRow.ActualRowNumber === targetRow.ActualRowNumber) {
        this.tempImageBase64 = null;
        this.imagePreviewUrl = '';
        this.imageLoadFailed = false;
        this.showImagePreview = false;
      }

      this.pcs.notificationService.success('Delete Image', 'Task image deleted successfully.');
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error deleting image:', error);
      this.pcs.notificationService.danger('Delete Failed', 'Failed to delete task image. Please try again.');
    } finally {
      this.isDeletingImage = false;
      this.deletingImageRowNumber = null;
    }
  }

  private clearTaskImageFromRow(targetRow: any): void {
    targetRow.ImageBlobUrl = '';
    targetRow._thumbnailUrl = '';
    targetRow._taskImageSource = null;
    if (targetRow._attachmentCount > 0) {
      targetRow._attachmentCount--;
    }
    this.syncImageBlobUrlAsPersisted(targetRow.ActualRowNumber, '');

    if (targetRow.TaskNumber) {
      this.tasksWithUnsavedImages.delete(targetRow.TaskNumber);
    }

    if (this.selectedAttachmentRow && this.selectedAttachmentRow.ActualRowNumber === targetRow.ActualRowNumber) {
      const taskImageIdx = this.attachmentList.findIndex(a => a.isTaskImage);
      if (taskImageIdx >= 0) {
        const att = this.attachmentList[taskImageIdx];
        if (att.file && (att as any).objectUrl) {
          URL.revokeObjectURL((att as any).objectUrl);
        }
        this.attachmentList.splice(taskImageIdx, 1);
      }
    }
  }

  private syncImageBlobUrlAsPersisted(rowNumber: number, imageBlobUrl: string): void {
    const originalRow = this.originalReadinessFormData
      ? this.originalReadinessFormData.find(x => x.ActualRowNumber === rowNumber)
      : null;
    if (originalRow) {
      originalRow.ImageBlobUrl = imageBlobUrl;
    }

    if (this.changedFields && this.changedFields[rowNumber]) {
      this.changedFields[rowNumber]['ImageBlobUrl'] = false;
      const hasAnyDirty = Object.keys(this.changedFields[rowNumber]).some(
        columnName => columnName !== 'Status' && this.changedFields[rowNumber][columnName]
      );
      if (!hasAnyDirty) {
        delete this.changedFields[rowNumber];
      }
    }

    this.undoStack = (this.undoStack || []).filter(x => x.columnName !== 'ImageBlobUrl' || x.rowNumber !== rowNumber);
    this.redoStack = (this.redoStack || []).filter(x => x.columnName !== 'ImageBlobUrl' || x.rowNumber !== rowNumber);
    this.lastAutoSavedChangeKey = this.getCurrentChangeKey();
  }

  showImagePopup(row: any) {
    const imageUrl = row._thumbnailUrl || row.ImageBlobUrl;
    if (imageUrl) {
      this.tempImageBase64 = null;
      this.imageLoadFailed = false;
      this.imagePreviewUrl = imageUrl;
      this.showImagePreview = true;
      this.selectedImageRow = row;
      this.cdr.detectChanges();
    } else {
      // If no image, open the attachment folder
      this.openAttachmentFolder(row);
    }
  }

  private safeUrlCache: { [url: string]: SafeUrl } = {};
  getSafeUrl(url: string): SafeUrl | string {
    if (!url) return '';
    if (this.safeUrlCache[url]) {
      return this.safeUrlCache[url];
    }
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    this.safeUrlCache[url] = safeUrl;
    return safeUrl;
  }

  closeImagePreview() {
    this.showImagePreview = false;
    this.imagePreviewUrl = '';
    this.tempImageBase64 = null;
    this.imageLoadFailed = false;
  }

  onImageLoad() {
    // Image loaded successfully
    this.imageLoadFailed = false;
    this.cdr.detectChanges();
  }

  onImageError() {
    // Image failed to load - retry with cache buster if not already done
    if (this.imagePreviewUrl && !this.imagePreviewUrl.includes('retryCount')) {
      // Add a retry parameter and try again
      const retryUrl = this.imagePreviewUrl + (this.imagePreviewUrl.includes('?') ? '&' : '?') + 'retryCount=1&t=' + Date.now();
      this.imagePreviewUrl = retryUrl;
      this.imageLoadFailed = false;
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 500);
    } else {
      // Already retried, show error
      this.imageLoadFailed = true;
      this.cdr.detectChanges();
    }
  }

  startDraggingImage(event: MouseEvent) {
    this.isDraggingImage = true;
    this.dragOffset.x = event.clientX - this.imagePreviewPosition.x;
    this.dragOffset.y = event.clientY - this.imagePreviewPosition.y;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMoveImage(event: MouseEvent) {
    if (this.isDraggingImage) {
      this.imagePreviewPosition.x = event.clientX - this.dragOffset.x;
      this.imagePreviewPosition.y = event.clientY - this.dragOffset.y;
    }
  }

  @HostListener('document:mouseup')
  stopDraggingImage() {
    this.isDraggingImage = false;
  }

  hasImage(row: any): boolean {
    if (!row) {
      return false;
    }
    const thumbnailUrl = row._thumbnailUrl && row._thumbnailUrl.trim();
    const legacyImageUrl = row.ImageBlobUrl && row.ImageBlobUrl.trim();
    return !!(thumbnailUrl || legacyImageUrl);
  }

  getRowAttachments(row: any): { fileName: string; url: string; fileType: string }[] {
    const list: { fileName: string; url: string; fileType: string }[] = [];
    if (row.ImageBlobUrl && row.ImageBlobUrl.trim() !== '') {
      list.push({ fileName: 'Image', url: row.ImageBlobUrl, fileType: 'image' });
    }
    if (row.AttachmentsJson) {
      try {
        const parsed = JSON.parse(row.AttachmentsJson) as { fileName?: string; url?: string; fileType?: string }[];
        if (Array.isArray(parsed)) list.push(...parsed.filter(x => x && x.url).map(x => ({ fileName: x.fileName || 'File', url: x.url, fileType: x.fileType || 'file' })));
      } catch (_) { }
    }
    return list;
  }

  hasAttachments(row: any): boolean {
    return this.getAttachmentCount(row) > 0;
  }

  isTaskNumberColumn(column: any): boolean {
    return !!(column && column.ColumnName === 'TaskNumber');
  }

  getTaskAttachmentTitle(row: any): string {
    if (this.hasAttachments(row)) {
      return 'View/Edit Attachments (' + this.getAttachmentCount(row) + ')';
    }
    return 'Add Attachments';
  }

  /** Number of attachments to show for this row (from mps.ReadinessTaskAttachment or legacy). */
  getAttachmentCount(row: any): number {
    if (row._attachmentCount != null) return row._attachmentCount;
    return this.getRowAttachments(row).length;
  }

  /** Load attachment counts from API and set row._attachmentCount for each task. Call after readiness form data is loaded. */
  async loadAttachmentCountsForReadinessData() {
    if (!this.readinessData || !this.readinessData.ReadinessFormData || !this.readinessData.ReadinessFormData.length) return;
    const projectTaskIds = this.readinessData.ReadinessFormData
      .map((r: any) => r.ProjectTaskId)
      .filter((id: number) => id != null && id > 0);
    if (projectTaskIds.length === 0) return;
    try {
      const res = await this.pcs.apiService.executeCommandService.executeCommand('GetReadinessTaskAttachmentCounts', { ProjectTaskIds: projectTaskIds }, false, false) as any;
      const payload = (res && res.Payload != null) ? res.Payload : res;
      const counts = ((payload && payload.Counts) || (payload && payload.counts) || []) as { ProjectTaskId?: number; Count?: number }[];
      const countByTaskId: Record<string, number> = {};
      counts.forEach((c: any) => {
        const taskId = c.ProjectTaskId != null ? c.ProjectTaskId : c.projectTaskId;
        const cnt = c.Count != null ? c.Count : (c.count != null ? c.count : 0);
        countByTaskId[String(taskId)] = cnt;
      });
      this.readinessData.ReadinessFormData.forEach((row: any) => {
        const id = row.ProjectTaskId;
        row._attachmentCount = (id != null && id > 0) ? (countByTaskId[String(id)] !== undefined ? countByTaskId[String(id)] : 0) : undefined;
      });
      this.cdr.detectChanges();
    } catch (_) { }
  }

  /** Fetch the latest Task Image URL from ProPak for a given TaskNumber. Returns null if unavailable. */
  private async getLatestProPakTaskImageUrl(taskNumber: string): Promise<string | null> {
    if (!this.projectId || !taskNumber) return null;
    try {
      const response = await this.pcs.apiService.executeCommandService.executeCommand(
        'GetWorkPackTaskListReference',
        { ProPakProjectId: this.projectId, TaskNumber: taskNumber },
        false, false
      ) as any;
      const payload = (response && response.Payload) ? response.Payload : null;
      if (payload && payload.TaskImageUrl) return payload.TaskImageUrl as string;
    } catch (_) { }
    return null;
  }

  /** Returns true when the attachment item looks like an image (by FileType or FileName extension). */
  private isImageAttachmentItem(x: any): boolean {
    if (!x || !x.BlobUrl) return false;
    const ft = (x.FileType || '').toLowerCase();
    if (ft.startsWith('image')) return true;
    const name = (x.FileName || '').toLowerCase();
    return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
  }

  /** Load the task image attachment URL per task for inline thumbnails. */
  async loadThumbnailsForReadinessData() {
    if (!this.readinessData || !this.readinessData.ReadinessFormData) return;
    const rowsWithAttachments = this.readinessData.ReadinessFormData.filter(
      (r: any) => (r._attachmentCount > 0 || (r.ImageBlobUrl && r.ImageBlobUrl.trim() !== '')) && r.ProjectTaskId > 0
    );
    for (const row of rowsWithAttachments) {
      let chosenUrl: string | null = null;
      let chosenSource: string | null = null;

      // Always check ReadinessTaskAttachment list first so we can detect the source of whichever
      // image we ultimately show in the thumbnail (Task Image flag preferred, else first image).
      if ((row as any)._attachmentCount > 0) {
        try {
          const res = await this.pcs.apiService.executeCommandService.executeCommand(
            'GetReadinessTaskAttachments', { ProjectTaskId: row.ProjectTaskId }, false, false
          ) as any;
          const payload = (res && res.Payload) ? res.Payload : res;
          const items = ((payload && payload.Attachments) ? payload.Attachments : (payload && payload.attachments) ? payload.attachments : []) as any[];
          const taskImage = items.find((x: any) => x && x.BlobUrl && x.IsTaskImage);
          const fallbackImage = !taskImage ? items.find((x: any) => this.isImageAttachmentItem(x)) : null;
          const picked = taskImage || fallbackImage;
          if (picked) {
            chosenSource = picked.Source || null;
            chosenUrl = picked.BlobUrl;
          }
        } catch (_) { }
      }

      // Fallback to legacy ImageBlobUrl on the row when no image attachment was found.
      if (!chosenUrl && row.ImageBlobUrl && row.ImageBlobUrl.trim() !== '') {
        chosenUrl = row.ImageBlobUrl;
      }

      // When the picked image is sourced from ProPak, always refresh with the latest URL straight from ProPak.
      if (chosenSource === 'ProPak' && (row as any).TaskNumber) {
        const latest = await this.getLatestProPakTaskImageUrl((row as any).TaskNumber);
        if (latest) chosenUrl = latest;
      }

      (row as any)._taskImageSource = chosenSource;
      if (chosenUrl) {
        (row as any)._thumbnailUrl = chosenUrl;
      }
    }
    this.cdr.detectChanges();
  }

  async openAttachmentFolder(row: any) {
    this.selectedAttachmentRow = row;
    this.fetchProPakImage = true;
    this.attachmentIdsToDelete = [];
    this.workPackTaskReference = null;
    this.workPackTaskImageLoaded = false;
    this.workPackTaskImageError = false;
    this.attachmentLoadWorkPackRef = false;
    this.isLoadingWorkPackReference = false;
    this.isLoadingAttachments = true;
    this.showAttachmentModal = true;
    this.attachmentList = [];
    this.cdr.detectChanges();

    const projectTaskId = row.ProjectTaskId;
    if (projectTaskId) {
      try {
        const res = await this.pcs.apiService.executeCommandService.executeCommand('GetReadinessTaskAttachments', { ProjectTaskId: projectTaskId }, false, false) as any;
        const payload = (res && res.Payload) ? res.Payload : res;
        const items = ((payload && payload.Attachments) ? payload.Attachments : (payload && payload.attachments) ? payload.attachments : []) as { ReadinessTaskAttachmentId?: number; FileName?: string; BlobUrl?: string; FileType?: string; Source?: string }[];
        this.attachmentList = items.map(x => ({
          id: x.ReadinessTaskAttachmentId,
          fileName: x.FileName || 'File',
          blobUrl: x.BlobUrl,
          fileType: x.FileType || this.guessMimeTypeFromName(x.FileName),
          source: x.Source,
          loading: (x.FileType || '').toLowerCase().startsWith('image'),
          loadError: false,
          isTaskImage: !!(x as any).IsTaskImage
        }));
        if (this.selectedAttachmentRow) this.selectedAttachmentRow._attachmentCount = this.attachmentList.length;

        // For ANY attachment sourced from ProPak (task image flag or not), refresh its URL with the
        // latest one from ProPak so the popup preview / attachment list uses the up-to-date image
        // instead of a (potentially expired) stored URL.
        const hasProPakAttachment = this.attachmentList.some(a => a.source === 'ProPak');
        if (hasProPakAttachment && row.TaskNumber) {
          const latest = await this.getLatestProPakTaskImageUrl(row.TaskNumber);
          if (latest) {
            this.attachmentList = this.attachmentList.map(a =>
              a.source === 'ProPak' ? { ...a, blobUrl: latest, loadError: false } : a
            );
            // Also keep the inline thumbnail on the row in sync with the latest ProPak URL
            // when the task image itself is ProPak-sourced.
            const proPakTaskImage = this.attachmentList.find(a => a.isTaskImage && a.source === 'ProPak');
            if (proPakTaskImage && this.selectedAttachmentRow) {
              this.selectedAttachmentRow._thumbnailUrl = latest;
              this.selectedAttachmentRow._taskImageSource = 'ProPak';
            }
          }
        }
      } catch (_) {
        this.attachmentList = [];
      }
    }
    this.isLoadingAttachments = false;
    if (this.fetchProPakImage && this.projectId && row.TaskNumber) {
      this.loadWorkPackTaskReference();
    }
    this.cdr.detectChanges();
  }

  closeAttachmentModal() {
    this.attachmentList.forEach(att => { if (att.file && (att as any).objectUrl) URL.revokeObjectURL((att as any).objectUrl); });
    this.showAttachmentModal = false;
    this.selectedAttachmentRow = null;
    this.attachmentList = [];
    this.attachmentIdsToDelete = [];
    this.workPackTaskReference = null;
    this.isLoadingWorkPackReference = false;
    this.cdr.detectChanges();
  }

  async loadWorkPackTaskReference() {
    if (!this.fetchProPakImage || this.attachmentLoadWorkPackRef || !this.projectId || !(this.selectedAttachmentRow && this.selectedAttachmentRow.TaskNumber)) return;
    this.attachmentLoadWorkPackRef = true;
    this.isLoadingWorkPackReference = true;
    this.workPackTaskImageLoaded = false;
    this.workPackTaskImageError = false;
    try {
      const response = await this.pcs.apiService.executeCommandService.executeCommand('GetWorkPackTaskListReference', { ProPakProjectId: this.projectId, TaskNumber: this.selectedAttachmentRow.TaskNumber }, false, false) as any;
      if (response && response.Payload && (response.Payload.TaskId || response.Payload.WorkPackNumber)) {
        this.workPackTaskReference = {
          workPackNumber: response.Payload.WorkPackNumber,
          taskNumber: response.Payload.TaskNumber,
          revision: response.Payload.Revision,
          taskImageUrl: response.Payload.TaskImageUrl
        };
        if (this.workPackTaskReference.taskImageUrl) {
          this.workPackTaskImageLoaded = false;
        } else {
          this.workPackTaskImageLoaded = true;
        }
      }
    } catch (_) { }
    finally {
      this.isLoadingWorkPackReference = false;
      this.cdr.detectChanges();
    }
  }

  onWorkPackTaskImageLoad() {
    this.workPackTaskImageLoaded = true;
    this.workPackTaskImageError = false;
    this.cdr.detectChanges();
  }

  onWorkPackTaskImageError() {
    this.workPackTaskImageLoaded = true;
    this.workPackTaskImageError = true;
    this.cdr.detectChanges();
  }

  onProPakToggleChange() {
    if (this.fetchProPakImage && this.projectId && (this.selectedAttachmentRow && this.selectedAttachmentRow.TaskNumber)) {
      // Force a fresh backend fetch whenever toggle turns ON again.
      this.attachmentLoadWorkPackRef = false;
      this.workPackTaskReference = null;
      this.loadWorkPackTaskReference();
    } else if (!this.fetchProPakImage) {
      // Reset ProPak reference when toggle is OFF
      this.attachmentLoadWorkPackRef = false;
      this.workPackTaskReference = null;
      this.workPackTaskImageLoaded = false;
      this.workPackTaskImageError = false;
    }
    this.cdr.detectChanges();
  }

  addProPakImageAsTaskImage() {
    if (!(this.workPackTaskReference && this.workPackTaskReference.taskImageUrl)) return;
    // Remove any existing task image from the list
    const existingIdx = this.attachmentList.findIndex(a => a.isTaskImage);
    if (existingIdx >= 0) {
      this.removeAttachment(existingIdx);
    }
    this.attachmentList.push({
      fileName: 'Task Image',
      blobUrl: this.workPackTaskReference.taskImageUrl,
      fileType: 'image',
      source: 'ProPak',
      isTaskImage: true
    });
    this.cdr.detectChanges();
  }

  addProPakImageToAttachments() {
    if (!(this.workPackTaskReference && this.workPackTaskReference.taskImageUrl)) return;
    const alreadyAdded = this.attachmentList.some(a => a.source === 'ProPak' && a.blobUrl === this.workPackTaskReference.taskImageUrl);
    if (alreadyAdded) {
      this.pcs.notificationService.info('Already added', 'ProPak task image is already in the list.');
      return;
    }
    this.attachmentList.push({
      fileName: 'ProPak Task Image',
      blobUrl: this.workPackTaskReference.taskImageUrl,
      fileType: 'image',
      source: 'ProPak'
    });
    this.cdr.detectChanges();
  }

  triggerTaskImageUpload() {
    const el = document.getElementById('taskImageFileInput') as HTMLInputElement;
    if (el) el.click();
  }

  // Drag and Drop Handlers for Task Image
  onDragEnterTaskImageZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverTaskImageZone = true;
  }

  onDragOverTaskImageZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverTaskImageZone = true;
  }

  onDragLeaveTaskImageZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (target.classList.contains('task-image-dropzone')) {
      this.isDragOverTaskImageZone = false;
    }
  }

  onDropTaskImageZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverTaskImageZone = false;

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Process only the first file as task image (single image)
    const file = files[0];
    const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    
    if (!allowedImageTypes.includes(file.type) && !file.name.match(/\.(png|jpg|jpeg|gif|webp)$/i)) {
      this.pcs.notificationService.danger('Invalid file', 'Only image files (PNG, JPG, JPEG, GIF, WEBP) are allowed for task image.');
      return;
    }

    // Read the file and set as task image
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.tempImageBase64 = e.target.result;
      this.selectedImageRow = this.selectedAttachmentRow;
      this.imagePreviewUrl = this.tempImageBase64;
      this.cdr.detectChanges();
    };
    reader.onerror = () => {
      this.pcs.notificationService.danger('Error', 'Failed to read the image file.');
    };
    reader.readAsDataURL(file);
  }

  onTaskImageFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.pcs.notificationService.danger('Invalid file', 'Please select an image file (PNG, JPG, GIF, etc.).');
      input.value = '';
      return;
    }
    // Remove any existing task image from the list
    const existingIdx = this.attachmentList.findIndex(a => a.isTaskImage);
    if (existingIdx >= 0) {
      this.removeAttachment(existingIdx);
    }
    const objectUrl = URL.createObjectURL(file);
    this.attachmentList.push({
      fileName: file.name,
      fileType: file.type,
      source: 'UserUpload',
      file,
      isTaskImage: true,
      ...({ objectUrl } as any)
    });
    input.value = '';
    this.cdr.detectChanges();
  }

  hasTaskImage(): boolean {
    return this.attachmentList.some(a => a.isTaskImage);
  }

  getTaskImageUrl(): string {
    const att = this.attachmentList.find(a => a.isTaskImage);
    if (!att) return '';
    return att.blobUrl || (att as any).objectUrl || '';
  }

  getTaskImageSource(): string {
    const att = this.attachmentList.find(a => a.isTaskImage);
    if (!att) return '';
    if (att.source === 'ProPak') return 'ProPak';
    return 'ClientDash';
  }

  removeTaskImage() {
    const idx = this.attachmentList.findIndex(a => a.isTaskImage);
    if (idx >= 0) {
      this.removeAttachment(idx);
    }
  }

  previewTaskImageInPopup() {
    const url = this.getTaskImageUrl();
    if (url) {
      this.tempImageBase64 = null;
      this.imageLoadFailed = false;
      this.imagePreviewUrl = url;
      this.showImagePreview = true;
      this.selectedImageRow = this.selectedAttachmentRow;
      this.cdr.detectChanges();
    }
  }

  onAttachmentThumbnailLoad(index: number) {
    if (this.attachmentList[index]) {
      this.attachmentList[index].loading = false;
      this.attachmentList[index].loadError = false;
      this.cdr.detectChanges();
    }
  }

  onAttachmentThumbnailError(index: number) {
    if (this.attachmentList[index]) {
      this.attachmentList[index].loading = false;
      this.attachmentList[index].loadError = true;
      this.cdr.detectChanges();
    }
  }

  onAttachmentFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|xlsx|csv)$/i)) {
        this.pcs.notificationService.danger('Invalid file', `"${file.name}" is not allowed. Use images, PDF, XLSX, or CSV.`);
        continue;
      }
      const fileType = file.type || this.guessMimeTypeFromName(file.name);
      const objectUrl = URL.createObjectURL(file);
      this.attachmentList.push({
        fileName: file.name,
        fileType,
        source: 'UserUpload',
        file,
        ...({ objectUrl } as any)
      });
    }
    input.value = '';
    this.cdr.detectChanges();
  }

  removeAttachment(index: number) {
    const att = this.attachmentList[index];
    if (att.id != null) this.attachmentIdsToDelete.push(att.id);
    if (att.file && (att as any).objectUrl) URL.revokeObjectURL((att as any).objectUrl);
    this.attachmentList.splice(index, 1);
    this.cdr.detectChanges();
  }

  async saveAttachmentsToRow() {
    if (!(this.selectedAttachmentRow && this.selectedAttachmentRow.ProjectTaskId)) {
      this.pcs.notificationService.danger('Cannot save', 'Task must be saved first.');
      return;
    }
    const projectTaskId = this.selectedAttachmentRow.ProjectTaskId as number;
    const newAttachments: { FileName: string; FileType: string; Source: string; Base64Data?: string; BlobUrl?: string; IsTaskImage?: boolean }[] = [];
    for (const att of this.attachmentList) {
      if (att.id != null) continue;
      if (att.source === 'ProPak' && att.blobUrl) {
        newAttachments.push({ FileName: att.fileName, FileType: att.fileType, Source: 'ProPak', BlobUrl: att.blobUrl, IsTaskImage: !!att.isTaskImage });
        continue;
      }
      if (att.file) {
        const base64 = await this.fileToBase64(att.file);
        newAttachments.push({ FileName: att.fileName, FileType: att.fileType, Source: 'UserUpload', Base64Data: base64, IsTaskImage: !!att.isTaskImage });
      }
    }
    this.isUploadingAttachment = true;
    this.cdr.detectChanges();
    try {
      const saveResponse = await this.pcs.apiService.executeCommandService.executeCommand('SaveReadinessTaskAttachments', {
        ProjectTaskId: projectTaskId,
        NewAttachments: newAttachments,
        DeleteAttachmentIds: this.attachmentIdsToDelete
      }, false, false) as any;
      const payload = (saveResponse && saveResponse.Payload) ? saveResponse.Payload : saveResponse;
      const savedItems = ((payload && payload.Attachments) ? payload.Attachments : (payload && payload.attachments) ? payload.attachments : []) as any[];
      if (savedItems.length > 0) {
        this.attachmentList = savedItems.map(x => ({
          id: x.ReadinessTaskAttachmentId,
          fileName: x.FileName || 'File',
          blobUrl: x.BlobUrl,
          fileType: x.FileType || this.guessMimeTypeFromName(x.FileName),
          source: x.Source,
          loading: false,
          loadError: false,
          isTaskImage: !!x.IsTaskImage
        }));
      }
      const newCount = this.attachmentList.length;
      this.selectedAttachmentRow._attachmentCount = newCount;
      // Update thumbnail: prefer task image, then first image
      const taskImageAtt = this.attachmentList.find(a => !!a.isTaskImage && !!a.blobUrl);
      const firstImage = taskImageAtt || this.attachmentList.find(a => (a.fileType || '').toLowerCase().startsWith('image') && !!a.blobUrl);
      this.selectedAttachmentRow._thumbnailUrl = firstImage ? (firstImage.blobUrl || '') : '';
      const rowInData = this.readinessData && this.readinessData.ReadinessFormData && this.readinessData.ReadinessFormData.find((r: any) => r.ProjectTaskId === projectTaskId);
      if (rowInData) {
        (rowInData as any)._attachmentCount = newCount;
        (rowInData as any)._thumbnailUrl = this.selectedAttachmentRow._thumbnailUrl;
      }
      this.closeAttachmentModal();
      this.pcs.notificationService.success('Saved', 'Attachments saved.');
    } catch (err) {
      this.pcs.notificationService.danger('Save failed', 'Could not save attachments.');
    } finally {
      this.isUploadingAttachment = false;
      this.cdr.detectChanges();
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.indexOf(',') >= 0 ? dataUrl.split(',')[1] : dataUrl;
        resolve(base64 || '');
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  //private guessMimeTypeFromName(name: string): string {
  //  const n = (name || '').toLowerCase();
  //  if (n.endsWith('.png')) return 'image/png';
  //  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  //  if (n.endsWith('.gif')) return 'image/gif';
  //  if (n.endsWith('.webp')) return 'image/webp';
  //  if (n.endsWith('.pdf')) return 'application/pdf';
  //  if (n.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  //  if (n.endsWith('.csv')) return 'text/csv';
  //  return 'application/octet-stream';
  //}

  private guessMimeTypeFromName(name: string): string {
    const ext = (name || '').split('.').pop().toLowerCase();

    const map: { [key: string]: string } = {
      png: 'png',
      jpg: 'jpg',
      jpeg: 'jpg',
      gif: 'gif',
      webp: 'webp',
      pdf: 'pdf',
      xlsx: 'xlsx',
      csv: 'csv'
    };

    return map[ext || ''] || '';
  }

  isPreviewableFile(fileType: string): boolean {
    return (fileType || '').startsWith('image');
  }

  getAttachmentIcon(att: AttachmentListItem): any {
    if ((att.fileType || '').startsWith('image')) return this.faImage;
    const n = (att.fileName || '').toLowerCase();
    if (n.endsWith('.pdf')) return this.faFilePdf;
    if (n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.csv')) return this.faFileExcel;
    if (n.endsWith('.doc') || n.endsWith('.docx')) return this.faFileWord;
    return this.faFile;
  }

  getAttachmentIconClass(att: AttachmentListItem): string {
    if ((att.fileType || '').startsWith('image')) return 'attachment-icon-image';
    const n = (att.fileName || '').toLowerCase();
    if (n.endsWith('.pdf')) return 'attachment-icon-pdf';
    if (n.endsWith('.xlsx') || n.endsWith('.xls')) return 'attachment-icon-excel';
    if (n.endsWith('.csv')) return 'attachment-icon-csv';
    if (n.endsWith('.doc') || n.endsWith('.docx')) return 'attachment-icon-word';
    return 'attachment-icon-file';
  }

  getAttachmentOpenUrl(att: any): string | null {
    if (att.blobUrl) return att.blobUrl;
    if (att.url) return att.url;
    if (att.file) return (att as any).objectUrl || URL.createObjectURL(att.file);
    return null;
  }

  openAttachment(att: any) {
    const url = this.getAttachmentOpenUrl(att);
    if (!url) return;

    if (url.startsWith('http://') || url.startsWith('https://')) {
      const lowerUrl = url.toLowerCase();
      const isImage = (att.fileType && att.fileType.startsWith('image')) || lowerUrl.match(/\.(jpeg|jpg|gif|png|webp|bmp)$/i) != null;

      if (isImage) {
        const html = `<html><head><title>Image Preview</title><style>body { margin: 0; display: flex; justify-content: center; align-items: center; background-color: #222; height: 100vh; } img { max-width: 100%; max-height: 100%; object-fit: contain; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }</style></head><body><img src="${url}" alt="Attachment"></body></html>`;
        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      } else if (lowerUrl.match(/\.(xlsx|xls|doc|docx)$/i) != null) {
        // Office Viewer works flawlessly for Excel/Word, but CANNOT parse CSV files
        const officeViewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
        window.open(officeViewerUrl, '_blank');
      } else if (lowerUrl.match(/\.(pdf)$/i) != null) {
        // Google Viewer securely previews PDFs in a new tab without forcing a download
        window.open(this.getGoogleViewerUrl(url), '_blank');
      } else {
        // CSV files and unknown extensions will bypass viewers and download natively
        window.open(url, '_blank');
      }
    } else {
      window.open(url, '_blank');
    }
  }

  private getGoogleViewerUrl(rawUrl: string): string {
    if (!rawUrl) return '#';
    if (rawUrl.includes('docs.google.com/gview')) return rawUrl;
    return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(rawUrl)}`;
  }

  private async openUrlAsPreview(url: string, fileType: string) {
    try {
      const response = await fetch(url, {
        headers: { 'Accept': '*/*' }
      });

      if (!response.ok) {
        console.warn(`Failed to fetch attachment (${response.status}): ${url}`);
        this.pcs.notificationService.danger('Error', `Failed to load attachment: ${response.statusText}`);
        return;
      }

      const blob = await response.blob();

      let mimeType = blob.type || 'application/octet-stream';

      const contentType = response.headers.get('content-type');
      if (contentType) {
        mimeType = contentType.split(';')[0].trim(); // Remove charset if present
      }

      if (!mimeType || mimeType === 'application/octet-stream') {
        const lowerUrl = url.toLowerCase();
        if (fileType === 'image' || lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg')) {
          mimeType = 'image/jpeg';
        } else if (lowerUrl.includes('.png')) {
          mimeType = 'image/png';
        } else if (lowerUrl.includes('.gif')) {
          mimeType = 'image/gif';
        } else if (lowerUrl.includes('.webp')) {
          mimeType = 'image/webp';
        } else if (lowerUrl.includes('.pdf')) {
          mimeType = 'application/pdf';
        } else if (lowerUrl.includes('.xlsx')) {
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (lowerUrl.includes('.xls')) {
          mimeType = 'application/vnd.ms-excel';
        } else if (lowerUrl.includes('.csv')) {
          mimeType = 'text/csv';
        }
      }

      const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      window.open(blobUrl, '_blank');

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 10000);
    } catch (error) {
      console.error('Error opening attachment as preview:', error);
      this.pcs.notificationService.danger('Error', `Failed to open attachment: ${error.message}`);
    }
  }

  openAttachmentUrl(url: string) {
    window.open(url, '_blank');
  }

  triggerAttachmentFileInput() {
    const el = document.getElementById('attachmentFileInput') as HTMLInputElement;
    if (el) el.click();
  }

  triggerAttachmentFolderInput() {
    const el = document.getElementById('attachmentFolderInput') as HTMLInputElement;
    if (el) el.click();
  }

  // Drag and Drop Handlers for Attachments
  onDragEnterAttachmentZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverAttachmentZone = true;
  }

  onDragOverAttachmentZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverAttachmentZone = true;
  }

  onDragLeaveAttachmentZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Only set to false if leaving the dropzone completely
    const target = event.target as HTMLElement;
    if (target.classList.contains('attachment-dropzone')) {
      this.isDragOverAttachmentZone = false;
    }
  }

  onDropAttachmentZone(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverAttachmentZone = false;

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Process dropped files using the same logic as file input
    this.processAttachmentFiles(files);
  }

  /** Process attachment files from either file input or drag-and-drop */
  private processAttachmentFiles(files: FileList) {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|xlsx|csv)$/i)) {
        this.pcs.notificationService.danger('Invalid file', `"${file.name}" is not allowed. Use images, PDF, XLSX, or CSV.`);
        continue;
      }

      const fileType = file.type || this.guessMimeTypeFromName(file.name);
      const objectUrl = URL.createObjectURL(file);
      this.attachmentList.push({
        fileName: file.name,
        fileType,
        source: 'UserUpload',
        file,
        ...({ objectUrl } as any)
      });
    }

    this.cdr.detectChanges();
  }

  private pidLinksCache = new Map<string, { pidName: string; url: string; href: string }[]>();

  getPidAttachmentLinks(pidAttachmentUrl: string): { pidName: string; url: string; href: string }[] {
    if (!pidAttachmentUrl) return [];

    if (this.pidLinksCache.has(pidAttachmentUrl)) {
      return this.pidLinksCache.get(pidAttachmentUrl)!;
    }

    const raw = pidAttachmentUrl.trim();
    if (!raw) return [];

    let result: { pidName: string; url: string; href: string }[] = [];

    // Legacy: if backend returned a raw URL, show a best-effort name from filename.
    if (!raw.includes('|') && !raw.includes(';')) {
      result = [{ pidName: this.getFileNameFromUrl(raw), url: raw, href: this.getAttachmentHref(raw) }];
    } else {
      // Format: PIDName|AttachmentUrl;PIDName|AttachmentUrl;...
      const pairs = raw
        .split(';')
        .map(x => x.trim())
        .filter(Boolean);

      result = pairs
        .map(pair => {
          const idx = pair.indexOf('|');
          if (idx < 0) {
            return { pidName: this.getFileNameFromUrl(pair), url: pair, href: this.getAttachmentHref(pair) };
          }
          const pidName = pair.substring(0, idx).trim();
          const url = pair.substring(idx + 1).trim();
          return { pidName: pidName || this.getFileNameFromUrl(url), url, href: this.getAttachmentHref(url) };
        })
        .filter(x => !!x.url);
    }

    this.pidLinksCache.set(pidAttachmentUrl, result);
    return result;
  }

  // PDF Preview Modal
  showPdfPreview: boolean = false;
  pdfPreviewUrl: string = '';
  pdfFileName: string = '';

  getAttachmentHref(url: string): string {
    if (!url) return '#';
    return this.getGoogleViewerUrl(url);
  }

  onAttachmentClick(event: MouseEvent, url: string): void {
    const isMiddleClick = (event as any).button === 1;
    const isModifiedClick = !!(event.ctrlKey || event.metaKey || event.shiftKey || isMiddleClick);

    if (isModifiedClick) return;

    event.preventDefault();
    event.stopPropagation();

    if (url) {
      window.open(this.getGoogleViewerUrl(url), '_blank', 'noopener,noreferrer');
    }
  }

  closePdfPreview(): void {
    this.showPdfPreview = false;
    this.pdfPreviewUrl = '';
    this.pdfFileName = '';
    this.cdr.detectChanges();
  }

  getSafePdfUrl(url: string): any {
    if (!url) return '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private getFileNameFromUrl(url: string): string {
    try {
      const u = new URL(url);
      const last = u.pathname.split('/').filter(Boolean).pop();
      return last || url;
    } catch (e) {
      return url;
    }
  }

}
